import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { WhatsAppChannelService } from '../service/core/WhatsAppChannelService.service';
import { TeamService } from '../service/core/team.service';
import { SubscriptionService } from '../service/core/subscription.service';
import { SopService } from '../service/core/sop.service';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ApiService } from '../service/core/api.service';
import { TimezoneService } from '../service/core/timezone.service';
import { PermissionTemplateService } from '../service/core/permission-template.service';
import { ApiKeyStoreService, CompanyApiKey } from '../service/core/api-key-store.service';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { AuthService } from '../service/core/auth.service';
import { TokenService } from '@/pages/auth/token.service';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { InputNumberModule } from 'primeng/inputnumber';
import { environment } from 'src/environments/environment';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { FieldLabelService } from '@/pages/service/core/field-label.service';
import { SourceConfigService, SourceConfig, SourceCredentials, CreateSourcePayload, UpdateSourcePayload, MetaForm, MetaFormFields, ShopifyEvent, ShopifyRegisterResult } from '../service/core/source-config.service';
import { OperationsService } from '../operations/services/operations.service';
import { EmailSequenceService } from '@/pages/email-sequences/email-sequence.service';
import { VerticalConfigService, VerticalKey } from '../service/core/vertical-config.service';
import { WorkflowService } from '../service/core/workflow.service';
import { EventSetupService, EventPayload } from '../service/core/event-setup.service';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { BookingConfigComponent } from './booking-config/booking-config.component';
import { BillingOverviewComponent } from './billing/billing-overview.component';
import { ActivityTypesComponent } from './activity-types/activity-types.component';
import { ProposalTemplatesComponent } from './proposal-templates/proposal-templates.component';
import { NumberRulesComponent } from './number-rules/number-rules.component';
import { ProAutomationComponent } from './pro-automation/pro-automation.component';
import { ReminderCadenceComponent } from './reminder-cadence/reminder-cadence.component';
import { MasterDataService, MasterDataDto, MasterDataCategory } from '../service/core/master-data.service';
import { WaTemplateService } from '../whatsapp-template-list/services/wa-template.service';
import { WaTemplate } from '../whatsapp-template-list/models/wa-template.models';
import { EmailTemplateStore } from '../email-templates/services/email-template-store';
import { AppDatePipe } from '../service/core/app-date.pipe';
import { MyProfileComponent } from '../my-profile/my-profile.component';
import { SETTINGS_CATALOG, SettingsCard, SettingsCategory } from './settings-catalog';
import { SettingsBacklinkComponent } from './settings-backlink.component';

/** How a SYSTEM_FIELD entry stores its value (persisted in master_data.metadata as JSON). */
type SystemFieldValueType = 'text' | 'textarea' | 'number' | 'boolean' | 'date' | 'keyvalue' | 'file' | 'multifile';

@Component({
    selector: 'app-settings',
    imports: [CommonModule,
        TabsModule,
        TableModule,
        ReactiveFormsModule,
        InputTextModule,
        ToastModule,
        TagModule,
        CheckboxModule,
        FormsModule,
        DialogModule,
        ButtonModule,
        SelectModule,
        TextareaModule,
        ProgressSpinnerModule,
        TooltipModule,
        MultiSelectModule,
        DragDropModule,
        InputNumberModule,
        BookingConfigComponent,
        BillingOverviewComponent,
        ActivityTypesComponent,
        ProposalTemplatesComponent,
        NumberRulesComponent,
        ProAutomationComponent,
        ReminderCadenceComponent,
        MyProfileComponent,
        SettingsBacklinkComponent,
        AppDatePipe],
    templateUrl: './settings.html',
    styleUrl: './settings.scss',
    standalone: true,
    providers: [MessageService]
})
export class Settings implements OnInit {

    private route = inject(ActivatedRoute);
    private routerNav = inject(Router);
    private emailSequenceService = inject(EmailSequenceService);
    private readonly validTabs: ReadonlyArray<Settings['activeTab']> = ['company', 'profile', 'channels', 'config', 'users', 'userslist', 'billing', 'api', 'sources', 'booking', 'master-data', 'activity-types', 'statuses', 'proposals', 'numbering', 'automation', 'reminders'];

    activeTab: 'hub' | 'company' | 'profile' | 'channels' | 'config' | 'users' | 'userslist' | 'billing' | 'api' | 'sources' | 'events' | 'booking' | 'master-data' | 'activity-types' | 'statuses' | 'proposals' | 'numbering' | 'automation' | 'reminders' = 'hub';
    channelTab: 'whatsapp' | 'chatbot' = 'whatsapp';

    // ── Settings HUB (the landing page: categorised cards + search) ──────────
    /** Live search over card title / description / keywords. */
    hubSearch = '';

    /** Catalog filtered for the tenant's mode (Pro vs enterprise) and the search term. */
    get hubCatalog(): SettingsCategory[] {
        const pro = this.verticalConfig.proMode();
        const q = this.hubSearch.trim().toLowerCase();
        return SETTINGS_CATALOG
            .map(cat => ({
                ...cat,
                cards: cat.cards.filter(c => {
                    if (pro && c.proHidden) return false;
                    if (!pro && c.proOnly) return false;
                    // searchOnly cards are sub-parts of another card's flow. They stay out of the
                    // grid so one integration shows as one card, but remain findable by name.
                    if (c.searchOnly && !q) return false;
                    if (!q) return true;
                    return c.title.toLowerCase().includes(q)
                        || c.description.toLowerCase().includes(q)
                        || (c.keywords ?? []).some(k => k.toLowerCase().includes(q));
                }),
            }))
            .filter(cat => cat.cards.length > 0);
    }

    /** Card click → the setting itself (a ?tab= of this page, or a standalone route). */
    openHubCard(card: SettingsCard): void {
        this.routerNav.navigate(card.routerLink, card.queryParams ? { queryParams: card.queryParams } : {});
    }

    companyForm!: FormGroup;
    companyProfile: any = null;
    subscriptionProfile: any = null;
    /** The signed-in user's own profile (name/email/phone/employeeCode/role). */
    myProfile: any = null;
    /** Live Meta WhatsApp quality — phone number → rating, template name → score. */
    waNumberQuality: Record<string, string> = {};
    waTemplateQuality: Record<string, string> = {};
    showUpgradeDialog = false;
    upgradingPlan = false;
    loadingCompany = false;

    /** Industry vertical — re-skins the whole app (theme + vocabulary). Stored in
        company-ai-settings; changing it recolours instantly + relabels screens. */
    verticalOptions: { label: string; value: VerticalKey }[] = [];
    selectedVertical: VerticalKey = 'generic';
    savingVertical = false;

    metaConnected = false;
    channelAccount: any = null;
    channelConfigs: any[] = [];
    providersList: any = [];
    loadingWhatsApp = false;

    showAccountDialog = false;
    showNumberDialog = false;
    showTokenDialog = false;

    // ── Remove linked account ────────────────────────────────────────────────
    // Settings had no delete affordance at all, which is why the Veda-inside-Koshish
    // mix-up (both tenants holding the same Meta phone_number_id, 23,277 inbound
    // webhooks landing on the wrong tenant) had to be unpicked by hand in SQL.
    // Deleting an account cascades to its company_channel_configs rows, so the
    // confirm dialog loads the impact report first and names every number it takes.
    showDeleteAccountDialog = false;
    /** GET /api/channel/accounts/{id}/usage payload — null until it loads. */
    accountUsage: any = null;
    loadingAccountUsage = false;
    deletingAccount = false;
    /* Channel type is no longer asked for - a sender registered on this screen is always
       CUSTOMER support. The value is still sent (see numberForm's default and saveNumber) so the
       stored rows and the backend contract are unchanged. */

    chatbotStyle = {
        botName: 'Instarepli Assistant',
        primaryColor: '#16a34a',
        textColor: '#ffffff',
        position: 'right',
        borderRadius: 18,
        initialMessage: 'Hello! How can I help you today?',
        quickButtons: ['Get Started', 'Talk to Sales']
    };
    widgetSecretKey = '';
    widgetSecretVisible = false;
    creatingWidgetSecret = false;
    savingChatbotSettings = false;
    // Form Objects
    accountForm = { accountName: '', externalAccountId: '' };
    /** Reveals the optional token box in the Edit Linked Account dialog. Off on every open. */
    showAdvancedToken = false;
    numberForm: {
        senderIdentifier: string; displayName: string; isDefault: boolean;
        phoneNumber: string; channelType: string; wabaId: string;
        // Journey this number is dedicated to. null = general-purpose number:
        // every journey without its own number opens on the company default.
        operationId: string | null;
    } = { senderIdentifier: '', displayName: '', isDefault: false, phoneNumber: '', channelType: 'CUSTOMER', wabaId: '', operationId: null };
    tokenForm = { accountName: '', token: '' };
    editingConfig: any = null;

    // Status
    statuses: any[] = [];
    loadingStatuses = false;
    savingStatus = false;
    statusForm = { name: '', displayOrder: 1 };

    // ── Journey Stages (V014+) ─────────────────────────────────
    //
    // A Journey Stage = top-level (isPhaseRoot=true). Examples: Lead, KYC, Sale.
    // It can contain Sub-stages (children rows with parent_id set) OR can
    // attach SOPs directly to itself. SOPs always link to whichever node
    // is the "leaf" — the journey stage (if no subs) or each sub-stage.
    //
    // The Settings UI is a 2-level accordion:
    //   Journey Stage (expandable)
    //     └─ Sub-stage (expandable, if any) → SOP list
    //     OR direct SOP list (if no sub-stages)

    // Add-stage dialog state
    showStageDialog = false;
    savingStage = false;
    stageForm = { name: '', order: 1 };

    // Add-sub-stage dialog state
    showSubstageDialog = false;
    savingSubstage = false;
    substageParentId: string | null = null;
    substageParentName = '';
    substageForm = { name: '', order: 1 };

    // SOP dialog state (new Journey-Stages flow). All names are
    // prefixed `journey...` so they don't clash with the legacy
    // SOP-management dialog (`showSopDialog` / `savingSop` /
    // `sopForm`) declared later in this file. The two dialogs
    // coexist while we phase out the legacy path.
    showJourneySopDialog = false;
    savingJourneySop = false;
    sopForOwnerId: string | null = null;        // stage or sub-stage id
    sopForOwnerName = '';                       // label shown in the dialog
    sopForOwnerKind: 'stage' | 'substage' = 'stage';
    journeySopForm = {
        id: null as string | null,              // null = create, present = edit
        label: '',
        description: '',
        slaValue: null as number | null,        // raw number, user enters
        slaUnit: 'minutes' as 'minutes' | 'hours' | 'days',
        isMandatory: true,
        orderNo: 1
    };

    // SOPs cache — keyed by statusId (stage OR sub-stage id)
    sopsByOwner: Record<string, any[]> = {};
    loadingSopsFor: Record<string, boolean> = {};

    // Expansion state — Sets keyed by id
    expandedStages = new Set<string>();
    expandedSubstages = new Set<string>();

    // ── Map-workflow dialog ────────────────────────────────────
    // Click the link icon on a stage row → this dialog opens and lets
    // the admin attach an AI workflow that will run whenever a lead is
    // active in that stage. The selection writes to
    // workflow_status_master.ai_workflow_id via PATCH /api/statuses/{id}.
    showMapWorkflowDialog = false;
    savingMapWorkflow = false;
    mappingStageId: string | null = null;
    mappingStageName = '';
    mappingSelectedWorkflowId: string | null = null;
    availableWorkflows: any[] = [];     // populated lazily
    loadingMapWorkflows = false;         // distinct from legacy `loadingWorkflows`
                                          // (used by the SourceConfig dialog)

    // ── Map-department dialog ──────────────────────────────────
    // Mirror of the workflow-map dialog but for the team that owns
    // the stage. Writes to workflow_status_master.department_id
    // (column added in V014). The team_status_mapping table is the
    // legacy mapping path; this column is the cleaner inline option
    // and is what AssignmentService will consult once Step 3 lands.
    showMapDepartmentDialog = false;
    savingMapDepartment = false;
    mappingDeptStageId: string | null = null;
    mappingDeptStageName = '';
    mappingSelectedDepartmentId: string | null = null;
    availableDepartments: any[] = [];     // populated lazily
    loadingMapDepartments = false;
    /** Restricted-stage toggle: when on, the stage is worked by MULTIPLE restricted
     *  (business-partner / distributor) teams and does no auto-assignment. */
    mappingRestricted = false;
    mappingSelectedDepartmentIds: string[] = [];
    /** Only restricted-mode teams are selectable for a restricted stage. */
    get restrictedDepartments(): any[] {
        return (this.availableDepartments || []).filter((t: any) => t?.restrictedMode === true);
    }

    // ──────────────────────────────────────────────────────────────
    // V017/V018/V019 — Unified Phase Configuration dialog
    //
    // One dialog with tabs replaces the scattered "Map workflow" /
    // "Map department" / "SOPs" dialogs for end-to-end phase setup.
    // Workflow mapping is intentionally NOT in this dialog — it lives
    // on the Operations screen where phases are sequenced.
    //
    // Tabs:
    //   - general    name, displayOrder, channel, openingMode, dept, notice template
    //   - opening    phase_whatsapp_config (template + slots + variable map)
    //   - statuses   phase_statuses CRUD (color, default, terminal, won)
    //   - sops       existing direct-SOP editor for the phase
    //   - subphases  existing sub-phase + sub-phase-SOP editor
    //   - audit      timeline of every change to this phase (V020)
    // ──────────────────────────────────────────────────────────────

    /** Currently configured phase — null when dialog is closed. */
    showPhaseConfigDialog = false;
    phaseConfigStage: any = null;
    phaseConfigActiveTab: 'general' | 'opening' | 'voice' | 'email' | 'statuses' | 'sops' | 'subphases' | 'audit' = 'general';

    /** Email-tab form — picks an existing email template + send timing.
        Persisted via the shared phase config endpoint (templateId carries
        the email template id; reminderSlots[0] carries the delay). */
    phaseEmailForm = {
        emailTemplateId: null as string | null,
        trigger: 'PHASE_ACTIVATED' as string,
        delayValue: 0,
        delayUnit: 'MINUTES' as 'MINUTES' | 'HOURS' | 'DAYS',
        active: true,
    };
    emailTemplates: any[] = [];
    loadingEmailTemplates = false;

    /** Phase → email-sequence (drip) binding, set from this phase's Email config tab. */
    phaseEmailSequenceId: string | null = null;
    emailSequenceOptions: { label: string; value: string }[] = [];
    loadingEmailSequences = false;

    /** General-tab editable form — populated from the phase row when dialog opens. */
    phaseGeneralForm = {
        name: '',
        displayOrder: 1,
        channel: 'whatsapp' as 'whatsapp' | 'sms' | 'voice' | 'email' | 'web',
        channels: ['whatsapp'] as string[],
        openingMode: 'auto' as 'auto' | 'manual',
        departmentId: null as string | null,
        // CO-OWNING departments — several teams can work one phase (a shared "Distributors"
        // stage). Each lead is routed to exactly ONE of them and stays private to that team;
        // the phase's Assignment rules decide which. Read together with departmentId by the
        // backend's owningTeamIds().
        departmentIds: [] as string[],
        /** Read-only here: a RESTRICTED stage stores its business-partner teams in the very same
         *  departmentIds column, and is configured in the "Map team to stage" dialog. Carried so
         *  the save below can leave those rows alone instead of clearing them. */
        restricted: false,
        noticeTemplateId: null as string | null,
        // Phase-level SLA (whole-phase clock). Entered as value + unit,
        // persisted as slaMinutes. Separate from per-SOP SLAs.
        slaValue: null as number | null,
        slaUnit: 'minutes' as 'minutes' | 'hours' | 'days',
        // V024 — phase position flags + entry metadata
        isEntryPhase: false,
        allowManualCreate: false,
        isLastPhase: false,
        // UI-only choice that drives visibility of the metadata picker.
        //   'lead_pushed'        — Meta lead form / web form / manual
        //                          intake pushes structured data; validate
        //                          the payload against entryMetadataFields.
        //   'customer_initiated' — customer messages us first (inbound
        //                          WhatsApp / voice / web chat); no pre-
        //                          collected data, AI handles intake.
        // Derived from entryMetadataFields presence on dialog open; persisted
        // implicitly via the array (empty = customer_initiated, populated
        // = lead_pushed). No backend column needed.
        intakePattern: 'customer_initiated' as 'lead_pushed' | 'customer_initiated',
        // Array of { fieldCode, required } pairs. UI shows the picker
        // only when isEntryPhase AND intakePattern is 'lead_pushed'.
        entryMetadataFields: [] as { fieldCode: string; required: boolean }[],
    };
    savingPhaseGeneral = false;

    /** Loading flag for the entry-phase metadata picker. The actual
        `systemFieldOptions` list lives further down (declared once near
        the master-data state — already hydrated from SYSTEM_FIELD on
        component init via loadMasterData('SYSTEM_FIELD')). */
    loadingSystemFields = false;
    /** True once the real SYSTEM_FIELD master list has replaced the hardcoded
        fallback — guards ensureSystemFieldsLoaded() so it actually fetches. */
    private systemFieldsLoaded = false;

    /** Statuses-tab list cache (one phase at a time) + per-row edit state. */
    phaseStatuses: any[] = [];
    loadingPhaseStatuses = false;
    savingPhaseStatus = false;
    // Inline-add form at the bottom of the Statuses tab.
    //
    // DEFINITION ONLY. This screen names a phase's stages — what they are called, their
    // colour, their order, and which one a case starts on. What a stage MEANS is journey
    // business, not master-data: auto-advance, the agent's step outcome, the deal outcome
    // and the win % are all configured per journey in Operations → Phase flow → Outcomes.
    // A stage can sit in two journeys and mean different things in each, which is exactly
    // why those settings never belonged on the shared definition.
    phaseStatusForm = {
        id: null as string | null,            // null = create, set = edit
        name: '',
        color: '#3B82F6',
        displayOrder: 1,
        isDefault: false,
    };
    deletingPhaseStatusId: string | null = null;

    /** Opening-tab form — phase_whatsapp_config (template + slots). */
    phaseWaConfigForm = {
        id: null as string | null,
        templateId: null as string | null,
        channelConfigId: null as string | null,
        variableMapping: {} as Record<string, string>,
        reminderSlots: [] as any[],
        headerImageUrl: '' as string | null,
        triggerMode: 'ON_REPLY' as 'ON_REPLY' | 'ON_BUTTON',
        triggerButtonText: '' as string | null,
        greetingText: '' as string | null,
        isActive: true,
    };
    /** Options for the opening "what starts the workflow" picker. */
    readonly triggerModeOptions = [
        { label: 'Any reply (yes/ok or any tap)', value: 'ON_REPLY' },
        { label: 'A specific button only',        value: 'ON_BUTTON' },
    ];
    loadingPhaseWaConfig = false;
    savingPhaseWaConfig = false;

    /** Templates + channel-configs cached for the Opening tab pickers.
        Split by intent so the right picker shows the right shortlist:
        - notice templates (templateType=NOTICE) for the General tab
        - opening templates (templateType=ENTRY) for the Opening tab
        Both populated by loadCompanyTemplates() on dialog open. */
    availableTemplates: WaTemplate[] = [];
    loadingTemplatesForPhase = false;
    availableChannelConfigs: any[] = [];
    loadingChannelConfigsForPhase = false;

    /** Audit-tab feed — timeline of changes to this phase + children. */
    phaseAuditLog: any[] = [];
    loadingPhaseAudit = false;

    /** Static option lists for the General tab. */
    readonly channelOptions = [
        { value: 'whatsapp', label: 'WhatsApp', icon: 'pi pi-whatsapp' },
        { value: 'voice',    label: 'Voice',    icon: 'pi pi-phone' },
        { value: 'email',    label: 'Email',    icon: 'pi pi-envelope' },
        { value: 'sms',      label: 'SMS',      icon: 'pi pi-mobile' },
    ];
    readonly openingModeOptions = [
        { value: 'auto',   label: 'Auto',   help: 'System sends opening template (or starts AI when 24h open)' },
        { value: 'manual', label: 'Manual', help: 'No auto reach-out. Agent picks up from inbox' },
    ];

    // Teams
    teams: any[] = [];
    loadingTeams = false;
    savingTeam = false;
    savingMember = false;
    // V016 — per-member manager-flag toggle. Keyed by memberId so the
    // row's spinner is local to that row, not the whole list.
    togglingManagerId: string | null = null;
    // Per-row delete/remove spinners (local to the clicked row).
    deletingTeamId: string | null = null;
    removingUserId: string | null = null;
    removingMemberId: string | null = null;
    // Admin "change password" dialog.
    showPasswordDialog = false;
    passwordUser: any = null;
    newPassword = '';
    savingPassword = false;
    teamForm = { name: '' };
    // V016 — `isManager` defaults to false; ticked from the dialog
    // when the admin wants this person to land on manager-only screens
    // (SLA breach inbox, team review board) for this team.
    /** Add-member form. `selectedEmployees` is a LIST — a department is normally staffed in one
     *  go, and adding people one dialog at a time was the slowest part of setting a team up. */
    memberForm = { teamId: '', selectedEmployees: [] as any[], isManager: false };
    showAddMemberDialog = false;
    selectedTeamForMember: any = null;

    // Employees (for add-member dialog & users tab)
    employees: any[] = [];
    loadingEmployees = false;

    // CSV bulk-import of users
    uploadingUsers = false;
    showUserImportResult = false;
    userImportResult: { total: number; created: number; failed: number; errors: string[]; warnings: string[] } | null = null;

    // Add/Edit employee dialog
    showAddUserDialog = false;
    userInviteForm!: FormGroup;
    savingUser = false;
    /** Set when the dialog is editing an existing user; null = adding a new one. */
    editingUserId: string | null = null;
    /** Show/hide toggle for the invite dialog's password field so admins can verify what
     *  they typed before sharing it. Reset on every open. */
    userPasswordVisible = false;
    /** Show/hide toggle for the "Change password" dialog (same rationale). */
    resetPasswordVisible = false;
    roleOptions = [
        { label: 'Agent', value: 'ROLE_AGENT' },
        { label: 'Manager', value: 'ROLE_MANAGER' },
        { label: 'Company Admin', value: 'ROLE_COMPANY_ADMIN' },
    ];

    // Map stage to team (single selection)
    showMapStatusDialog = false;
    selectedTeamForStatus: any = null;
    selectedStatusId: string | null = null;
    originalStatusId: string | null = null;
    savingTeamStatuses = false;

    // View statuses of team
    showTeamStatusesDialog = false;
    viewingTeamForStatus: any = null;
    teamStatusMappings: any[] = [];
    loadingTeamStatuses = false;

    // View members dialog
    showMembersDialog = false;
    viewingTeam: any = null;
    /** Groups within the team currently open in the Members dialog (drive assignment). */
    teamGroups: any[] = [];
    newGroupName = '';
    savingGroup = false;
    teamMembers: any[] = [];
    loadingTeamMembers = false;
    /** Members-dialog tab: 'people' (flat searchable list) or 'groups' (bucketed by group). */
    membersTab: 'people' | 'groups' = 'people';
    /** Search box in the members dialog — matches name, email or employee code. */
    memberSearch = '';

    /** teamMembers filtered by {@link memberSearch} — case-insensitive substring on name/email/code. */
    get filteredMembers(): any[] {
        const q = (this.memberSearch || '').trim().toLowerCase();
        if (!q) return this.teamMembers;
        return this.teamMembers.filter(m => {
            const name = (m?.name || this.getMemberName(m?.userId) || '').toLowerCase();
            const email = (m?.email || '').toLowerCase();
            const code = (m?.employeeCode || '').toLowerCase();
            const phone = (m?.phone || '').toLowerCase();
            return name.includes(q) || email.includes(q) || code.includes(q) || phone.includes(q);
        });
    }

    /** Members bucketed by group id (respects the search filter so hidden people
     *  don't inflate the group counts). Members with no group land under '__none'. */
    get membersByGroup(): Record<string, any[]> {
        const out: Record<string, any[]> = { __none: [] };
        for (const g of this.teamGroups) out[g.id] = [];
        for (const m of this.filteredMembers) {
            const key = m?.groupId && out[m.groupId] ? m.groupId : '__none';
            out[key].push(m);
        }
        return out;
    }

    /** How many members are flagged as manager — shown as a pill at the top. */
    get membersManagerCount(): number {
        return this.teamMembers.filter(m => !!m?.isManager).length;
    }

    // SOP state
    showSopDialog = false;
    selectedStageForSop: any = null;
    sopTasks: any[] = [];
    loadingSop = false;
    savingSop = false;
    deletingSopId: string | null = null;
    sopForm = { label: '', orderNo: 1, isMandatory: false };

    // ── LEAD SOURCES ──────────────────────────────────────────────
    sourceTypeOptions = [
        { type: 'meta_ads',       label: 'Meta Ads',        icon: 'pi-facebook',  desc: 'Facebook & Instagram lead forms' },
        { type: 'google_ads',     label: 'Google Ads',      icon: 'pi-google',    desc: 'Google search & display leads' },
        { type: 'indiamart',      label: 'IndiaMART',       icon: 'pi-shopping-bag', desc: 'Lead Manager enquiries, auto-pulled every 5 min' },
        { type: 'shopify',        label: 'Shopify',         icon: 'pi-shopping-cart', desc: 'Store orders, customers & abandoned checkouts' },
        { type: 'website',        label: 'Website Form',    icon: 'pi-globe',     desc: 'Forms embedded on your website' },
        { type: 'whatsapp',       label: 'WhatsApp',        icon: 'pi-whatsapp',  desc: 'Inbound WhatsApp messages' },
        { type: 'manual',         label: 'Manual',          icon: 'pi-user-plus', desc: 'Leads created from the leads page UI' },
        { type: 'webhook',        label: 'Custom Webhook',  icon: 'pi-code',      desc: 'Any custom HTTP integration' }
    ];
    // Hydrated from master_data (category=SYSTEM_FIELD) in loadMasterDataForSettings().
    // Defaults below are the fallback if the API hasn't responded yet — they match
    // the seeded system rows so first paint is still usable.
    systemFieldOptions: { label: string; value: string }[] = [
        { label: 'Name',    value: 'name'    },
        { label: 'Phone',   value: 'phone'   },
        { label: 'Email',   value: 'email'   },
        { label: 'Company', value: 'company' },
        { label: 'City',    value: 'city'    }
    ];

    // ── Sources tab: per-type filtering (submenu deep-links) ──────────────────
    /** null = all types (classic table); a type key = filtered view; meta_ads = the Meta panel. */
    sourceTypeFilter: string | null = null;
    sourceTypeLabels: Record<string, string> = {
        meta_ads: 'Meta (Facebook & Instagram)', google_ads: 'Google Ads', indiamart: 'IndiaMART',
        shopify: 'Shopify',
        website: 'Website Form', whatsapp: 'WhatsApp', manual: 'Manual', webhook: 'Custom Webhook'
    };

    get filteredSources(): SourceConfig[] {
        return this.sourceTypeFilter
            ? this.sources.filter(s => s.source === this.sourceTypeFilter)
            : this.sources;
    }

    /**
     * Meta is deliberately absent from this list. It has its own card on the Settings landing
     * page that opens the guided wizard, and two entry points to the same integration was the
     * confusing part.
     *
     * The label stays in sourceTypeLabels and the panel below still renders, so an existing
     * ?source=meta_ads deep link keeps working. That matters for a tenant whose Pages were set
     * up on the old panel with a pasted token and who has not shared assets yet -- the wizard
     * cannot manage those until they do, and this panel is the only thing that can.
     */
    private static readonly HIDDEN_SOURCE_CHIPS = ['meta_ads'];

    /** Chip order for the in-page type switcher (replaces the retired submenu deep-links). */
    get sourceTypeKeys(): string[] {
        return Object.keys(this.sourceTypeLabels)
            .filter(k => !Settings.HIDDEN_SOURCE_CHIPS.includes(k));
    }

    /** Type chip click — navigate (not just assign) so the ?source= handler runs its
     *  side effects (the Meta / Google panels need their own loads). */
    setSourceType(type: string | null): void {
        this.routerNav.navigate([], {
            relativeTo: this.route,
            queryParams: type ? { tab: 'sources', source: type } : { tab: 'sources' },
        });
    }

    /** Skip the choose-type dialog when the type is already picked by the submenu. */
    addSourceOfType(type: string): void {
        this.pickedSourceType = type;
        this.proceedToConfigure();
    }

    /** Which provider's forms the shared form-manager methods act on. */
    formProvider: 'meta' | 'google' = 'meta';

    // ── Meta panel (sources tab, meta_ads filter): one tab per connected Page ─
    /** The Page (source) whose forms are being managed; null = the "add page" card. */
    metaPanelSource: SourceConfig | null = null;
    /** Connection card for a NEW page. */
    metaConn = { name: '', page_id: '', token: '', test_mode: true };

    /** The guided Meta setup (share assets → map forms → dataset routing → go live). This panel
     *  stays for existing pages and the manual token path; new connections belong in the wizard,
     *  which never asks anyone to paste a Page token. */
    openMetaSetupWizard(): void { this.routerNav.navigate(['/meta-setup']); }
    metaConnSaving = false;
    metaConnError = '';
    /** Set by verify-connect: the REAL page name Facebook reports for the stored token. */
    metaVerifiedName: Record<number, string> = {};

    get metaSources(): SourceConfig[] {
        return this.sources.filter(s => s.source === 'meta_ads');
    }

    // ── Google panel (sources tab, google_ads filter) ─────────────────────────
    get googleSources(): SourceConfig[] {
        return this.sources.filter(s => s.source === 'google_ads');
    }
    /** Connection info for the selected Google source: webhook URL + key to paste into Google Ads. */
    googleCreds: { webhookUrl: string; googleKey: string } | null = null;
    googleCredsLoading = false;

    /** Inline "add Google source" card (mirrors Meta's Add Page). */
    googleConn = { name: '', key: '' };
    googleConnSaving = false;
    googleConnError = '';

    /** A random webhook key so the admin doesn't have to invent one (they can still edit it). */
    genGoogleKey(): void {
        const rnd = Array.from({ length: 24 }, () => Math.floor(Math.random() * 36).toString(36)).join('');
        this.googleConn.key = 'gh_' + rnd;
    }

    startAddGoogleSource(): void {
        this.formProvider = 'google';
        this.metaPanelSource = null;
        this.googleCreds = null;
        this.googleConnError = '';
        this.googleConn = { name: '', key: '' };
        this.genGoogleKey();
    }

    saveGoogleConnection(): void {
        const name = this.googleConn.name.trim();
        if (!name) { this.googleConnError = 'Give this source a name.'; return; }
        if (!this.googleConn.key.trim()) { this.googleConnError = 'A webhook key is required.'; return; }
        this.googleConnSaving = true;
        this.googleConnError = '';
        const sourceId = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'google_ads';
        this.sourceConfigService.create({
            source: 'google_ads',
            source_id: sourceId,
            required_fields: ['phone', 'name'],
            field_mapping: {},
            metadata: { campaign_name: name },
            google_key: this.googleConn.key.trim(),
        } as CreateSourcePayload).subscribe({
            next: (created: any) => {
                this.sourceConfigService.list().subscribe({
                    next: (list) => {
                        this.sources = list;
                        const src = list.find(s => s.id === created.id) ?? null;
                        this.googleConnSaving = false;
                        this.selectGoogleSource(src);
                        this.messageService.add({ severity: 'success', summary: 'Google source created',
                            detail: 'Paste the Webhook URL + Key into Google Ads, then map the forms as leads arrive.' });
                    },
                    error: () => { this.googleConnSaving = false; }
                });
            },
            error: (err) => {
                this.googleConnSaving = false;
                this.googleConnError = err?.message ?? 'Could not create the Google source.';
            }
        });
    }

    selectGoogleSource(src: SourceConfig | null): void {
        this.formProvider = 'google';
        this.metaPanelSource = src;
        this.metaForms = []; this.metaFormOpen = null; this.metaFormsError = '';
        this.googleCreds = null;
        if (src) {
            this.loadMetaForms();
            this.googleCredsLoading = true;
            this.sourceConfigService.getCredentials(src.id).subscribe({
                next: (c) => { this.googleCreds = { webhookUrl: c.webhookUrl, googleKey: (c as any).googleKey ?? '' }; this.googleCredsLoading = false; },
                error: () => { this.googleCredsLoading = false; }
            });
        }
    }

    copyText(text: string): void {
        try { navigator.clipboard?.writeText(text); this.messageService.add({ severity: 'success', summary: 'Copied', detail: '' , life: 1500}); } catch {}
    }

    selectMetaSource(src: SourceConfig | null): void {
        this.formProvider = 'meta';
        this.metaPanelSource = src;
        this.metaForms = []; this.metaFormOpen = null; this.metaFormsError = '';
        this.metaImportSelection = {};
        this.metaConnError = '';
        if (src) {
            this.loadMetaForms();
            if (this.metaVerifiedName[src.id] === undefined) this.verifyMetaPanelSource(src, true);
        } else {
            this.metaConn = { name: '', page_id: '', token: '', test_mode: true };
        }
    }

    metaGeneratingToken = false;

    /** Mint the Page token from the entered Page ID via the central system-user token. */
    generateMetaPageToken(): void {
        const pageId = this.metaConn.page_id?.trim();
        if (!pageId) { this.metaConnError = 'Enter the Facebook Page ID first, then Generate token.'; return; }
        this.metaGeneratingToken = true;
        this.metaConnError = '';
        this.sourceConfigService.generateMetaPageToken(pageId).subscribe({
            next: (res) => {
                this.metaGeneratingToken = false;
                this.metaConn.token = res.access_token;
                if (!this.metaConn.name?.trim() && res.page_name) this.metaConn.name = res.page_name;
                this.messageService.add({ severity: 'success', summary: 'Token generated',
                    detail: `Page token for ${res.page_name || pageId} filled in.` });
            },
            error: (err) => {
                this.metaGeneratingToken = false;
                this.metaConnError = err?.message ?? 'Could not generate the token — is the Page assigned to the system user?';
            }
        });
    }

    /** Create the source for a new Page, then verify + subscribe it in the same breath. */
    saveMetaConnection(): void {
        const name = this.metaConn.name.trim();
        if (!name || !this.metaConn.page_id.trim() || !this.metaConn.token.trim()) {
            this.metaConnError = 'Name, Facebook Page ID and Page Access Token are all required.';
            return;
        }
        this.metaConnSaving = true;
        this.metaConnError = '';
        const sourceId = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'meta_page';
        this.sourceConfigService.create({
            source: 'meta_ads',
            source_id: sourceId,
            required_fields: ['phone', 'name'],
            field_mapping: { full_name: 'name', phone_number: 'phone', email_address: 'email' },
            metadata: { campaign_name: name },
            meta_page_id: this.metaConn.page_id.trim(),
            meta_page_access_token: this.metaConn.token.trim(),
            meta_test_mode: this.metaConn.test_mode,
        } as CreateSourcePayload).subscribe({
            next: (created: any) => {
                this.sourceConfigService.list().subscribe({
                    next: (list) => {
                        this.sources = list;
                        const src = list.find(s => s.id === created.id) ?? null;
                        this.metaConnSaving = false;
                        this.selectMetaSource(src);
                        if (src) this.verifyMetaPanelSource(src, false);
                    },
                    error: () => { this.metaConnSaving = false; }
                });
            },
            error: (err) => {
                this.metaConnSaving = false;
                this.metaConnError = err?.message ?? 'Could not save the Meta connection.';
            }
        });
    }

    /** Verify the token really reads THIS page (shows its real FB name) + subscribe its leads. */
    verifyMetaPanelSource(src: SourceConfig, silent: boolean): void {
        this.sourceConfigService.verifyMetaConnection(src.id).subscribe({
            next: (res) => {
                this.metaVerifiedName[src.id] = res.page_name;
                if (!silent) this.messageService.add({ severity: 'success', summary: 'Page connected',
                    detail: `Verified & subscribed: ${res.page_name}` });
            },
            error: (err) => {
                this.metaVerifiedName[src.id] = '';
                if (!silent) this.messageService.add({ severity: 'error', summary: 'Verification failed',
                    detail: err?.message ?? 'Meta rejected the Page id / token pair.' });
            }
        });
    }

    // Import selection (checkboxes on not-yet-imported forms).
    metaImportSelection: Record<string, boolean> = {};
    metaImporting = false;

    get metaHasImportSelection(): boolean {
        return Object.values(this.metaImportSelection).some(v => v);
    }

    /** The forms that can still be imported (Meta discovers all; Google auto-imports on discovery). */
    get metaNotImportedForms(): MetaForm[] {
        return this.metaForms.filter(f => !f.imported);
    }

    get metaAllSelected(): boolean {
        const ni = this.metaNotImportedForms;
        return ni.length > 0 && ni.every(f => this.metaImportSelection[f.form_id]);
    }

    toggleAllMetaImport(checked: boolean): void {
        for (const f of this.metaNotImportedForms) this.metaImportSelection[f.form_id] = checked;
    }

    importSelectedMetaForms(): void {
        const src = this.metaPanelSource;
        if (!src) return;
        const ids = Object.keys(this.metaImportSelection).filter(k => this.metaImportSelection[k]);
        if (!ids.length) return;
        this.metaImporting = true;
        this.sourceConfigService.importMetaForms(src.id, ids).subscribe({
            next: (forms) => {
                this.metaImporting = false;
                this.metaImportSelection = {};
                this.metaForms = forms;
                this.messageService.add({ severity: 'success', summary: 'Forms imported',
                    detail: 'Now map each form\'s fields so its leads can flow.' });
            },
            error: (err) => {
                this.metaImporting = false;
                this.messageService.add({ severity: 'error', summary: 'Import failed',
                    detail: err?.message ?? 'Could not import the selected forms.' });
            }
        });
    }

    toggleMetaFormEnabled(form: MetaForm): void {
        const src = this.metaPanelSource;
        if (!src) return;
        const call = this.formProvider === 'google'
            ? this.sourceConfigService.toggleGoogleForm(src.id, form.form_id)
            : this.sourceConfigService.toggleMetaForm(src.id, form.form_id);
        call.subscribe({
            next: (row) => {
                form.enabled = row.enabled;
                this.messageService.add({ severity: 'info',
                    summary: row.enabled ? 'Form enabled' : 'Form disabled',
                    detail: row.enabled ? 'Its leads will be ingested again — replay any that were held.'
                                        : 'New leads from this form will be held, not ingested.' });
            },
            error: (err) => this.messageService.add({ severity: 'error', summary: 'Error',
                detail: err?.message ?? 'Could not toggle the form.' })
        });
    }

    metaImportPastId: string | null = null;

    /** Backfill a Meta form's historical leads (submitted before the webhook connected). */
    importPastMetaLeads(form: MetaForm): void {
        const src = this.metaPanelSource;
        if (!src) return;
        this.metaImportPastId = form.form_id;
        this.sourceConfigService.importPastMetaLeads(src.id, form.form_id).subscribe({
            next: (r) => {
                this.metaImportPastId = null;
                this.loadMetaForms();
                this.messageService.add({ severity: 'success', summary: 'Past leads imported',
                    detail: `${r.fetched} fetched from Meta — ${r.ingested} ingested`
                        + (r.held ? `, ${r.held} held (map/enable the form)` : '')
                        + (r.failed ? `, ${r.failed} failed` : '') + '.', life: 10000 });
            },
            error: (err) => {
                this.metaImportPastId = null;
                this.messageService.add({ severity: 'error', summary: 'Import failed',
                    detail: err?.message ?? 'Could not import past leads.' });
            }
        });
    }

    metaReplayingId: string | null = null;

    replayMetaForm(form: MetaForm): void {
        const src = this.metaPanelSource;
        if (!src) return;
        this.metaReplayingId = form.form_id;
        const call = this.formProvider === 'google'
            ? this.sourceConfigService.replayGoogleForm(src.id, form.form_id)
            : this.sourceConfigService.replayMetaForm(src.id, form.form_id);
        call.subscribe({
            next: (res) => {
                this.metaReplayingId = null;
                form.pending_leads = res.pending_after ?? 0;
                if (res.blocked_reason) {
                    this.messageService.add({ severity: 'warn', summary: 'Nothing replayed',
                        detail: 'The form is still ' + (res.blocked_reason === 'DISABLED' ? 'disabled'
                            : 'unmapped') + ' — fix that first.' });
                } else {
                    this.messageService.add({ severity: 'success', summary: 'Replay finished',
                        detail: `${res.replayed} lead(s) ingested` + (res.failed ? `, ${res.failed} failed` : '') + '.' });
                }
            },
            error: (err) => {
                this.metaReplayingId = null;
                this.messageService.add({ severity: 'error', summary: 'Replay failed',
                    detail: err?.message ?? 'Could not replay the held leads.' });
            }
        });
    }

    /** Per-form journey pick while the mapping editor is open. '' = use the source default. */
    metaFormOperationDraft = '';

    /** Per-form source name while the editor is open (e.g. "JNV Class 9"). '' = page source. */
    metaFormSourceNameDraft = '';
    /** Pick-or-type suggestions = source names already used on this page's other forms. */
    metaSourceNameOptions: { label: string; value: string }[] = [];

    /** Free-text filter over the open form's questions — long forms get unwieldy fast. */
    metaFieldSearch = '';

    get filteredMetaFormFields() {
        const fields = this.metaFormOpen?.fields ?? [];
        const q = this.metaFieldSearch.trim().toLowerCase();
        if (!q) return fields;
        return fields.filter(f =>
            f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
            || (f.type ?? '').toLowerCase().includes(q));
    }

    // ── Meta Lead Forms (per-form mapping; meta_ads sources only) ─────────────
    // A form with no saved mapping HOLDS its leads, so the badges here are operational
    // state, not decoration: "Not mapped" means that form's leads are waiting.
    metaForms: MetaForm[] = [];
    metaFormsLoading = false;
    metaFormsError = '';
    /** The form whose fields are open for mapping (accordion-style, one at a time). */
    metaFormOpen: MetaFormFields | null = null;
    metaFormLoadingId: string | null = null;
    /** question key → selected system field ('' = don't map) while editing. */
    metaFormDraft: Record<string, string> = {};
    metaFormSaving = false;

    // ── Shopify events (shopify sources only; fixed set of 3 topics) ──────────
    shopifyEvents: ShopifyEvent[] = [];
    shopifyEventsLoading = false;
    shopifyEventsError = '';
    /** The event whose mapping/journey editor is open (accordion, one at a time). */
    shopifyEventOpen: ShopifyEvent | null = null;
    shopifyEventTogglingSlug: string | null = null;
    shopifyEventSaving = false;
    /** Draft mapping rows (incoming Shopify key → system field) for the open event. */
    shopifyEventDraftRows: { incoming: string; system: string }[] = [];
    shopifyEventDraftOperationId = '';
    shopifyEventDraftSourceName = '';
    // Connect / auto-register webhooks
    shopifyRegistering = false;
    shopifyRegisterResult: ShopifyRegisterResult | null = null;
    // The activity this event punches (drives phase automation: activity → stage → follow-up).
    shopifyEventDraftActivityTypeId: string | null = null;
    shopifyActivityOptions: { label: string; value: string }[] = [];

    campaignTypeOptions = [
        { label: 'Paid Ads',     value: 'Paid Ads'     },
        { label: 'Organic',      value: 'Organic'      },
        { label: 'Referral',     value: 'Referral'     },
        { label: 'Email',        value: 'Email'        },
        { label: 'Social Media', value: 'Social Media' },
        { label: 'Influencer',   value: 'Influencer'   },
        { label: 'Event',        value: 'Event'        },
        { label: 'Other',        value: 'Other'        }
    ];

    regionOptions = [
        { label: 'India',  value: 'India'  },
        { label: 'UAE',    value: 'UAE'    },
        { label: 'USA',    value: 'USA'    },
        { label: 'UK',     value: 'UK'     },
        { label: 'Global', value: 'Global' },
        { label: 'Other',  value: 'Other'  }
    ];

    currentYear = new Date().getFullYear();

    get sourceIdHasError(): boolean {
        const id = this.sourceForm.source_id;
        return id.length > 0 && !/^[a-z0-9_]+$/.test(id);
    }

    sources: SourceConfig[] = [];
    loadingSources = false;

    // Step 1 — choose source type
    showChooseDialog = false;
    pickedSourceType = '';

    // Step 2 — configure
    showConfigDialog = false;
    editingSource: SourceConfig | null = null;
    sourceForm = {
        source_id: '', required_fields: ['phone', 'name'] as string[], campaign_name: '', campaign_type: '',
        region: '', google_key: '', workflowName: '', operationId: '',
        // Meta (Facebook/Instagram) Lead Ads — meta_ads sources only. Central-app model: the
        // app-level secrets live on the lead service, so a source carries just the Page id
        // (webhook routing key) + Page token. The token is never returned by the API, so blank
        // on an edit means "keep what's stored"; meta_configured tells the UI it's already set.
        meta_page_access_token: '', meta_page_id: '',
        meta_test_mode: false, meta_configured: false, meta_webhook_url: '',
        // IndiaMART — key is never returned by the API, so blank on an edit means
        // "keep what's stored"; indiamart_configured drives the saved/unchanged hint.
        indiamart_crm_key: '', indiamart_configured: false,
        // Shopify — custom app per store. Domain is round-tripped; token + secret are never
        // returned, so blank on an edit means "keep what's stored" (shopify_configured drives the hint).
        shopify_store_domain: '', shopify_access_token: '', shopify_webhook_secret: '', shopify_configured: false,
    };
    workflows: { id: string; flowName: string }[] = [];
    loadingWorkflows = false;
    operations: { id: string; name: string }[] = [];
    loadingOperations = false;
    fieldMappingRows: { incoming: string; system: string }[] = [{ incoming: '', system: '' }];
    savingSource = false;
    sourceFormError = '';

    // Step 3 — credentials + test
    showCredentialsDialog = false;
    currentCredentials: SourceCredentials | null = null;
    credentialsMode: 'new' | 'regenerate' | 'test' = 'new';
    copiedField = '';
    testBody = { name: 'Test User', phone: '+919000000001', email: 'test@example.com' };
    testResult: any = null;
    testLoading = false;

    // Row-level action tracking
    regeneratingId: number | null = null;
    togglingId: number | null = null;
    deletingId: number | null = null;

    // Delete confirmation
    showDeleteConfirm = false;
    sourceToDelete: SourceConfig | null = null;

    get groupedSources() {
        return this.sourceTypeOptions
            .map(st => ({ ...st, items: this.sources.filter(s => s.source === st.type) }))
            .filter(g => g.items.length > 0);
    }
    getSourceLabel = (type: string) => this.sourceTypeOptions.find(s => s.type === type)?.label ?? type;
    getSourceIcon  = (type: string) => this.sourceTypeOptions.find(s => s.type === type)?.icon  ?? 'pi-circle';

    // ── Pull-source status chip (IndiaMART & future pull providers) ──────────
    /** Chip severity: green once the poller has connected, red on key/API failure,
     *  orange while throttled, grey before the first poll or when paused. */
    getPullSeverity(src: any): 'success' | 'danger' | 'warn' | 'secondary' | 'info' {
        if (src?.pullEnabled === false) return 'secondary';
        switch (src?.pullStatus) {
            case 'CONNECTED':    return 'success';
            case 'INVALID_KEY':
            case 'ERROR':        return 'danger';
            case 'RATE_LIMITED': return 'warn';
            case 'PENDING':      return 'info';
            default:             return 'secondary';
        }
    }
    getPullLabel(src: any): string {
        if (src?.pullEnabled === false) return 'Pull paused';
        switch (src?.pullStatus) {
            case 'CONNECTED':    return 'Pull: connected';
            case 'INVALID_KEY':  return 'Pull: invalid key';
            case 'RATE_LIMITED': return 'Pull: rate limited';
            case 'ERROR':        return 'Pull: error';
            case 'PENDING':      return 'Pull: pending';
            default:             return 'Pull: —';
        }
    }
    /** Tooltip: last error when unhealthy, else last successful sync time. */
    getPullTooltip(src: any): string {
        if (src?.pullError) return src.pullError;
        if (src?.pullLastSyncedAt) return 'Last synced ' + new Date(src.pullLastSyncedAt).toLocaleString();
        return 'Waiting for the first poll — usually under a minute.';
    }
    /** The source's display identity — Campaign Name from metadata (either key casing). */
    getCampaignName = (src: any): string =>
        (src?.metadata?.campaignName ?? src?.metadata?.campaign_name ?? '').toString();

    // ── MASTER DATA ──────────────────────────────────────────────
    masterDataTab: MasterDataCategory = 'SYSTEM_FIELD';
    readonly masterDataCategories: { key: MasterDataCategory; label: string; icon: string; hint: string }[] = [
        { key: 'SYSTEM_FIELD', label: 'System Field', icon: 'pi pi-tags',  hint: 'Canonical fields available when mapping incoming source fields.' },
        { key: 'TAG',          label: 'Tags',         icon: 'pi pi-tag',   hint: 'Reusable contact tags agents apply to cases (quick-pick + multi-select).' },
        { key: 'LOSS_REASON',  label: 'Loss Reasons', icon: 'pi pi-thumbs-down', hint: 'Why deals are lost — offered when a case is set to a lost status; feeds the loss analysis.' },
        { key: 'SOURCE_NAME',  label: 'Source Names',  icon: 'pi pi-sitemap', hint: 'Reusable lead-source names (e.g. "JNV Class 9") — pickable when mapping a Meta/Google lead form so its leads show under that source.' }
    ];
    masterDataRows: Record<MasterDataCategory, MasterDataDto[]> = { LEAD_STATUS: [], SYSTEM_FIELD: [], TAG: [], LOSS_REASON: [], SOURCE_NAME: [] } as any;
    loadingMasterData: Record<MasterDataCategory, boolean> = { LEAD_STATUS: false, SYSTEM_FIELD: false, TAG: false, LOSS_REASON: false } as any;
    showMasterDataDialog = false;
    editingMasterData: MasterDataDto | null = null;
    masterDataForm: {
        code: string; label: string; description: string; displayOrder: number; isActive: boolean;
        fieldType: SystemFieldValueType; boolValue: boolean;
        kvPairs: { key: string; value: string }[];
        /** keyvalue only: offer an "Other" choice at capture time that reveals a free-text input. */
        allowOther: boolean;
        /** SYSTEM_FIELD only: track how many times an agent fills this field as a performance metric. */
        trackPerformance: boolean;
        /** SYSTEM_FIELD only: show this field in the compact Captured-data panel on the case screen.
         *  Unticked fields move to the Details modal. If no field is ticked, the panel shows all.
         *  In Pro this is the ONLY place to set it (fields sync to the single Default phase). */
        showInCapture: boolean;
    } = { code: '', label: '', description: '', displayOrder: 1, isActive: true, fieldType: 'text', boolValue: false, kvPairs: [], allowOther: false, trackPerformance: false, showInCapture: false };
    /** Value-type options for a SYSTEM_FIELD entry (persisted in master_data.metadata as JSON). */
    readonly systemFieldTypeOptions: { label: string; value: SystemFieldValueType }[] = [
        { label: 'Text',                   value: 'text' },
        { label: 'Text area',              value: 'textarea' },
        { label: 'Number',                 value: 'number' },
        { label: 'Boolean (true / false)', value: 'boolean' },
        { label: 'Date',                   value: 'date' },
        { label: 'Key – Value pairs',      value: 'keyvalue' },
        { label: 'File upload',            value: 'file' },
        { label: 'Multi-file upload',      value: 'multifile' },
    ];
    savingMasterData = false;
    masterDataFormError = '';
    deletingMasterDataId: string | null = null;

    constructor(
        private fb: FormBuilder,
        private messageService: MessageService,
        private whatsAppService: WhatsAppChannelService,
        private authService: AuthService,
        private teamService: TeamService,
        private subscriptionService: SubscriptionService,
        private sopService: SopService,
        private sourceConfigService: SourceConfigService,
        /** Captured-data code -> the tenant's label, for the message-variable picker. */
        private fieldLabels: FieldLabelService,
        private eventSetupService: EventSetupService,
        private workflowService: WorkflowService,
        private operationsService: OperationsService,
        private verticalConfig: VerticalConfigService,
        private masterDataService: MasterDataService,
        private waTemplateService: WaTemplateService,
        private emailTemplateStore: EmailTemplateStore,
        private tokenService: TokenService,
        private api: ApiService,
        private tzService: TimezoneService,
        private permissionTemplateService: PermissionTemplateService,
        private apiKeyStore: ApiKeyStoreService
    ) {}

    // ── API Keys (Settings → API Keys tab) ──────────────────────────────────
    apiKeys: CompanyApiKey[] = [];
    loadingApiKeys = false;
    newApiKeyName = '';
    newApiKeyValue = '';
    savingApiKey = false;
    deletingApiKeyId: string | null = null;
    revealedApiKeyIds = new Set<string>();

    loadApiKeys(): void {
        this.loadingApiKeys = true;
        this.apiKeyStore.list().subscribe({
            next: (rows) => { this.apiKeys = rows || []; this.loadingApiKeys = false; },
            error: () => { this.apiKeys = []; this.loadingApiKeys = false; }
        });
    }

    createApiKey(): void {
        const name = (this.newApiKeyName || '').trim();
        const value = (this.newApiKeyValue || '').trim();
        if (!name || !value) {
            this.messageService.add({ severity: 'warn', summary: 'Missing details', detail: 'Enter a name and the API key.' });
            return;
        }
        this.savingApiKey = true;
        this.apiKeyStore.create(name, value).subscribe({
            next: (created) => {
                if (created) this.apiKeys = [created, ...this.apiKeys];
                this.newApiKeyName = '';
                this.newApiKeyValue = '';
                this.savingApiKey = false;
                this.messageService.add({ severity: 'success', summary: 'API key saved', detail: name });
            },
            error: () => {
                this.savingApiKey = false;
                this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Could not save the API key.' });
            }
        });
    }

    deleteApiKey(k: CompanyApiKey): void {
        if (!k?.id) return;
        this.deletingApiKeyId = k.id;
        this.apiKeyStore.delete(k.id).subscribe({
            next: () => {
                this.apiKeys = this.apiKeys.filter(x => x.id !== k.id);
                this.deletingApiKeyId = null;
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: k.name });
            },
            error: () => {
                this.deletingApiKeyId = null;
                this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: 'Could not delete the API key.' });
            }
        });
    }

    toggleApiKeyReveal(id: string): void {
        if (this.revealedApiKeyIds.has(id)) this.revealedApiKeyIds.delete(id);
        else this.revealedApiKeyIds.add(id);
    }

    maskApiKey(v: string): string {
        // Fully hidden by default — reveal nothing of the key (the eye toggle shows it on demand).
        return v ? '••••••••••••' : '';
    }

    // ── Permission templates (Users tab picker) ─────────────────────────────
    /** Templates for the per-user "Access template" picker on the Users tab. */
    permTemplateOptions: { label: string; value: string | null }[] = [{ label: 'Role default', value: null }];
    private assigningTemplateUserId: string | null = null;

    private loadPermissionTemplates(): void {
        this.permissionTemplateService.list().subscribe({
            next: (rows) => {
                this.permTemplateOptions = [
                    { label: 'Role default', value: null },
                    ...(rows || []).map(t => ({ label: t.name, value: t.id as string }))
                ];
            },
            error: () => { /* picker falls back to "Role default" only */ }
        });
    }

    /** True when the row user is an admin — templates never apply to admins. */
    isAdminUser(u: any): boolean {
        const roles = u?.roles || [];
        return roles.includes('ROLE_COMPANY_ADMIN') || roles.includes('ROLE_SUPER_ADMIN');
    }

    /** True when the user currently being edited is an admin — used to disable the Access
     *  Template dropdown in the invite/edit dialog (admins always have full access). */
    get editingUserIsAdmin(): boolean {
        if (!this.editingUserId) return false;
        const u = (this.employees || []).find(e => e.id === this.editingUserId);
        return u ? this.isAdminUser(u) : false;
    }

    /** Human label for a row's current access template (used in the read-only table cell). */
    // ── Teams search + filters ───────────────────────────────────
    teamSearch = '';
    /** null = any, true/false = that state. Mirrors the two toggle columns. */
    teamCheckInFilter: boolean | null = null;
    teamModeFilter: 'partner' | 'standard' | null = null;

    get teamCheckInFilterOptions() {
        return [{ label: 'Check-in on', value: true }, { label: 'Check-in off', value: false }];
    }
    get teamModeFilterOptions() {
        return [{ label: 'Business partner', value: 'partner' }, { label: 'Standard', value: 'standard' }];
    }

    get teamFiltersActive(): boolean {
        return !!this.teamSearch.trim() || this.teamCheckInFilter !== null || !!this.teamModeFilter;
    }
    clearTeamFilters(): void {
        this.teamSearch = '';
        this.teamCheckInFilter = null;
        this.teamModeFilter = null;
    }

    get filteredTeams(): any[] {
        const q = this.teamSearch.trim().toLowerCase();
        let rows = this.teams || [];
        if (this.teamCheckInFilter !== null) {
            rows = rows.filter(t => !!t?.attendanceEnabled === this.teamCheckInFilter);
        }
        if (this.teamModeFilter) {
            const wantPartner = this.teamModeFilter === 'partner';
            rows = rows.filter(t => !!t?.restrictedMode === wantPartner);
        }
        if (!q) return rows;
        return rows.filter(t => String(t?.name || '').toLowerCase().includes(q));
    }

    userTemplateLabel(u: any): string {
        const id = u?.permissionTemplateId;
        if (!id) return 'Role default';
        const opt = this.permTemplateOptions.find(o => o.value === id);
        return opt ? opt.label : 'Role default';
    }

    // ── Users search ─────────────────────────────────────────────────────────
    /** Free-text filter over the Users table. */
    userSearch = '';

    /** Users matching {@link userSearch} — name, email, phone, employee code, role label
     *  and access-template label are all searchable, so "manager", "EMP-0007" or a partial
     *  phone all find the right person. Empty search → the full list, untouched. */
    /** Role filter — the values userRoleBadges() can produce. */
    userRoleFilter: 'admin' | 'manager' | 'agent' | null = null;
    /** Access-template filter. '' is the sentinel for "Role default" (no template). */
    userTemplateFilter: string | null = null;

    get userRoleFilterOptions() {
        return [
            { label: 'Admin',   value: 'admin' },
            { label: 'Manager', value: 'manager' },
            { label: 'Agent',   value: 'agent' },
        ];
    }
    /**
     * Template options for the FILTER. permTemplateOptions already leads with
     * `{ label: 'Role default', value: null }` for the per-user assign picker,
     * but null is this filter's "no filter" sentinel — that entry would render a
     * duplicate row that silently cleared the filter instead of selecting it.
     * Drop it and use '' for "assigned no template".
     */
    get userTemplateFilterOptions() {
        const real = (this.permTemplateOptions || []).filter(o => o.value !== null);
        return [{ label: 'Role default (no template)', value: '' }, ...real];
    }

    get userFiltersActive(): boolean {
        return !!this.userSearch.trim() || !!this.userRoleFilter || this.userTemplateFilter !== null;
    }
    clearUserFilters(): void {
        this.userSearch = '';
        this.userRoleFilter = null;
        this.userTemplateFilter = null;
    }

    get filteredEmployees(): any[] {
        const q = this.userSearch.trim().toLowerCase();
        const role = this.userRoleFilter;
        const tpl = this.userTemplateFilter;

        let rows = this.employees || [];
        if (role) {
            rows = rows.filter(u => this.userRoleBadges(u).some(b => b.tone === role));
        }
        if (tpl !== null) {
            // '' matches users with no template assigned, i.e. "Role default".
            rows = rows.filter(u => (u?.permissionTemplateId || '') === tpl);
        }
        if (!q) return rows;
        return rows.filter(u => {
            const name = u?.name || `${u?.firstName ?? ''} ${u?.lastName ?? ''}`;
            const haystack = [
                name, u?.email, u?.phone, u?.employeeCode,
                this.userTemplateLabel(u),
                ...this.userRoleBadges(u).map(r => r.label),
            ].filter(v => v != null && String(v).trim() !== '').join(' ').toLowerCase();
            return haystack.includes(q);
        });
    }

    clearUserSearch(): void { this.userSearch = ''; }

    /** Role badges for the users table — mirrors the invite dialog's badges. */
    userRoleBadges(u: any): { label: string; tone: 'agent' | 'manager' | 'admin' }[] {
        const roles: string[] = u?.roles || [];
        const out: { label: string; tone: 'agent' | 'manager' | 'admin' }[] = [];
        if (roles.includes('ROLE_COMPANY_ADMIN') || roles.includes('ROLE_SUPER_ADMIN')) out.push({ label: 'Admin', tone: 'admin' });
        if (roles.includes('ROLE_MANAGER')) out.push({ label: 'Manager', tone: 'manager' });
        if (roles.includes('ROLE_AGENT')) out.push({ label: 'Agent', tone: 'agent' });
        return out;
    }

    onUserTemplateChange(u: any): void {
        this.assigningTemplateUserId = u.id;
        this.permissionTemplateService.assignUser(u.id, u.permissionTemplateId ?? null).subscribe({
            next: () => {
                this.assigningTemplateUserId = null;
                this.messageService.add({ severity: 'success', summary: 'Access template updated',
                    detail: `${u.name || u.email} now follows ${u.permissionTemplateId ? 'the selected template' : 'their role’s access'}.`, life: 2200 });
            },
            error: (err) => {
                this.assigningTemplateUserId = null;
                this.showError('Could not update access template', err);
                // Re-sync the row with the server state.
                this.employees = [];
                this.loadEmployees();
            }
        });
    }

    // ── Company profile (Company tab, admin only) ──────────────────────────
    /** Draft company name for the Company configuration form. */
    companyNameDraft = '';
    savingCompany = false;
    /** True while the logo uploads / clears — spins the camera badge. */
    uploadingLogo = false;
    /** The company's display timezone — drives how ALL dates/times render (web + mobile). */
    timezoneDraft = 'Asia/Kolkata';
    readonly timezoneOptions = [
        { label: '(GMT+05:30) India — Kolkata',            value: 'Asia/Kolkata' },
        { label: '(GMT+00:00) UTC',                        value: 'UTC' },
        { label: '(GMT+04:00) Gulf — Dubai',               value: 'Asia/Dubai' },
        { label: '(GMT+03:00) Saudi Arabia — Riyadh',      value: 'Asia/Riyadh' },
        { label: '(GMT+05:00) Pakistan — Karachi',         value: 'Asia/Karachi' },
        { label: '(GMT+06:00) Bangladesh — Dhaka',         value: 'Asia/Dhaka' },
        { label: '(GMT+07:00) Bangkok / Jakarta',          value: 'Asia/Bangkok' },
        { label: '(GMT+08:00) Singapore',                  value: 'Asia/Singapore' },
        { label: '(GMT+09:00) Tokyo',                      value: 'Asia/Tokyo' },
        { label: '(GMT+10:00) Sydney',                     value: 'Australia/Sydney' },
        { label: '(GMT+00:00) London',                     value: 'Europe/London' },
        { label: '(GMT+01:00) Central Europe — Berlin',    value: 'Europe/Paris' },
        { label: '(GMT-05:00) US Eastern — New York',      value: 'America/New_York' },
        { label: '(GMT-06:00) US Central — Chicago',       value: 'America/Chicago' },
        { label: '(GMT-08:00) US Pacific — Los Angeles',   value: 'America/Los_Angeles' },
    ];
    /** The company's DEFAULT country (ISO 3166-1 alpha-2) — used to normalize phone numbers that
     *  arrive without a country code (a bare 10-digit number becomes +91…/+44… per this setting). */
    countryDraft = 'IN';
    readonly countryOptions = [
        { label: '🇮🇳 India (+91)',            value: 'IN' },
        { label: '🇬🇧 United Kingdom (+44)',   value: 'GB' },
        { label: '🇺🇸 United States (+1)',      value: 'US' },
        { label: '🇨🇦 Canada (+1)',            value: 'CA' },
        { label: '🇦🇪 UAE (+971)',             value: 'AE' },
        { label: '🇸🇦 Saudi Arabia (+966)',    value: 'SA' },
        { label: '🇵🇰 Pakistan (+92)',         value: 'PK' },
        { label: '🇧🇩 Bangladesh (+880)',      value: 'BD' },
        { label: '🇸🇬 Singapore (+65)',        value: 'SG' },
        { label: '🇦🇺 Australia (+61)',        value: 'AU' },
        { label: '🇩🇪 Germany (+49)',          value: 'DE' },
        { label: '🇫🇷 France (+33)',           value: 'FR' },
    ];
    /** Phone-format backfill (Company tab) — re-normalizes existing lead numbers. */
    phoneFixBusy = false;

    /** Live blob URL of the company logo — the SAME one the top-left brand mark renders,
     *  so the preview here always matches what every user sees. */
    get companyLogoSrc(): string | null {
        return this.verticalConfig.logoObjectUrl();
    }

    /** Save the Company configuration form (name + display timezone) in one click.
     *  Only fields that actually changed are sent; the timezone service updates
     *  immediately so every date on screen re-renders without a reload. */
    saveCompanyConfig(): void {
        if (this.savingCompany) return;
        const name = (this.companyNameDraft || '').trim();
        if (!name) {
            this.messageService.add({ severity: 'warn', summary: 'Company name is required' });
            return;
        }
        const tz = this.timezoneDraft;
        const country = this.countryDraft;
        const nameChanged = name !== (this.companyProfile?.name || '');
        const tzChanged = tz !== (this.companyProfile?.timezone || '');
        const countryChanged = country !== (this.companyProfile?.country || '');
        if (!nameChanged && !tzChanged && !countryChanged) {
            this.messageService.add({ severity: 'info', summary: 'Nothing to save',
                detail: 'The company configuration has no changes.' });
            return;
        }
        const calls: any[] = [];
        if (nameChanged) calls.push(this.whatsAppService.updateCompanyProfile({ name }));
        if (tzChanged) calls.push(this.api.put<any>('/api/company/timezone', { timezone: tz }));
        if (countryChanged) calls.push(this.api.put<any>('/api/company/country', { country }));
        this.savingCompany = true;
        forkJoin(calls).subscribe({
            next: () => {
                this.savingCompany = false;
                if (this.companyProfile) { this.companyProfile.name = name; this.companyProfile.timezone = tz; this.companyProfile.country = country; }
                if (tzChanged) this.tzService.setTimezone(tz);
                this.messageService.add({ severity: 'success', summary: 'Company profile saved',
                    detail: tzChanged ? 'Dates across the app now show in the chosen timezone.' : undefined });
            },
            error: (err) => {
                this.savingCompany = false;
                this.showError('Could not save company profile', err);
            }
        });
    }

    /** Last dry-run result of the phone-format backfill (null until previewed). */
    phoneFixPreview: { scanned: number; changed: number; samples: { old: string; new: string }[] } | null = null;

    /** Preview (dry run) how many stored lead phones would be re-normalized to country-aware E.164. */
    previewPhoneFix(): void {
        if (this.phoneFixBusy) return;
        this.phoneFixBusy = true;
        this.api.post<any>('/api/company/phones/backfill?apply=false', {}).subscribe({
            next: (res: any) => {
                this.phoneFixBusy = false;
                const d = res?.data ?? res ?? {};
                this.phoneFixPreview = d;
                this.messageService.add({ severity: 'info', summary: 'Phone format preview',
                    detail: `${d.changed || 0} of ${d.scanned || 0} numbers would be updated (default country ${d.region || this.countryDraft}).` });
            },
            error: (err) => { this.phoneFixBusy = false; this.showError('Could not preview phone fixes', err); }
        });
    }

    /** Apply the backfill — persists the normalized numbers. Only enabled after a preview shows changes. */
    applyPhoneFix(): void {
        if (this.phoneFixBusy || !this.phoneFixPreview?.changed) return;
        this.phoneFixBusy = true;
        this.api.post<any>('/api/company/phones/backfill?apply=true', {}).subscribe({
            next: (res: any) => {
                this.phoneFixBusy = false;
                const d = res?.data ?? res ?? {};
                this.messageService.add({ severity: 'success', summary: 'Phone numbers updated',
                    detail: `${d.changed || 0} numbers normalized to E.164.` });
                this.phoneFixPreview = null;
            },
            error: (err) => { this.phoneFixBusy = false; this.showError('Could not apply phone fixes', err); }
        });
    }

    /** Upload / replace the COMPANY logo (admin). Shown in the top-left corner for every
     *  user in the company — independent of the admin's personal photo in My profile. */
    onCompanyLogo(ev: Event): void {
        const input = ev.target as HTMLInputElement;
        const f = input?.files?.[0];
        if (!f) return;
        this.uploadingLogo = true;
        this.whatsAppService.uploadCompanyLogo(f).subscribe({
            next: (res: any) => {
                this.uploadingLogo = false;
                if (input) input.value = '';
                const path = res?.data?.logoUrl ?? res?.logoUrl ?? null;
                if (this.companyProfile) this.companyProfile.logoUrl = path;
                // Swap the top-left mark live — other users pick it up on their next app load.
                this.verticalConfig.applyCompanyLogo(path);
                this.messageService.add({ severity: 'success', summary: 'Logo updated',
                    detail: 'Everyone in your company now sees this logo in the top-left.' });
            },
            error: (err) => {
                this.uploadingLogo = false;
                if (input) input.value = '';
                this.showError('Could not upload the logo', err);
            }
        });
    }

    /** Remove the company logo — the apps fall back to the default mark. */
    removeCompanyLogo(): void {
        if (this.uploadingLogo) return;
        this.uploadingLogo = true;
        this.whatsAppService.removeCompanyLogo().subscribe({
            next: () => {
                this.uploadingLogo = false;
                if (this.companyProfile) this.companyProfile.logoUrl = null;
                this.verticalConfig.applyCompanyLogo(null);
                this.messageService.add({ severity: 'success', summary: 'Logo removed',
                    detail: 'The default mark shows until you upload a new logo.' });
            },
            error: (err) => {
                this.uploadingLogo = false;
                this.showError('Could not remove the logo', err);
            }
        });
    }

    ngOnInit(): void {
        this.fieldLabels.load().subscribe();   // names shown beside the message-variable paths
        this.initForms();
        this.verticalOptions = this.verticalConfig.options();

        // Active tab is driven by the `?tab=` query param so the global submenu
        // panel can switch tabs without the page maintaining its own nav. If no
        // tab is set on first load, redirect to the default so the global panel
        // highlights the right entry. The optional `channel` param drives the
        // WhatsApp ↔ Chatbot sub-toggle when on the channels tab.
        //
        // Per-tab lazy loading: nothing is fetched up front. Each tab's APIs are
        // pulled the first time that tab is opened (see loadActiveTabData), so
        // landing on Settings no longer fires every module's endpoint at once.
        this.route.queryParamMap.subscribe(params => {
            const requested = params.get('tab');

            // Non-admins (agent / manager) get exactly ONE Settings tab: their own profile.
            // Force it and ignore any other ?tab= (a deep-link to an admin tab would 403 its APIs).
            if (!this.isCompanyAdmin) {
                if (requested !== 'profile') {
                    this.routerNav.navigate([], {
                        relativeTo: this.route, queryParams: { tab: 'profile' }, replaceUrl: true,
                    });
                    return;
                }
                this.activeTab = 'profile';
                this.loadActiveTabData();
                return;
            }

            // Pro mode hides Workflow Stages (config), Users (userslist) and Teams (users) —
            // a bookmarked deep-link to those must not render the hidden tab. Conversely the
            // Pro-only 'statuses' tab must not render outside Pro (it targets the hidden Default stage).
            // Activity Types + Events are available in Pro too (per request) — only the
            // journey/team/booking admin tabs stay enterprise-only.
            const proHiddenInPro = this.verticalConfig.proMode() && requested
                    && ['config', 'users', 'userslist', 'booking'].includes(requested);
            const proOnlyOutsidePro = !this.verticalConfig.proMode() && ['statuses', 'automation', 'reminders'].includes(requested as string);
            if (proHiddenInPro || proOnlyOutsidePro) {
                // Blocked deep-link → land on the hub instead of a tab the mode can't use.
                this.routerNav.navigate([], {
                    relativeTo: this.route, queryParams: {}, replaceUrl: true,
                });
                return;
            }
            if (requested && this.validTabs.includes(requested as Settings['activeTab'])) {
                this.activeTab = requested as Settings['activeTab'];
            } else if (!requested) {
                // No ?tab= → the Settings HUB landing page (categorised cards + search).
                this.activeTab = 'hub';
                return;
            }

            const channel = params.get('channel');
            if (this.activeTab === 'channels' && (channel === 'whatsapp' || channel === 'chatbot')) {
                this.channelTab = channel;
            }

            // Master Data tab: `?mdcat=` preselects a category (e.g. the Source Names menu entry).
            const mdcat = params.get('mdcat');
            if (this.activeTab === 'master-data' && mdcat) this.masterDataTab = mdcat as any;

            // Sources tab: `?source=` pre-filters to one integration type (submenu deep-links).
            // meta_ads renders the dedicated Meta panel instead of the generic table.
            const src = params.get('source');
            this.sourceTypeFilter = (this.activeTab === 'sources'
                && src && this.sourceTypeLabels[src]) ? src : null;
            if (this.sourceTypeFilter === 'meta_ads') {
                this.formProvider = 'meta';
                this.once('operations', () => this.loadOperations());
                this.metaPanelSource = null;
                this.metaForms = []; this.metaFormOpen = null;
            }
            if (this.sourceTypeFilter === 'google_ads') {
                this.formProvider = 'google';
                this.once('operations', () => this.loadOperations());
                this.metaPanelSource = null;
                this.metaForms = []; this.metaFormOpen = null;
            }

            // Load only what the now-active tab needs, the first time it's shown.
            this.loadActiveTabData();
        });
    }

    /** Data already fetched, keyed so a loader shared by more than one tab runs
     *  exactly once across the whole Settings session. */
    private loadedKeys = new Set<string>();

    /** Run {@code fn} only the first time {@code key} is requested. */
    private once(key: string, fn: () => void): void {
        if (this.loadedKeys.has(key)) return;
        this.loadedKeys.add(key);
        fn();
    }

    /**
     * Fetch only the APIs the active tab needs, the first time it's opened.
     * Called on every tab change (and first load). Shared data (company,
     * subscription, operations, statuses, …) is keyed via {@link once} so it
     * loads a single time no matter how many tabs reference it; re-opening a
     * tab never re-fetches. Some tabs also warm the data their dialogs bind to
     * (e.g. the Add-Source dialog needs operations + workflows + system fields;
     * the Map-Stage-to-Team dialog needs the workflow stages).
     */
    private loadActiveTabData(): void {
        switch (this.activeTab) {
            case 'hub':
                // The landing page is pure navigation — nothing to fetch.
                break;
            case 'profile':
                // The embedded <app-my-profile> loads its own data — nothing to fetch here.
                break;
            case 'company':
                this.once('company', () => this.loadCompany());
                this.once('vertical', () => this.loadVertical());
                this.once('subscription', () => this.loadSubscriptionPlan());
                break;
            case 'channels':
                this.once('providers', () => this.getProvider());
                this.once('whatsapp', () => this.loadWhatsApp());
                this.once('chatbot', () => this.loadChatbotSettings());
                // The Add/Edit Number dialog binds a journey to the number.
                this.once('operations', () => this.loadOperations());
                break;
            case 'config':
                this.once('statuses', () => this.loadStatuses());
                this.once('workflows', () => this.loadWorkflows());
                this.once('operations', () => this.loadOperations());
                break;
            case 'users': // Teams — Map-Stage-to-Team dialog binds the workflow stages.
                this.once('teams', () => this.loadTeams());
                this.once('employees', () => this.loadEmployees());
                this.once('statuses', () => this.loadStatuses());
                break;
            case 'userslist': // Users
                this.once('employees', () => this.loadEmployees());
                this.once('permTemplates', () => this.loadPermissionTemplates());
                break;
            case 'billing':
                this.once('subscription', () => this.loadSubscriptionPlan());
                break;
            case 'sources': // Add/Edit Source dialog binds operations, workflows + system fields.
                this.once('sources', () => this.loadSources());
                // Journey dropdown in the per-form mapping editor (Meta panel AND edit dialog).
                this.once('operations', () => this.loadOperations());
                this.once('operations', () => this.loadOperations());
                this.once('workflows', () => this.loadWorkflows());
                this.ensureSystemFieldsLoaded(); // self-guarding
                break;
            case 'master-data':
                this.once('master-data', () => {
                    this.loadMasterData('SYSTEM_FIELD');
                    this.loadMasterData('TAG');
                    this.loadMasterData('LOSS_REASON');
                    this.loadMasterData('SOURCE_NAME');
                });
                break;
            case 'events':
                this.once('company', () => this.loadCompany());
                break;
            case 'api':
                this.once('company', () => this.loadCompany());
                this.once('apiKeys', () => this.loadApiKeys());
                break;
            case 'booking':
                // <app-booking-config> fetches its own data when it renders.
                break;
            case 'statuses': // Pro mode — statuses of the single hidden Default stage.
                this.once('statuses-tab', () => this.loadStatusesTab());
                break;
        }
    }

    /**
     * Pro-mode "Statuses" tab. Pro companies have one hidden Default stage, so
     * instead of the per-phase config dialog we resolve that stage here and drive
     * the SAME inline status editor (phaseConfigStage + phaseStatuses) directly.
     */
    loadStatusesTab(): void {
        this.loadingPhaseStatuses = true;
        this.whatsAppService.getStatuses().subscribe({
            next: (res: any) => {
                const rows: any[] = res?.data ?? res ?? [];
                const phase = rows.find(r => r?.isPhaseRoot === true && r?.isEntryPhase === true)
                    || rows.find(r => r?.isPhaseRoot === true)
                    || rows[0];
                if (!phase?.id) {
                    this.loadingPhaseStatuses = false;
                    this.phaseConfigStage = null;
                    this.phaseStatuses = [];
                    return;
                }
                this.phaseConfigStage = { id: phase.id, name: phase.name };
                this.resetPhaseStatusForm();
                this.loadPhaseStatuses(phase.id);
            },
            error: err => {
                this.loadingPhaseStatuses = false;
                this.showError('Load Statuses Failed', err);
            }
        });
    }

    loadWorkflows(): void {
        this.loadingWorkflows = true;
        this.workflowService.getCompanyWorkflows().subscribe({
            next: (res: any) => {
                const raw: any[] = res?.data ?? res ?? [];
                this.workflows = raw
                    .filter((w: any) => w.status === 'ACTIVE')
                    .sort((a: any, b: any) => a.flowName.localeCompare(b.flowName))
                    .map((w: any) => ({ id: w.id, flowName: w.flowName }));
                this.loadingWorkflows = false;
            },
            error: () => { this.loadingWorkflows = false; }
        });
    }

    loadOperations(): void {
        this.loadingOperations = true;
        this.operationsService.list().subscribe({
            next: (res: any) => {
                const raw: any[] = res?.data ?? res ?? [];
                this.operations = raw
                    .filter((o: any) => o.isActive !== false)
                    .map((o: any) => ({ id: o.id, name: o.name }));
                this.loadingOperations = false;
            },
            error: () => { this.loadingOperations = false; }
        });
    }

    private initForms() {
        this.companyForm = this.fb.group({
            name: [{ value: '', disabled: true }],
            plan: [{ value: '', disabled: true }],
            status: [{ value: '', disabled: true }]
        });
        this.userInviteForm = this.fb.group({
            name: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.email]],
            phone: [''],
            roles: [['ROLE_AGENT'], [Validators.required]],
            // Optional staff code — blank lets the backend auto-generate (EMP-0001).
            employeeCode: [''],
            // Optional departments (teams) the user belongs to.
            departmentIds: [[]],
            // Optional permission template — null = role default. On save it goes to a
            // separate endpoint (permissionTemplateService.assignUser), not the user PUT.
            permissionTemplateId: [null],
            // Temporary password the admin sets and shares; user must change it at first sign-in.
            password: ['', [Validators.required, Validators.minLength(6)]]
        });
    }

    isUserFormInvalid(field: string): boolean {
        const c = this.userInviteForm?.get(field);
        return !!(c?.invalid && (c.dirty || c.touched));
    }

    openAddUserDialog(): void {
        if (!this.subscriptionService.canAddUser()) {
            this.messageService.add({ severity: 'warn', summary: 'Limit Reached', detail: 'User limit reached for your current plan.' });
            return;
        }
        this.editingUserId = null;
        this.userInviteForm.reset({ name: '', email: '', phone: '', roles: ['ROLE_AGENT'],
            employeeCode: '', departmentIds: [], permissionTemplateId: null, password: '' });
        this.userInviteForm.get('email')?.enable();
        const pwd = this.userInviteForm.get('password');
        pwd?.setValidators([Validators.required, Validators.minLength(6)]);
        pwd?.updateValueAndValidity();
        this.userInviteForm.markAsPristine();
        this.userInviteForm.markAsUntouched();
        this.userPasswordVisible = false;
        this.ensureDepartmentsLoaded();
        this.showAddUserDialog = true;
    }

    /** Open the dialog to EDIT an existing user (email immutable; password not changed here). */
    openEditUserDialog(u: any): void {
        this.editingUserId = u.id;
        const roleValues = (u.roles || [])
            .map((r: any) => (typeof r === 'string' ? r : (r?.name || r?.authority || r?.value)))
            .filter(Boolean);
        this.userInviteForm.reset({
            name: u.name || '',
            email: u.email || '',
            phone: u.phone || '',
            roles: roleValues.length ? roleValues : ['ROLE_AGENT'],
            employeeCode: u.employeeCode || '',
            departmentIds: Array.isArray(u.departmentIds)
                ? u.departmentIds
                : (u.departmentId ? [u.departmentId] : []),
            permissionTemplateId: this.isAdminUser(u) ? null : (u.permissionTemplateId || null),
            password: '',
        });
        this.userInviteForm.get('email')?.disable();
        const pwd = this.userInviteForm.get('password');
        pwd?.clearValidators();
        pwd?.updateValueAndValidity();
        this.userInviteForm.markAsPristine();
        this.userInviteForm.markAsUntouched();
        this.userPasswordVisible = false;
        this.ensureDepartmentsLoaded();
        this.showAddUserDialog = true;
    }

    /** Fill the temporary-password field with a strong, easy-to-read password. */
    generateUserPassword(): void {
        const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ', lower = 'abcdefghijkmnopqrstuvwxyz', digits = '23456789', symbols = '!@#$%&*?';
        const all = upper + lower + digits + symbols;
        const rand = (s: string) => s[Math.floor(Math.random() * s.length)];
        let pwd = rand(upper) + rand(lower) + rand(digits) + rand(symbols);
        while (pwd.length < 12) pwd += rand(all);
        this.userInviteForm.get('password')?.setValue(pwd.split('').sort(() => Math.random() - 0.5).join(''));
        this.userInviteForm.get('password')?.markAsDirty();
    }

    getProvider() {
        this.whatsAppService.getChannelProviders().subscribe({
            next: res => { this.providersList = res; this.loadWaQuality(); },
            error: err => this.showError('Provider Load Failed', err)
        });
    }

    /** Pull live Meta quality for numbers + templates (read-only health badges). */
    loadWaQuality(): void {
        this.whatsAppService.getWhatsAppQuality().subscribe({
            next: (res: any) => {
                const data = res?.data ?? res ?? {};
                this.waNumberQuality = data?.numbers ?? {};
                this.waTemplateQuality = data?.templates ?? {};
            },
            error: () => { /* health badges are best-effort — ignore failures */ }
        });
    }

    /** PrimeNG tag severity for a Meta quality rating (GREEN/YELLOW/RED). */
    waQualitySeverity(rating: string | undefined | null): 'success' | 'warn' | 'danger' | 'secondary' {
        switch ((rating || '').toUpperCase()) {
            case 'GREEN':  return 'success';
            case 'YELLOW': return 'warn';
            case 'RED':    return 'danger';
            default:       return 'secondary';
        }
    }

    /** Friendly label for a Meta quality rating. */
    waQualityLabel(rating: string | undefined | null): string {
        const r = (rating || '').toUpperCase();
        if (r === 'GREEN')  return 'High';
        if (r === 'YELLOW') return 'Medium';
        if (r === 'RED')    return 'Low';
        return r || 'Unknown';
    }

    /** True when the signed-in user is a company admin (sees company details). */
    get isCompanyAdmin(): boolean {
        return (this.tokenService.getRoles() || []).includes('ROLE_COMPANY_ADMIN');
    }

    /** Friendly label for the user's primary role. */
    get myRoleLabel(): string {
        const roles = this.tokenService.getRoles() || [];
        if (roles.includes('ROLE_COMPANY_ADMIN')) return 'Admin';
        if (roles.includes('ROLE_MANAGER')) return 'Manager';
        if (roles.includes('ROLE_AGENT')) return 'Agent';
        return 'Member';
    }

    /** Jump from Company profile to the personal My profile tab. */
    goToMyProfile(): void {
        this.routerNav.navigate([], { relativeTo: this.route, queryParams: { tab: 'profile' } });
    }

    /** Initials for the signed-in user's avatar. */
    getMyInitials(): string {
        const n = (this.myProfile?.name || '').trim();
        if (!n) return 'U';
        const parts = n.split(/\s+/);
        return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'U';
    }

    /** Load the signed-in user's own profile (drives the role-aware Profile tab). */
    loadMyProfile(): void {
        this.teamService.getMyProfile().subscribe({
            next: (res: any) => { this.myProfile = (res?.data ?? res) ?? null; },
            error: () => { this.myProfile = null; }
        });
    }

    loadCompany() {
        this.loadMyProfile();
        this.loadingCompany = true;
        this.whatsAppService.getCompanyProfileDetails().subscribe({
            next: res => {
                const data = (res as any)?.data ?? res;
                this.companyProfile = data;
                this.timezoneDraft = data?.timezone || 'Asia/Kolkata';
                this.countryDraft = data?.country || 'IN';
                this.companyNameDraft = data?.name ?? '';
                // Keep the shared brand mark in sync with the stored logo (undefined =
                // an older backend without the field — leave the cached logo alone).
                if (data && data.logoUrl !== undefined) {
                    this.verticalConfig.applyCompanyLogo(data.logoUrl);
                }
                this.companyForm.patchValue({
                    name: data?.name ?? '',
                    plan: '',
                    status: data?.status ?? ''
                });
                this.loadingCompany = false;
            },
            error: err => {
                this.loadingCompany = false;
                this.showError('Company Load Failed', err);
            }
        });
    }

    /** Read the saved vertical so the dropdown reflects the company's choice. */
    loadVertical() {
        this.operationsService.getCompanyAiSettings().subscribe({
            next: (s: any) => {
                const v = (s?.vertical ?? 'generic').toString();
                this.selectedVertical = (this.verticalOptions.some(o => o.value === v) ? v : 'generic') as VerticalKey;
            },
            error: () => { this.selectedVertical = 'generic'; }
        });
    }

    /** Persist the vertical and re-skin the app immediately (theme + vocabulary). */
    onVerticalChange(v: VerticalKey) {
        this.selectedVertical = v;
        this.savingVertical = true;
        // Instant feedback — recolour + relabel now; the save just persists it.
        this.verticalConfig.applyVertical(v, true);
        this.operationsService.saveCompanyAiSettings({ vertical: v } as any).subscribe({
            next: () => {
                this.savingVertical = false;
                this.messageService.add({
                    severity: 'success', summary: 'Vertical updated',
                    detail: `Look & feel switched to ${this.verticalOptions.find(o => o.value === v)?.label ?? v}.`
                });
            },
            error: err => {
                this.savingVertical = false;
                this.showError('Could not save vertical', err);
            }
        });
    }

    loadSubscriptionPlan() {
        this.whatsAppService.getSubscriptionPlan().subscribe({
            next: res => {
                const data = (res as any)?.data ?? res;
                this.subscriptionProfile = data;
                this.companyForm.patchValue({
                    plan: data?.plan ?? ''
                });
            },
            error: err => this.showError('Plan Load Failed', err)
        });
    }

    loadWhatsApp() {
        this.loadingWhatsApp = true;
        this.whatsAppService.getChannelAccounts().subscribe({
            next: accounts => {
                this.loadWidgetSecretFromAccounts(accounts || []);
                // Pick the WhatsApp/Meta account, not the web-widget (INSTAREPLI) account
                const waAccount = (accounts || []).find((a: any) =>
                    (a.providerName || '').toLowerCase() === 'meta'
                ) ?? (accounts || []).find((a: any) =>
                    (a.providerName || '').toLowerCase() !== 'instarepli'
                );
                if (waAccount) {
                    this.metaConnected = true;
                    this.channelAccount = waAccount;
                    this.loadNumbers();
                } else {
                    this.metaConnected = false;
                    this.channelAccount = null;
                    this.loadingWhatsApp = false;
                }
            },
            error: err => {
                this.loadingWhatsApp = false;
                this.widgetSecretKey = '';
                this.showError('WhatsApp Load Failed', err);
            }
        });
    }

    private loadWidgetSecretFromAccounts(accounts: any[]): void {
        if (!accounts?.length) {
            this.widgetSecretKey = '';
            return;
        }

        const configCalls = accounts.map((account: any) =>
            this.whatsAppService.getChannelConfigs(account.id).pipe(catchError(() => of([])))
        );

        forkJoin(configCalls).subscribe({
            next: (allConfigs: any[]) => {
                const merged = (allConfigs || []).flat();
                const widgetCfg = merged.find((cfg: any) => cfg?.phoneNumber === 'Widget' && !!cfg?.senderIdentifier);
                this.widgetSecretKey = widgetCfg?.senderIdentifier || '';
            },
            error: () => {
                this.widgetSecretKey = '';
            }
        });
    }

    loadNumbers() {
        this.whatsAppService.getChannelConfigs(this.channelAccount.id).subscribe({
            next: res => {
                this.channelConfigs = res.map((cfg: any) => ({
                    ...cfg,
                    status: cfg.status || 'PENDING',
                    testing: false
                }));
                this.loadingWhatsApp = false;
            },
            error: err => {
                this.loadingWhatsApp = false;
                this.showError('Numbers Load Failed', err);
            }
        });
    }

    saveAccount() {
        const payload = {
            providerId: this.providersList[0]?.id,
            accountName: this.accountForm.accountName,
            // externalAccountId: this.accountForm.externalAccountId,
            // No token: the platform system-user token is resolved server-side for every tenant.
            // The field stays in the payload shape so the existing endpoint is unchanged, and so a
            // legacy per-client token already stored on an account keeps working as a fallback.
            credentials: {}
        };

        this.whatsAppService.createChannelAccount(payload).subscribe({
            next: () => {
                this.showAccountDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'WhatsApp Account Linked' });
                this.loadWhatsApp();
            },
            error: err => this.showError('Connection Failed', err)
        });
    }

    openEditNumber(row: any): void {
        this.editingConfig = row;
        this.numberForm = {
            senderIdentifier: row.senderIdentifier ?? '',
            displayName: row.displayName ?? '',
            isDefault: row.isDefault ?? false,
            phoneNumber: row.phoneNumber ?? '',
            channelType: row.channelType ?? 'CUSTOMER',
            wabaId: row.wabaId ?? '',
            operationId: row.operationId ?? null
        };
        this.showNumberDialog = true;
    }

    saveNumber() {
        const payload = {
            channelAccount: { id: this.channelAccount.id },
            channel: 'WHATSAPP',
            senderIdentifier: this.numberForm.senderIdentifier,
            displayName: this.numberForm.displayName,
            isDefault: this.numberForm.isDefault,
            inboundEnabled: true,
            phoneNumber: this.numberForm.phoneNumber,
            outboundEnabled: true,
            channelType: this.numberForm.channelType,
            wabaId: this.numberForm.wabaId || undefined,
            // Always sent, including as null — the backend writes this field
            // verbatim, so omitting it on edit would silently unbind the journey.
            operationId: this.numberForm.operationId ?? null
        };

        if (this.editingConfig) {
            this.whatsAppService.updateChannelConfig(this.editingConfig.id, payload).subscribe({
                next: () => {
                    this.showNumberDialog = false;
                    this.editingConfig = null;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Number Updated' });
                    this.loadNumbers();
                },
                error: err => this.showError('Update Number Failed', err)
            });
        } else {
            this.whatsAppService.createChannelConfig(payload).subscribe({
                next: () => {
                    this.showNumberDialog = false;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Number Added' });
                    this.loadNumbers();
                },
                error: err => this.showError('Add Number Failed', err)
            });
        }
    }

    testSender(row: any) {
        row.testing = true;
        this.whatsAppService.testSender(row.id).subscribe({
            next: () => {
                row.status = 'VERIFIED';
                row.testing = false;
                this.messageService.add({ severity: 'success', summary: 'Verified', detail: 'Number is active' });
            },
            error: err => {
                row.status = 'FAILED';
                row.testing = false;
                this.showError('Test Failed', err);
            }
        });
    }

    /** One-click subscribe: connects this number's WABA to our VENDOR BROADCASTING webhook so it
     *  starts receiving WhatsApp messages. Uses the central VB token server-side — no token needed. */
    subscribeNumber(row: any) {
        row.subscribing = true;
        this.whatsAppService.subscribeNumber(row.id).subscribe({
            next: () => {
                row.subscribing = false;
                this.messageService.add({ severity: 'success', summary: 'Subscribed',
                    detail: 'This number will now receive WhatsApp messages.' });
            },
            error: (err: any) => {
                row.subscribing = false;
                this.showError('Subscribe failed', err);
            }
        });
    }

    getStatusSeverity(status: string): "success" | "secondary" | "info" | "warn" | "danger" | "contrast" | undefined {
        switch (status) {
            case 'VERIFIED': return 'success';
            case 'PENDING': return 'warn';
            case 'FAILED': return 'danger';
            default: return 'info';
        }
    }

    private showError(summary: string, detail: any) {
        this.messageService.add({ severity: 'error', summary, detail: detail.message || detail });
    }

    openMetaGuide(): void {
        window.open('https://developers.facebook.com/docs/whatsapp/cloud-api/get-started', '_blank', 'noopener,noreferrer');
    }

    async copyToClipboard(value: string, successDetail: string): Promise<void> {
        if (!value) {
            return;
        }
        try {
            await navigator.clipboard.writeText(value);
            this.messageService.add({ severity: 'success', summary: 'Copied', detail: successDetail });
        } catch {
            this.messageService.add({ severity: 'warn', summary: 'Copy Failed', detail: 'Clipboard access is not available.' });
        }
    }


    private resolveApiBase(): string {
        if (environment.apiBaseUrl.startsWith('http://') || environment.apiBaseUrl.startsWith('https://')) {
            return environment.apiBaseUrl.replace(/\/$/, '');
        }
        return `${window.location.origin}${environment.apiBaseUrl}`.replace(/\/$/, '');
    }

    /* The "Webhook & Token for Meta" card was removed along with its two getters.
       Neither value was ever the admin's to copy:
         - the callback is registered ONCE on the Meta app (https://instarepli.live/public/webhook/meta
           — the definer's public origin, NOT this app's: instarepli.com proxies only /api, /conversation
           and /ws, so a webhook posted there would get index.html back);
         - the verify token is a single app-level @Value in MetaWebhookController, never per company.
           The old getter built `instarepli-${companyProfile?.id}`, which matched no server value and
           displayed "instarepli-undefined" whenever the profile had not loaded.
       Pressing Subscribe on a number is what actually points Meta at us. */

    get websiteEmbedCode(): string {
        const side = this.chatbotStyle.position === 'left' ? 'left' : 'right';
        return `<script>\n(function(w,d,s,u){\n  var js=d.createElement(s); js.src=u; js.async=true;\n  d.head.appendChild(js);\n  js.onload=function(){\n    w.InstarepliWidget.init({\n      iframeUrl: '${window.location.origin}/webchat-widget?secretKey=YOUR_SECRET_ID',\n      position: '${side}',\n      theme: {\n        primaryColor: '${this.chatbotStyle.primaryColor}',\n        textColor: '${this.chatbotStyle.textColor}',\n        borderRadius: ${this.chatbotStyle.borderRadius},\n        botName: '${this.escapeJsText(this.chatbotStyle.botName)}',\n        initialMessage: '${this.escapeJsText(this.chatbotStyle.initialMessage)}',\n        quickButtons: ${this.quickButtonsJsArray}\n      }\n    });\n  };\n})(window,document,'script','${window.location.origin}/assets/widget/loader.js');\n</script>`;
    }

    get mobileWebViewCode(): string {
        return `// React Native WebView\nimport { WebView } from 'react-native-webview';\n\n<WebView\n  source={{ uri: '${window.location.origin}/webchat-widget?secretKey=YOUR_SECRET_ID' }}\n  javaScriptEnabled\n  domStorageEnabled\n  style={{ flex: 1 }}\n/>`;
    }

    get centeredChatbotCode(): string {
        return `<iframe\n  src=\"${window.location.origin}/webchat-center?secretKey=YOUR_SECRET_ID\"\n  title=\"Instarepli Chat\"\n  style=\"width:100%;max-width:900px;height:720px;border:0;display:block;margin:0 auto;\"\n></iframe>`;
    }

    saveChatbotSettings(): void {
        const payload = {
            secretKey: this.widgetSecretKey || null,
            botName: this.chatbotStyle.botName,
            initialMessage: this.chatbotStyle.initialMessage,
            quickButtons: (this.chatbotStyle.quickButtons || []).filter((x) => !!String(x || '').trim()),
            position: this.chatbotStyle.position,
            primaryColor: this.chatbotStyle.primaryColor,
            textColor: this.chatbotStyle.textColor,
            borderRadius: this.chatbotStyle.borderRadius
        };

        this.savingChatbotSettings = true;
        this.whatsAppService.saveChatbotSettings(payload).subscribe({
            next: (res: any) => {
                this.savingChatbotSettings = false;
                if (res?.success !== true) {
                    this.showError('Save Failed', res?.message || 'Unable to save chatbot settings.');
                    return;
                }
                this.applyChatbotSettingsResponse(res?.data ?? {});
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: res?.message || 'Chatbot settings saved successfully'
                });
            },
            error: (err) => {
                this.savingChatbotSettings = false;
                this.showError('Save Failed', err);
            }
        });
    }

    /**
     * Pencil on the Linked Account card. This is a RENAME dialog now: the token box is collapsed
     * behind a link, because a shared WABA needs no per-client token. It is kept, not deleted, so a
     * WABA that was never shared with us (or a client who insists on their own token) still has a
     * way in — that fallback is the only reason the credentials column is still read at send time.
     */
    openTokenEditor(): void {
        this.tokenForm.accountName = this.channelAccount?.accountName || '';
        this.tokenForm.token = this.channelAccount?.credentials?.accessToken || '';
        this.showAdvancedToken = false;
        this.showTokenDialog = true;
    }

    saveAccessToken(): void {
        const token = (this.tokenForm.token || '').trim();
        const accountName = (this.tokenForm.accountName || '').trim();

        if (!this.channelAccount?.id || (!token && !accountName)) {
            this.showError('Nothing to Save', 'Enter a friendly name or a new access token.');
            return;
        }

        // Send only what was filled in. The backend writes the keys it receives, so a
        // rename must not carry an empty token along and blank a working connection.
        const patch: { accountName?: string; accessToken?: string } = {};
        if (accountName) patch.accountName = accountName;
        if (token) patch.accessToken = token;

        this.whatsAppService.updateChannelAccount(this.channelAccount.id, patch).subscribe({
            next: () => {
                this.showTokenDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Linked account updated.' });
                this.loadWhatsApp();
            },
            error: err => this.showError('Update Failed', err)
        });
    }

    /**
     * Trash on the Linked Account card. Opens the confirm dialog and immediately loads
     * the impact report — the operator must read WHICH phone numbers leave GrowHigh
     * before the delete is offered, because the account delete cascades to them.
     */
    openRemoveAccountDialog(): void {
        if (!this.channelAccount?.id) {
            return;
        }
        this.accountUsage = null;
        this.showDeleteAccountDialog = true;
        this.loadAccountUsage();
    }

    /** Fills `accountUsage`. Leaves it null on failure so the dialog fails CLOSED —
     *  `canRemoveAccount` stays false and the confirm button stays disabled. */
    private loadAccountUsage(): void {
        if (!this.channelAccount?.id) {
            return;
        }
        this.loadingAccountUsage = true;
        this.whatsAppService.getAccountUsage(this.channelAccount.id).subscribe({
            next: (res: any) => {
                this.loadingAccountUsage = false;
                // Endpoints on this service are inconsistent about the ApiResponse
                // envelope, so unwrap `data` when it is there.
                this.accountUsage = res?.data ?? res ?? null;
            },
            error: err => {
                this.loadingAccountUsage = false;
                this.accountUsage = null;
                this.showError('Usage Check Failed', err);
            }
        });
    }

    /** Only true once the report has loaded AND the server says nothing blocks the delete. */
    get canRemoveAccount(): boolean {
        return !!this.accountUsage && this.accountUsage.canDelete === true;
    }

    /** Human-readable reasons the delete is refused (templates / lead / phase configs). */
    get accountUsageBlockers(): string[] {
        return this.accountUsage?.blockers ?? [];
    }

    confirmRemoveAccount(): void {
        if (!this.channelAccount?.id || !this.canRemoveAccount) {
            return;
        }

        this.deletingAccount = true;
        this.whatsAppService.deleteChannelAccount(this.channelAccount.id).subscribe({
            next: () => {
                this.deletingAccount = false;
                this.showDeleteAccountDialog = false;
                this.accountUsage = null;
                this.messageService.add({
                    severity: 'success', summary: 'Removed',
                    detail: 'The account and its WhatsApp numbers were removed from GrowHigh.'
                });
                // Clear locally first so the card cannot flash the stale "connected" state
                // while the reload is in flight; loadWhatsApp() then settles the real state.
                this.metaConnected = false;
                this.channelAccount = null;
                this.channelConfigs = [];
                this.loadWhatsApp();
            },
            error: err => {
                this.deletingAccount = false;
                // 409 = templates / lead configs / phase configs still reference these
                // numbers. The server names them; show that verbatim, never swallow it.
                const detail =
                    err?.message               // ApiService normalises the body message here
                    ?? err?.error?.message
                    ?? err?.error?.error
                    ?? 'Could not remove the account. Try again or contact support.';
                this.showError('Cannot Remove Account', detail);
                // Re-read usage so the dialog now lists the blockers the server just found
                // and disables the button instead of inviting a second doomed attempt.
                if (err?.status === 409) {
                    this.loadAccountUsage();
                }
            }
        });
    }

    createWebhookSecretKey(): void {
       const providerId = this.providersList?.find((p:any) => p.channel === 'WEB_WIDGET')?.id;
        if (!providerId) {
            this.showError('Provider Missing', 'Unable to find channel provider.');
            return;
        }

        const secretKey = this.generateWebhookSecretKey();
        const randomSuffix = this.randomDigits(4);
        const accountPayload = {
            providerId,
            accountName: `IR Bot ${randomSuffix}`,
            externalAccountId: `IRBOT-${Date.now().toString().slice(-8)}`,
            credentials: { accessToken: secretKey }
        };

        this.creatingWidgetSecret = true;

        this.whatsAppService.createChannelAccount(accountPayload).pipe(
            switchMap((createdAccount: any) => {
                const accountId = createdAccount?.id || createdAccount?.data?.id;
                if (!accountId) {
                    throw new Error('Unable to create channel account for webhook secret.');
                }

                const configPayload = {
                    channelAccount: { id: accountId },
                    channel: 'WEB_WIDGET',
                    senderIdentifier: secretKey,
                    displayName: `Widget-${this.randomDigits(4)}`,
                    isDefault: false,
                    inboundEnabled: true,
                    phoneNumber: 'Widget',
                    outboundEnabled: true,
                    channelType: 'CUSTOMER'
                };

                return this.whatsAppService.createChannelConfig(configPayload);
            })
        ).subscribe({
            next: () => {
                this.creatingWidgetSecret = false;
                this.widgetSecretKey = secretKey;
                this.widgetSecretVisible = false;
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Webhook secret key created.' });
                this.loadWhatsApp();
            },
            error: (err) => {
                this.creatingWidgetSecret = false;
                this.showError('Secret Key Creation Failed', err);
            }
        });
    }

    private generateWebhookSecretKey(): string {
        const companyIdRaw = `${this.companyProfile?.id || this.companyProfile?.companyId || ''}`;
        const idDigits = companyIdRaw.replace(/\D/g, '').slice(-4).padStart(4, '0');
        return `IR-bot-${idDigits}-${this.randomDigits(4)}`;
    }

    private randomDigits(length: number): string {
        return Array.from({ length }, () => Math.floor(Math.random() * 10)).join('');
    }

    loadChatbotSettings(): void {
        this.whatsAppService.getChatbotSettings().subscribe({
            next: (res: any) => {
                if (res?.success !== true) {
                    return;
                }
                this.applyChatbotSettingsResponse(res?.data ?? {});
            },
            error: () => {
                // Keep defaults if API fails
            }
        });
    }

    private applyChatbotSettingsResponse(data: any): void {
        const pickText = (value: any, fallback: string): string => {
            const v = typeof value === 'string' ? value.trim() : '';
            return v.length ? v : fallback;
        };
        const quickButtonsRaw = data?.quickButtons ?? data?.buttons ?? [];
        const parsedButtons = Array.isArray(quickButtonsRaw)
            ? quickButtonsRaw
                .map((btn: any) => (typeof btn === 'string' ? btn : (btn?.label || btn?.text || btn?.value || '')))
                .map((v: string) => v.trim())
                .filter((v: string) => !!v)
            : [];

        this.chatbotStyle = {
            ...this.chatbotStyle,
            botName: pickText(data?.botName, this.chatbotStyle.botName),
            primaryColor: pickText(data?.primaryColor, this.chatbotStyle.primaryColor),
            textColor: pickText(data?.textColor, this.chatbotStyle.textColor),
            position: data?.position === 'left' ? 'left' : (data?.position === 'right' ? 'right' : this.chatbotStyle.position),
            borderRadius: Number.isFinite(Number(data?.borderRadius)) ? Number(data.borderRadius) : this.chatbotStyle.borderRadius,
            initialMessage: pickText(data?.initialMessage, this.chatbotStyle.initialMessage),
            quickButtons: parsedButtons.length ? parsedButtons : this.chatbotStyle.quickButtons
        };

        if (typeof data?.secretKey === 'string' && data.secretKey.trim()) {
            this.widgetSecretKey = data.secretKey.trim();
        }
    }

    getCompanyInitials(): string {
        const name = this.companyProfile?.name || '';
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return 'C';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }

    get isFreePlan(): boolean {
        return (this.subscriptionProfile?.plan || '').toUpperCase() === 'FREE';
    }

    addQuickButton(): void {
        const next = [...this.chatbotStyle.quickButtons, `Option ${this.chatbotStyle.quickButtons.length + 1}`];
        this.chatbotStyle = { ...this.chatbotStyle, quickButtons: next };
    }

    removeQuickButton(index: number): void {
        const next = this.chatbotStyle.quickButtons.filter((_, i) => i !== index);
        this.chatbotStyle = { ...this.chatbotStyle, quickButtons: next.length ? next : ['Get Started'] };
    }

    private escapeJsText(value: string): string {
        return (value || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n');
    }

    get quickButtonsJsArray(): string {
        const buttons = (this.chatbotStyle.quickButtons || [])
            .map((item) => this.escapeJsText(item))
            .filter((item) => item.length > 0);
        const safe = buttons.length ? buttons : ['Get Started'];
        return `[${safe.map((b) => `'${b}'`).join(', ')}]`;
    }

    openUpgradeDialog(): void {
        this.showUpgradeDialog = true;
    }

    // ── Status ────────────────────────────────────────────────

    loadStatuses(): void {
        this.loadingStatuses = true;
        this.whatsAppService.getStatuses().subscribe({
            next: (res: any) => {
                this.loadingStatuses = false;
                this.statuses = res?.data ?? res ?? [];
            },
            error: err => {
                this.loadingStatuses = false;
                this.showError('Load Failed', err);
            }
        });
    }

    createStatus(): void {
        const name = (this.statusForm.name || '').trim();
        if (!name) {
            this.showError('Validation', 'Stage name is required.');
            return;
        }
        this.savingStatus = true;
        this.whatsAppService.createStatus({ name, displayOrder: this.statusForm.displayOrder }).subscribe({
            next: (res: any) => {
                this.savingStatus = false;
                const created = res?.data ?? res;
                this.statuses = [...this.statuses, created];
                this.statusForm = { name: '', displayOrder: this.statuses.length + 1 };
                this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message || 'Status created' });
            },
            error: err => {
                this.savingStatus = false;
                this.showError('Create Failed', err);
            }
        });
    }

    // ════════════════════════════════════════════════════════════
    // JOURNEY STAGES (V014+)
    //
    // The Settings → Journey Stages page is built around three derived
    // views:
    //   1. journeyStages — top-level (isPhaseRoot=true), ordered
    //   2. substagesOf(stageId) — children of a stage, ordered
    //   3. unfiledStages — pre-V014 flat rows still without a parent
    //      (rendered as a final fallback group)
    //
    // SOPs hang off whichever node is the leaf:
    //   - stage with no subs → SOPs attach to the stage itself
    //   - stage with subs    → each sub holds its own SOPs
    //                          (stage-level SOPs are still supported
    //                           but de-emphasised in this UI)
    // ════════════════════════════════════════════════════════════

    /** Top-level journey stages ordered by display order. */
    get journeyStages(): any[] {
        return (this.statuses || [])
            .filter((s: any) => s?.isPhaseRoot === true)
            .sort((a, b) => (
                (a.phaseDisplayOrder ?? a.displayOrder ?? 999)
                - (b.phaseDisplayOrder ?? b.displayOrder ?? 999)
            ));
    }

    /** Sub-stages of a given stage, ordered by displayOrder. */
    substagesOf(stageId: string): any[] {
        return (this.statuses || [])
            .filter((s: any) => s?.parentId === stageId)
            .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    }

    /** Pre-V014 flat statuses — neither stages nor sub-stages. */
    get unfiledStages(): any[] {
        return (this.statuses || [])
            .filter((s: any) => !s?.isPhaseRoot && !s?.parentId)
            .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    }

    get hasJourneyStages(): boolean {
        return this.journeyStages.length > 0;
    }

    // ── Journey Stages search ────────────────────────────────────────────────
    /** Free-text filter over the Journey Stages list (stage + sub-stage names). */
    stageSearch = '';

    private matchesStageSearch(s: any): boolean {
        const q = this.stageSearch.trim().toLowerCase();
        if (!q) return true;
        return String(s?.name ?? '').toLowerCase().includes(q);
    }

    /** Stages shown after the search filter. A stage survives when its OWN name matches or
     *  any of its sub-stages match — so searching a sub-stage still shows you where it lives.
     *  The unfiltered {@link journeyStages} stays intact for ordering / new-stage positioning. */
    get visibleJourneyStages(): any[] {
        if (!this.stageSearch.trim()) return this.journeyStages;
        return this.journeyStages.filter(st =>
            this.matchesStageSearch(st) || this.substagesOf(st.id).some(sub => this.matchesStageSearch(sub)));
    }

    /** Sub-stages shown under a stage. When the search matches the STAGE itself the whole
     *  stage is shown intact; otherwise only the matching sub-stages are listed. */
    visibleSubstagesOf(stageId: string): any[] {
        const subs = this.substagesOf(stageId);
        if (!this.stageSearch.trim()) return subs;
        const stage = this.journeyStages.find(s => s.id === stageId);
        if (stage && this.matchesStageSearch(stage)) return subs;
        return subs.filter(sub => this.matchesStageSearch(sub));
    }

    /** True when a search is active but nothing matched — drives the "no results" state. */
    get noStageSearchResults(): boolean {
        return !!this.stageSearch.trim() && this.visibleJourneyStages.length === 0;
    }

    clearStageSearch(): void { this.stageSearch = ''; }

    /** True when this stage has any sub-stages. Drives the UI fork
     *  between "show SOPs directly" vs "show sub-stages". */
    hasSubstages(stageId: string): boolean {
        return this.substagesOf(stageId).length > 0;
    }

    /** SOP count for the stage — sum across its sub-stages if any,
     *  otherwise the direct SOPs attached to the stage. */
    sopCountFor(stageId: string): number {
        if (this.hasSubstages(stageId)) {
            return this.substagesOf(stageId).reduce(
                (sum, sub) => sum + (this.sopsByOwner[sub.id]?.length ?? 0),
                0
            );
        }
        return this.sopsByOwner[stageId]?.length ?? 0;
    }

    // ── Expansion ──────────────────────────────────────────────

    toggleStage(stageId: string): void {
        if (this.expandedStages.has(stageId)) {
            this.expandedStages.delete(stageId);
        } else {
            this.expandedStages.add(stageId);
            // Eagerly load SOPs the first time the stage opens — for
            // stages without sub-stages, that's its direct SOP list.
            // For stages with sub-stages, sub-stage SOPs load on
            // sub-stage expansion (or on demand for counts).
            if (!this.hasSubstages(stageId) && !(stageId in this.sopsByOwner)) {
                this.loadSopsFor(stageId);
            }
            // Preload sub-stage SOPs so the count chips are accurate
            // before any sub-stage is opened.
            for (const sub of this.substagesOf(stageId)) {
                if (!(sub.id in this.sopsByOwner)) this.loadSopsFor(sub.id);
            }
        }
    }

    isStageExpanded(stageId: string): boolean {
        return this.expandedStages.has(stageId);
    }

    toggleSubstage(subId: string): void {
        if (this.expandedSubstages.has(subId)) {
            this.expandedSubstages.delete(subId);
        } else {
            this.expandedSubstages.add(subId);
            if (!(subId in this.sopsByOwner)) this.loadSopsFor(subId);
        }
    }

    isSubstageExpanded(subId: string): boolean {
        return this.expandedSubstages.has(subId);
    }

    // ── SOP loading ────────────────────────────────────────────

    private loadSopsFor(ownerId: string): void {
        this.loadingSopsFor[ownerId] = true;
        this.sopService.getTasksByStatus(ownerId).subscribe({
            next: (res: any) => {
                this.sopsByOwner[ownerId] = res?.data ?? res ?? [];
                this.loadingSopsFor[ownerId] = false;
            },
            error: () => {
                this.sopsByOwner[ownerId] = [];
                this.loadingSopsFor[ownerId] = false;
            }
        });
    }

    sopsFor(ownerId: string): any[] {
        return this.sopsByOwner[ownerId] ?? [];
    }

    // ── Add stage dialog ───────────────────────────────────────

    openAddStageDialog(): void {
        this.stageForm = {
            name: '',
            order: this.journeyStages.length + 1
        };
        this.showStageDialog = true;
    }

    createStage(): void {
        const name = (this.stageForm.name || '').trim();
        if (!name) {
            this.showError('Validation', 'Phase name is required.');
            return;
        }
        this.savingStage = true;
        // Sequence is intentionally NOT set here — phases are a library on
        // this screen. Order is assigned on the Operations screen where
        // phases get put into sequence. We send a high default so the row
        // sorts to the end of any legacy list view until an operation pins
        // it. (Backend's NOT NULL default would otherwise reject the row.)
        const positionAtEnd = (this.journeyStages?.length || 0) + 1;
        this.whatsAppService.createStatus({
            name,
            displayOrder: positionAtEnd,
            isPhaseRoot: true,
            phaseDisplayOrder: positionAtEnd
        }).subscribe({
            next: (res: any) => {
                this.savingStage = false;
                const created = res?.data ?? res;
                this.statuses = [...this.statuses, created];
                this.showStageDialog = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Stage created',
                    detail: `${created?.name ?? name} is ready — open it to add SOPs or sub-stages.`
                });
                // Auto-expand the new stage so the next action is obvious.
                if (created?.id) this.expandedStages.add(created.id);
            },
            error: err => {
                this.savingStage = false;
                this.showError('Create Failed', err);
            }
        });
    }

    // ── Add sub-stage dialog ───────────────────────────────────

    openAddSubstageDialog(stage: any): void {
        this.substageParentId = stage?.id ?? null;
        this.substageParentName = stage?.name ?? '';
        const existing = stage?.id ? this.substagesOf(stage.id) : [];
        this.substageForm = { name: '', order: existing.length + 1 };
        this.showSubstageDialog = true;
    }

    createSubstage(): void {
        const name = (this.substageForm.name || '').trim();
        if (!name || !this.substageParentId) {
            this.showError('Validation', 'Sub-stage name is required.');
            return;
        }
        this.savingSubstage = true;
        this.whatsAppService.createStatus({
            name,
            displayOrder: this.substageForm.order,
            parentId: this.substageParentId,
            isPhaseRoot: false
        }).subscribe({
            next: (res: any) => {
                this.savingSubstage = false;
                const created = res?.data ?? res;
                this.statuses = [...this.statuses, created];
                this.showSubstageDialog = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sub-stage added',
                    detail: `${created?.name ?? name} added under ${this.substageParentName}`
                });
            },
            error: err => {
                this.savingSubstage = false;
                this.showError('Create Failed', err);
            }
        });
    }

    // ── SOP dialog ─────────────────────────────────────────────

    openAddSopDialog(owner: any, kind: 'stage' | 'substage'): void {
        this.sopForOwnerId = owner?.id ?? null;
        this.sopForOwnerName = owner?.name ?? '';
        this.sopForOwnerKind = kind;
        const existing = this.sopForOwnerId ? this.sopsFor(this.sopForOwnerId) : [];
        this.journeySopForm = {
            id: null,
            label: '',
            description: '',
            slaValue: null,
            slaUnit: 'minutes',
            isMandatory: true,
            orderNo: existing.length + 1
        };
        this.showJourneySopDialog = true;
    }

    /** Open the SOP dialog pre-filled to EDIT an existing SOP. */
    openEditSopDialog(owner: any, sop: any, kind: 'stage' | 'substage'): void {
        this.sopForOwnerId = owner?.id ?? null;
        this.sopForOwnerName = owner?.name ?? '';
        this.sopForOwnerKind = kind;
        const sla = this.splitSlaMinutes(sop?.slaMinutes);
        this.journeySopForm = {
            id: sop?.id ?? null,
            label: sop?.label ?? '',
            description: sop?.description ?? '',
            slaValue: sla.value,
            slaUnit: sla.unit,
            isMandatory: sop?.isMandatory ?? true,
            orderNo: sop?.orderNo ?? 1
        };
        this.showJourneySopDialog = true;
    }

    /** Converts the dialog's value+unit to minutes. Null when no SLA set. */
    private toSlaMinutes(value: number | null, unit: 'minutes' | 'hours' | 'days'): number | null {
        if (value === null || value === undefined || isNaN(value) || value <= 0) return null;
        const mult = unit === 'days' ? 1440 : unit === 'hours' ? 60 : 1;
        return Math.round(value * mult);
    }

    /** Template-callable version of {@link toSlaMinutes} — used by the
     *  live preview "= 1h 30m" hint next to the SLA input. */
    toSlaMinutesPublic(value: number | null, unit: 'minutes' | 'hours' | 'days'): number | null {
        return this.toSlaMinutes(value, unit);
    }

    /** Inverse of toSlaMinutes — split stored minutes back to value + unit
        for hydrating the phase SLA form (prefers the largest whole unit). */
    private splitSlaMinutes(minutes: number | null | undefined): { value: number | null; unit: 'minutes' | 'hours' | 'days' } {
        if (!minutes || minutes <= 0) return { value: null, unit: 'minutes' };
        if (minutes % 1440 === 0) return { value: minutes / 1440, unit: 'days' };
        if (minutes % 60 === 0)   return { value: minutes / 60,   unit: 'hours' };
        return { value: minutes, unit: 'minutes' };
    }

    /** Friendly label for the SLA: "1h 30m" / "2 days" / "—". */
    formatSla(minutes: number | null | undefined): string {
        if (!minutes || minutes <= 0) return '—';
        if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes === 1440 ? '' : 's'}`;
        if (minutes >= 60) {
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return m ? `${h}h ${m}m` : `${h}h`;
        }
        return `${minutes}m`;
    }

    saveSop(): void {
        const label = (this.journeySopForm.label || '').trim();
        if (!label || !this.sopForOwnerId) {
            this.showError('Validation', 'SOP name is required.');
            return;
        }
        this.savingJourneySop = true;
        const slaMinutes = this.toSlaMinutes(this.journeySopForm.slaValue, this.journeySopForm.slaUnit);
        const payload = {
            statusId: this.sopForOwnerId,
            label,
            description: (this.journeySopForm.description || '').trim() || undefined,
            orderNo: this.journeySopForm.orderNo,
            isMandatory: this.journeySopForm.isMandatory,
            slaMinutes
        };
        const owner = this.sopForOwnerId!;
        const editingId = this.journeySopForm.id;

        // EDIT an existing SOP.
        if (editingId) {
            this.sopService.updateTask(editingId, payload).subscribe({
                next: (res: any) => {
                    this.savingJourneySop = false;
                    const updated = res?.data ?? res;
                    this.sopsByOwner[owner] = (this.sopsByOwner[owner] ?? [])
                        .map(s => s.id === editingId ? (updated ?? { ...s, ...payload, id: editingId }) : s);
                    this.showJourneySopDialog = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'SOP updated',
                        detail: `${updated?.label ?? label} updated`
                    });
                },
                error: err => {
                    this.savingJourneySop = false;
                    this.showError('Update Failed', err);
                }
            });
            return;
        }

        // CREATE a new SOP.
        this.sopService.createTask(payload).subscribe({
            next: (res: any) => {
                this.savingJourneySop = false;
                const created = res?.data ?? res;
                // Push into the cache so the UI updates immediately.
                if (!this.sopsByOwner[owner]) this.sopsByOwner[owner] = [];
                this.sopsByOwner[owner] = [...this.sopsByOwner[owner], created];
                this.showJourneySopDialog = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'SOP added',
                    detail: `${created?.label ?? label} added to ${this.sopForOwnerName}`
                });
            },
            error: err => {
                this.savingJourneySop = false;
                this.showError('Create Failed', err);
            }
        });
    }

    deleteSop(ownerId: string, sop: any): void {
        if (!sop?.id) return;
        // Native confirm — keeps this component dependency-free of
        // ConfirmationService. Swap to p-confirmDialog later if a
        // richer prompt is needed.
        if (!confirm(`Delete SOP "${sop.label}"?`)) return;
        this.sopService.deleteTask(sop.id).subscribe({
            next: () => {
                this.sopsByOwner[ownerId] =
                    (this.sopsByOwner[ownerId] ?? []).filter(s => s.id !== sop.id);
                this.messageService.add({
                    severity: 'success',
                    summary: 'SOP removed',
                    detail: `${sop.label} deleted`
                });
            },
            error: err => this.showError('Delete Failed', err)
        });
    }

    // ────────────────────────────────────────────────────────────
    // Map-workflow dialog — attach an AI workflow to a stage
    // ────────────────────────────────────────────────────────────

    /**
     * Resolves the label shown on the stage card for the currently-mapped
     * workflow. Returns null when nothing's mapped so the template can
     * fall back to "No workflow mapped".
     */
    mappedWorkflowLabel(stage: any): string | null {
        const id = stage?.aiWorkflowId;
        if (!id) return null;
        const match = (this.availableWorkflows || [])
            .find((w: any) => (w.id ?? w.flowId) === id);
        return match?.flowName ?? match?.name ?? match?.label ?? 'Workflow attached';
    }

    /** Convenience flag for the chip on the stage card. */
    hasMappedWorkflow(stage: any): boolean {
        return !!stage?.aiWorkflowId;
    }

    // ──────────────────────────────────────────────────────────────
    // V017/V018/V019/V020 — Unified Phase Configuration dialog
    // ──────────────────────────────────────────────────────────────

    /**
     * Open the tabbed configuration dialog for a phase. Populates the
     * General form synchronously from the row we already have, then
     * lazy-loads the tab content as the admin clicks through.
     */
    openPhaseConfigDialog(stage: any): void {
        if (!stage) return;
        this.phaseConfigStage = stage;
        this.phaseConfigActiveTab = 'general';

        // Hydrate General tab from the row.
        // entryMetadataFields arrives as an array of {fieldCode, required}
        // from the backend — normalise to that shape even if it comes
        // through as null or as objects with extra keys.
        const rawEntryFields = Array.isArray(stage.entryMetadataFields)
            ? stage.entryMetadataFields.map((f: any) => ({
                fieldCode: f?.fieldCode ?? f?.code ?? '',
                required: f?.required === true,
              })).filter((f: any) => !!f.fieldCode)
            : [];

        this.phaseGeneralForm = {
            name: stage.name || '',
            displayOrder: stage.displayOrder || stage.phaseDisplayOrder || 1,
            channel: (stage.channel || 'whatsapp') as any,
            channels: ((stage.channels && stage.channels.length) ? stage.channels : [stage.channel || 'whatsapp']) as string[],
            openingMode: (stage.openingMode || 'auto') as any,
            departmentId: stage.departmentId || null,
            departmentIds: Array.isArray(stage.departmentIds) ? [...stage.departmentIds] : [],
            restricted: stage.restricted === true,
            noticeTemplateId: stage.noticeTemplateId || null,
            slaValue: this.splitSlaMinutes(stage.slaMinutes).value,
            slaUnit: this.splitSlaMinutes(stage.slaMinutes).unit,
            isEntryPhase: stage.isEntryPhase === true,
            allowManualCreate: stage.allowManualCreate === true,
            isLastPhase: stage.isLastPhase === true,
            // Derive intake pattern from whether metadata is present.
            // If the row has fields → admin previously chose "lead_pushed".
            // If empty → assume "customer_initiated" (the more common case
            // across support / claim / hospital flows).
            intakePattern: (rawEntryFields.length > 0 ? 'lead_pushed' : 'customer_initiated') as any,
            entryMetadataFields: rawEntryFields,
        };

        // Pre-load SYSTEM_FIELD options if this is already flagged as
        // an entry phase — keeps the metadata picker ready without a
        // round trip after the user clicks the General tab.
        if (this.phaseGeneralForm.isEntryPhase) {
            this.ensureSystemFieldsLoaded();
        }

        // Reset other tab caches — they'll lazy-load on tab switch.
        this.phaseStatuses = [];
        this.phaseAuditLog = [];
        this.phaseWaConfigForm = {
            id: null, templateId: null, channelConfigId: null,
            variableMapping: {}, reminderSlots: [], headerImageUrl: '',
            triggerMode: 'ON_REPLY', triggerButtonText: '', greetingText: '', isActive: true,
        };
        this.phaseEmailForm = {
            emailTemplateId: null, trigger: 'PHASE_ACTIVATED',
            delayValue: 0, delayUnit: 'MINUTES', active: true,
        };

        // Pre-load lookups the General tab needs (departments + templates
        // — Notice template picker is on General, so don't make the admin
        // wait for a lazy load on first paint). Channel configs too, so
        // template-with-number labels render immediately.
        this.ensureDepartmentsLoaded();
        this.ensureTemplatesLoaded();
        this.ensureChannelConfigsLoaded();
        if (this.isEmailChannel) this.ensureEmailTemplatesLoaded();

        this.showPhaseConfigDialog = true;
    }

    /** Tab switcher — triggers lazy-load for the tab being entered. */
    switchPhaseConfigTab(tab: 'general' | 'opening' | 'voice' | 'email' | 'statuses' | 'sops' | 'subphases' | 'audit'): void {
        this.phaseConfigActiveTab = tab;
        if (!this.phaseConfigStage) return;
        const phaseId = this.phaseConfigStage.id;

        if (tab === 'opening' && !this.phaseWaConfigForm.id && !this.loadingPhaseWaConfig) {
            this.loadPhaseWaConfig(phaseId);
            this.ensureTemplatesLoaded();
            this.ensureChannelConfigsLoaded();
        }
        // Voice reuses the same phase config row — the channel config is a
        // voice line and reminderSlots are the call-retry cadence.
        if (tab === 'voice' && !this.phaseWaConfigForm.id && !this.loadingPhaseWaConfig) {
            this.loadPhaseWaConfig(phaseId);
            this.ensureChannelConfigsLoaded();
        }
        // Email pulls the real email-template catalogue + the persisted
        // phase email selection (stored in the same config row).
        if (tab === 'email') {
            this.ensureEmailTemplatesLoaded();
            if (!this.phaseWaConfigForm.id && !this.loadingPhaseWaConfig) {
                this.loadPhaseWaConfig(phaseId);
            }
            this.loadPhaseEmailSequences(phaseId);
        }
        if (tab === 'statuses' && !this.phaseStatuses.length && !this.loadingPhaseStatuses) {
            this.loadPhaseStatuses(phaseId);
        }
        if (tab === 'sops') {
            // Reuse the existing journey-sop loader. Single-arg signature —
            // it just needs the owner id (phase or sub-phase).
            this.loadSopsFor(phaseId);
        }
        if (tab === 'subphases') {
            // Sub-phase pane shows nested SOPs too — warm each sub-phase's
            // cache the same way the existing accordion does.
            const subs = this.substagesOf(phaseId);
            for (const sub of subs) {
                if (sub?.id && !(sub.id in this.sopsByOwner)) {
                    this.loadSopsFor(sub.id);
                }
            }
        }
        if (tab === 'audit' && !this.phaseAuditLog.length && !this.loadingPhaseAudit) {
            this.loadPhaseAudit(phaseId);
        }
    }

    /** Save the General tab — name / order / channel / openingMode / dept / notice. */
    savePhaseGeneral(): void {
        if (!this.phaseConfigStage?.id) return;
        const id = this.phaseConfigStage.id;
        const f = this.phaseGeneralForm;
        if (!f.name || !f.name.trim()) {
            this.showError('Validation', 'Phase name is required.');
            return;
        }

        this.savingPhaseGeneral = true;
        // Phase SLA → minutes. Send 0 (not null) when cleared so the backend
        // explicitly resets it (null would be treated as "no change").
        const slaMinutes = this.toSlaMinutes(f.slaValue, f.slaUnit);
        // Sequence fields are intentionally NOT sent — phases are a
        // library on this screen. The Operations screen is the only
        // surface that mutates displayOrder / phaseDisplayOrder.
        // entryMetadataFields is only meaningful when isEntryPhase is
        // true; we still send the cleared array when the user flips
        // entry-phase off so the backend doesn't keep stale config.
        this.whatsAppService.updateStatus(id, {
            name: f.name.trim(),
            // A RESTRICTED stage keeps its business-partner teams in departmentIds and has no
            // primary owner, so writing these two here would clear that list. Those stages are
            // configured in "Map team to stage"; leave both columns untouched (undefined is not
            // serialised, so the backend sees no change).
            departmentId: f.restricted ? undefined : f.departmentId,
            // Co-owners only make sense under a primary owner, and the primary must not repeat
            // in the list — the backend de-dupes, but storing it twice reads as a second team.
            departmentIds: f.restricted ? undefined
                : (f.departmentId
                    ? (f.departmentIds || []).filter((t) => t && t !== f.departmentId)
                    : []),
            channel: (f.channels && f.channels.length ? f.channels[0] : f.channel),
            channels: f.channels,
            openingMode: f.openingMode,
            noticeTemplateId: f.noticeTemplateId,
            slaMinutes: slaMinutes === null ? 0 : slaMinutes,
            isEntryPhase: f.isEntryPhase,
            allowManualCreate: !!f.allowManualCreate,
            isLastPhase: f.isLastPhase,
            entryMetadataFields: f.isEntryPhase
                ? (f.entryMetadataFields || [])
                : [],
        }).subscribe({
            next: (res: any) => {
                this.savingPhaseGeneral = false;
                // Reflect the server's view on the dialog's stage object so
                // re-opening the dialog shows the saved state.
                if (res && typeof res === 'object') {
                    Object.assign(this.phaseConfigStage, res);
                }
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: 'Phase configuration updated'
                });
                // Refresh the stages list so the card reflects the new
                // channel/opening-mode badges.
                this.loadStatuses();
            },
            error: err => {
                this.savingPhaseGeneral = false;
                this.showError('Save Failed', err);
            }
        });
    }

    /** Helper for the Opening tab — load the phase's wa config row (if any). */
    private loadPhaseWaConfig(phaseId: string): void {
        this.loadingPhaseWaConfig = true;
        this.whatsAppService.getPhaseWaConfig(phaseId).subscribe({
            next: (res: any) => {
                this.loadingPhaseWaConfig = false;
                if (res && res.id) {
                    this.phaseWaConfigForm = {
                        id: res.id,
                        templateId: res.templateId || null,
                        channelConfigId: res.channelConfigId || null,
                        variableMapping: res.variableMapping || {},
                        reminderSlots: (Array.isArray(res.reminderSlots) ? res.reminderSlots : [])
                            .map((s: any) => this.normaliseLoadedSlot(s)),
                        headerImageUrl: res.headerImageUrl || '',
                        triggerMode: res.triggerMode === 'ON_BUTTON' ? 'ON_BUTTON' : 'ON_REPLY',
                        triggerButtonText: res.triggerButtonText || '',
                        greetingText: res.greetingText || '',
                        isActive: res.isActive !== false,
                    };
                    // Email phases store their template + timing in the same
                    // row — mirror it into the email form for the Email tab.
                    if (this.isEmailChannel) {
                        const slot0 = Array.isArray(res.reminderSlots) ? res.reminderSlots[0] : null;
                        this.phaseEmailForm = {
                            emailTemplateId: res.templateId || null,
                            trigger: slot0?.trigger || 'PHASE_ACTIVATED',
                            delayValue: slot0?.delayValue ?? 0,
                            delayUnit: slot0?.delayUnit || 'MINUTES',
                            active: res.isActive !== false,
                        };
                    }
                } else {
                    // No config yet — leave the form in its default empty state.
                }
            },
            error: err => {
                this.loadingPhaseWaConfig = false;
                this.showError('Load Opening Config Failed', err);
            }
        });
    }

    savePhaseWaConfig(): void {
        if (!this.phaseConfigStage?.id) return;
        const f = this.phaseWaConfigForm;
        if (!f.channelConfigId) {
            this.showError('Validation', 'Channel config (WhatsApp sender / voice line) is required.');
            return;
        }

        this.savingPhaseWaConfig = true;
        this.whatsAppService.savePhaseWaConfig({
            phaseId: this.phaseConfigStage.id,
            templateId: f.templateId,
            channelConfigId: f.channelConfigId,
            variableMapping: f.variableMapping,
            reminderSlots: f.reminderSlots,
            headerImageUrl: f.headerImageUrl,
            triggerMode: f.triggerMode,
            triggerButtonText: f.triggerMode === 'ON_BUTTON' ? (f.triggerButtonText || null) : null,
            greetingText: f.greetingText || null,
            isActive: f.isActive,
        }).subscribe({
            next: (res: any) => {
                this.savingPhaseWaConfig = false;
                if (res && res.id) this.phaseWaConfigForm.id = res.id;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: 'Opening config saved'
                });
            },
            error: err => {
                this.savingPhaseWaConfig = false;
                this.showError('Save Failed', err);
            }
        });
    }

    /** Append a blank reminder slot to the Opening-tab form. */
    addReminderSlot(): void {
        const next = (this.phaseWaConfigForm.reminderSlots?.length || 0) + 1;
        this.phaseWaConfigForm.reminderSlots = [
            ...(this.phaseWaConfigForm.reminderSlots || []),
            {
                orderIndex: next,
                delayValue: 4,
                delayUnit: 'HOURS',
                tone: 'FRIENDLY',
                templateId: null,
                channelConfigId: null,      // derived from the slot's template
                variableMapping: {},
                headerImageUrl: '',
            }
        ];
    }

    /**
     * Reminder-slot template options — FOLLOWUP + REMINDER + ENTRY
     * templates (the kinds you'd nudge with), each labelled with the
     * sender phone, same reference as the opening picker.
     */
    get slotTemplateOptions(): any[] {
        return (this.availableTemplates || [])
            .filter(t => ['FOLLOWUP', 'REMINDER', 'ENTRY'].includes(t?.templateType as string)
                && (t?.status === 'APPROVED' || !t?.status))
            .map(t => ({ ...t, displayLabel: this.templateOptionLabel(t) }));
    }

    /**
     * When a slot's template changes: derive the slot's channel config
     * from that template (LeadWhatsAppConfig convention) and seed the
     * slot's variable mapping from the template body's {{N}} parameters
     * so the admin sees exactly which slots need binding.
     */
    onSlotTemplateChange(slot: any, templateId: string | null): void {
        slot.templateId = templateId;
        const t = (this.availableTemplates || []).find(x => x.id === templateId);
        slot.channelConfigId = t?.companyChannelConfigId ?? null;

        // Parse {{N}} from the template body and pre-create empty bindings.
        const body = this.templateBodyText(t);
        const matches = (body.match(/\{\{\d+\}\}/g) ?? []);
        const keys = Array.from(new Set(matches))
            .map(m => m.replace(/\D/g, ''))
            .sort((a, b) => Number(a) - Number(b));
        const existing = slot.variableMapping || {};
        const next: Record<string, string> = {};
        for (const k of keys) next[k] = existing[k] ?? '';
        slot.variableMapping = next;
    }

    /** Body text of a template (for {{N}} extraction + preview). */
    private templateBodyText(t: any): string {
        if (!t?.components) return '';
        const body = (t.components as any[]).find(c => c?.type === 'BODY');
        return body?.text ?? '';
    }

    /**
     * Harden a slot loaded from the server so the editor renders cleanly:
     *  - guarantee variableMapping is an object (never undefined → no crash
     *    on slot.variableMapping[k]),
     *  - re-derive channelConfigId from the slot's template when missing
     *    (slots saved before the per-slot sender was introduced),
     *  - re-seed any unbound {{N}} parameters from the template body while
     *    preserving the admin's existing bindings.
     * Safe to call before templates finish loading — it only enriches from
     * the template when one is found.
     */
    private normaliseLoadedSlot(slot: any): any {
        const s = { ...(slot || {}) };
        s.variableMapping = s.variableMapping || {};
        const t = (this.availableTemplates || []).find(x => x.id === s.templateId);
        if (t) {
            if (!s.channelConfigId) s.channelConfigId = t.companyChannelConfigId ?? null;
            const body = this.templateBodyText(t);
            const keys = Array.from(new Set(body.match(/\{\{\d+\}\}/g) ?? []))
                .map(m => m.replace(/\D/g, ''))
                .sort((a, b) => Number(a) - Number(b));
            const merged: Record<string, string> = {};
            for (const k of keys) merged[k] = s.variableMapping[k] ?? '';
            // Keep any pre-existing bindings whose key wasn't re-detected too.
            s.variableMapping = { ...s.variableMapping, ...merged };
        }
        return s;
    }

    /** Sorted variable keys for a slot's mapping editor. */
    slotVarKeys(slot: any): string[] {
        return Object.keys(slot?.variableMapping || {})
            .sort((a, b) => Number(a) - Number(b));
    }

    setSlotVarBinding(slot: any, key: string, value: string): void {
        slot.variableMapping = { ...(slot.variableMapping || {}), [key]: value };
    }

    /** Phone label for a slot's derived channel config. */
    slotPhone(slot: any): string {
        return this.channelPhoneFor(slot?.channelConfigId);
    }

    removeReminderSlot(index: number): void {
        const arr = this.phaseWaConfigForm.reminderSlots || [];
        arr.splice(index, 1);
        // Re-index orderIndex 1..N so the server gets canonical ordering.
        arr.forEach((s, i) => s.orderIndex = i + 1);
        this.phaseWaConfigForm.reminderSlots = [...arr];
    }

    moveReminderSlot(index: number, direction: -1 | 1): void {
        const arr = this.phaseWaConfigForm.reminderSlots || [];
        const target = index + direction;
        if (target < 0 || target >= arr.length) return;
        const tmp = arr[index];
        arr[index] = arr[target];
        arr[target] = tmp;
        arr.forEach((s, i) => s.orderIndex = i + 1);
        this.phaseWaConfigForm.reminderSlots = [...arr];
    }

    // ── Phase statuses (V019) ────────────────────────────────

    private loadPhaseStatuses(phaseId: string): void {
        this.loadingPhaseStatuses = true;
        this.whatsAppService.getPhaseStatuses(phaseId).subscribe({
            next: (res: any) => {
                this.loadingPhaseStatuses = false;
                this.phaseStatuses = Array.isArray(res) ? res : (res?.data ?? []);
            },
            error: err => {
                this.loadingPhaseStatuses = false;
                this.showError('Load Statuses Failed', err);
            }
        });
    }

    /** Reset the inline status form for a fresh add. */
    resetPhaseStatusForm(): void {
        this.phaseStatusForm = {
            id: null,
            name: '',
            color: '#3B82F6',
            displayOrder: (this.phaseStatuses?.length || 0) + 1,
            isDefault: false,
        };
    }

    /** Populate the inline form to edit an existing status. */
    editPhaseStatus(status: any): void {
        this.phaseStatusForm = {
            id: status.id,
            name: status.name,
            color: status.color || '#3B82F6',
            displayOrder: status.displayOrder ?? 1,
            isDefault: !!status.isDefault,
        };
    }

    submitPhaseStatusForm(): void {
        if (!this.phaseConfigStage?.id) return;
        const f = this.phaseStatusForm;
        if (!f.name || !f.name.trim()) {
            this.showError('Validation', 'Stage name is required.');
            return;
        }

        this.savingPhaseStatus = true;

        // Edit mode → PATCH; create mode → POST.
        //
        // The behaviour fields (isTerminal / winProbability / agentOutcome / caseOutcome) are
        // deliberately ABSENT from both payloads. The PATCH is partial — omitting a field leaves
        // the stored value untouched — so editing a stage's name here can never silently wipe the
        // outcomes an admin configured in the journey. New stages start with no behaviour at all,
        // which is the safe default: an unconfigured stage moves nothing and closes no deal.
        const op$ = f.id
            ? this.whatsAppService.updatePhaseStatus(f.id, {
                name: f.name.trim(),
                color: f.color,
                displayOrder: f.displayOrder,
                isDefault: f.isDefault,
            })
            : this.whatsAppService.createPhaseStatus({
                phaseId: this.phaseConfigStage.id,
                name: f.name.trim(),
                color: f.color,
                displayOrder: f.displayOrder,
                isDefault: f.isDefault,
            });

        op$.subscribe({
            next: () => {
                this.savingPhaseStatus = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: f.id ? 'Stage updated' : 'Stage added'
                });
                this.resetPhaseStatusForm();
                if (this.phaseConfigStage?.id) this.loadPhaseStatuses(this.phaseConfigStage.id);
            },
            error: err => {
                this.savingPhaseStatus = false;
                this.showError('Save Failed', err);
            }
        });
    }

    deletePhaseStatus(status: any): void {
        if (!status?.id) return;
        if (!confirm(`Delete stage "${status.name}"? Cases already on this stage will fall back to the phase default.`)) return;
        this.deletingPhaseStatusId = status.id;
        this.whatsAppService.deletePhaseStatus(status.id).subscribe({
            next: () => {
                this.deletingPhaseStatusId = null;
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Status removed' });
                if (this.phaseConfigStage?.id) this.loadPhaseStatuses(this.phaseConfigStage.id);
            },
            error: err => {
                this.deletingPhaseStatusId = null;
                this.showError('Delete Failed', err);
            }
        });
    }

    /** Toggle isDefault inline (a single PATCH). Backend auto-demotes others. */
    togglePhaseStatusDefault(status: any): void {
        const next = !status.isDefault;
        this.whatsAppService.updatePhaseStatus(status.id, { isDefault: next }).subscribe({
            next: () => {
                if (this.phaseConfigStage?.id) this.loadPhaseStatuses(this.phaseConfigStage.id);
            },
            error: err => this.showError('Update Failed', err)
        });
    }

    // ── Audit (V020) ─────────────────────────────────────────

    private loadPhaseAudit(phaseId: string): void {
        this.loadingPhaseAudit = true;
        this.whatsAppService.getParentAuditLog(phaseId).subscribe({
            next: (res: any) => {
                this.loadingPhaseAudit = false;
                const rows = Array.isArray(res) ? res : (res?.data ?? []);
                // Pre-format each diff once so the template renders structured
                // rows instead of calling a formatter per change-detection pass.
                this.phaseAuditLog = (rows || []).map((e: any) => ({
                    ...e,
                    displayChanges: this.auditChanges(e?.fieldChanges)
                }));
            },
            error: err => {
                this.loadingPhaseAudit = false;
                this.showError('Load Audit Failed', err);
            }
        });
    }

    /**
     * Turn a JSONB diff into structured, renderable change rows for the
     * audit feed. Each row carries a short (truncated) value for display
     * and the full value for a hover title — so objects/arrays read as
     * real JSON instead of the old "[object Object]".
     */
    auditChanges(diff: any): {
        field: string; oldShort: string; oldFull: string; newShort: string; newFull: string;
    }[] {
        if (!diff || typeof diff !== 'object') return [];
        const out: any[] = [];
        for (const key of Object.keys(diff)) {
            const entry = diff[key];
            if (!entry) continue;
            const oldFull = this.formatAuditValue(entry.old);
            const newFull = this.formatAuditValue(entry.new);
            out.push({
                field: this.humaniseFieldKey(key),
                oldFull, oldShort: this.truncateValue(oldFull, 80),
                newFull, newShort: this.truncateValue(newFull, 80),
            });
        }
        return out;
    }

    /** Format a single audit value: ∅ for empty, compact JSON for objects. */
    private formatAuditValue(v: any): string {
        if (v === null || v === undefined || v === '') return '∅';
        if (typeof v === 'object') {
            try { return JSON.stringify(v); } catch { return String(v); }
        }
        return String(v);
    }

    private truncateValue(s: string, n: number): string {
        return s.length > n ? s.slice(0, n - 1) + '…' : s;
    }

    /** camelCase / snake_case field key → "Title Case" label. */
    private humaniseFieldKey(key: string): string {
        return String(key)
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/[_-]+/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase())
            .trim();
    }

    /**
     * Legacy single-line summary, kept for any caller still using it.
     * The audit timeline now renders structured rows via auditChanges().
     */
    summariseFieldChanges(diff: any): string {
        return this.auditChanges(diff)
            .map(c => `${c.field}: ${c.oldShort} → ${c.newShort}`)
            .join(' · ');
    }

    // ── Lookup helpers ───────────────────────────────────────

    /**
     * V024 — re-trigger SYSTEM_FIELD master data load if the picker
     * needs it. The list is normally hydrated at component init (see
     * the constructor's loadMasterData('SYSTEM_FIELD') call), so this
     * usually returns immediately. Only fires a fresh load when the
     * cache is empty AND a load isn't already in flight.
     */
    ensureSystemFieldsLoaded(): void {
        // Guard on a dedicated flag, NOT systemFieldOptions.length — the list is
        // pre-seeded with hardcoded fallback rows, so a length check would always
        // skip and the picker would keep showing those instead of the tenant's
        // real System Fields (Settings → Master Data → System Field).
        if (this.systemFieldsLoaded || this.loadingSystemFields) return;
        this.loadingSystemFields = true;
        this.masterDataService.list('SYSTEM_FIELD', true).subscribe({
            next: (rows: any) => {
                this.loadingSystemFields = false;
                this.systemFieldsLoaded = true;
                // Mirror the shape loadMasterData() produces — {label, value}
                // — so the entry-phase picker behaves identically whether
                // it's hydrated by us or by the central loader.
                const list = Array.isArray(rows) ? rows : (rows?.data ?? []);
                this.systemFieldOptions = (list || [])
                    .filter((r: any) => r?.isActive !== false)
                    .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                    .map((r: any) => ({ label: r.label, value: r.code }));
            },
            error: () => { this.loadingSystemFields = false; }
        });
    }

    /**
     * Called when the admin flips the Entry phase toggle ON — load
     * SYSTEM_FIELD options if we don't have them yet.
     */
    onEntryPhaseToggle(): void {
        if (this.phaseGeneralForm.isEntryPhase) {
            this.ensureSystemFieldsLoaded();
        }
    }

    /**
     * Called when the admin switches the intake pattern radio. When
     * leaving "lead_pushed" we clear the configured metadata so the
     * persisted array reflects the new intent (customer-initiated phases
     * carry no contract). When entering "lead_pushed" we make sure the
     * SYSTEM_FIELD options are loaded so the picker is responsive.
     */
    onIntakePatternChange(): void {
        if (this.phaseGeneralForm.intakePattern === 'lead_pushed') {
            this.ensureSystemFieldsLoaded();
        } else {
            // Switching to customer_initiated → drop the previously-set
            // contract. The save call will persist an empty array which
            // the validator treats as soft-pass.
            this.phaseGeneralForm.entryMetadataFields = [];
        }
    }

    /**
     * Add a SYSTEM_FIELD row to the entry-phase metadata expectations.
     * No-op if the field is already in the list (prevents accidental
     * duplicates from rapid clicks).
     */
    addEntryMetadataField(fieldCode: string): void {
        if (!fieldCode) return;
        const existing = this.phaseGeneralForm.entryMetadataFields.find(f => f.fieldCode === fieldCode);
        if (existing) return;
        this.phaseGeneralForm.entryMetadataFields = [
            ...this.phaseGeneralForm.entryMetadataFields,
            { fieldCode, required: false }
        ];
    }

    removeEntryMetadataField(fieldCode: string): void {
        this.phaseGeneralForm.entryMetadataFields = this.phaseGeneralForm.entryMetadataFields
            .filter(f => f.fieldCode !== fieldCode);
    }

    toggleEntryMetadataRequired(fieldCode: string): void {
        this.phaseGeneralForm.entryMetadataFields = this.phaseGeneralForm.entryMetadataFields
            .map(f => f.fieldCode === fieldCode ? { ...f, required: !f.required } : f);
    }

    /** Lookup helper — humanise a fieldCode by finding its label.
        systemFieldOptions stores {label, value} pairs where value is the
        master_data code (e.g. "name", "phone"). entryMetadataFields uses
        fieldCode keyed to the same code, so we match on value. */
    fieldLabelFor(fieldCode: string): string {
        const m = (this.systemFieldOptions || []).find((o: any) => o.value === fieldCode);
        return m?.label ?? fieldCode;
    }

    /** Filter for the "add" dropdown — only show fields not yet added. */
    get availableSystemFieldOptions(): any[] {
        const taken = new Set(this.phaseGeneralForm.entryMetadataFields.map(f => f.fieldCode));
        return (this.systemFieldOptions || []).filter((o: any) => !taken.has(o.value));
    }

    private ensureDepartmentsLoaded(): void {
        if (this.availableDepartments?.length || this.loadingMapDepartments) return;
        this.loadingMapDepartments = true;
        this.whatsAppService.getTeams().subscribe({
            next: (res: any) => {
                this.loadingMapDepartments = false;
                this.availableDepartments = res?.data ?? res ?? [];
            },
            error: () => { this.loadingMapDepartments = false; }
        });
    }

    /**
     * V025 — load the company's WhatsApp templates once per Phase Config
     * dialog session. The dialog's General + Opening tabs both pick from
     * this list, filtered client-side by templateType.
     */
    private ensureTemplatesLoaded(): void {
        if (this.availableTemplates?.length || this.loadingTemplatesForPhase) return;
        this.loadingTemplatesForPhase = true;
        this.waTemplateService.getAll().subscribe({
            next: (res: any) => {
                this.loadingTemplatesForPhase = false;
                this.availableTemplates = res?.data ?? res?.body ?? res ?? [];
            },
            error: () => { this.loadingTemplatesForPhase = false; }
        });
    }

    // ============================================================
    //  Channel-aware Phase Config. The General-tab channel decides
    //  which config tab is offered: WhatsApp → Opening (+ phone
    //  preview), Voice → Voice config, Email → Email config.
    // ============================================================
    get isWhatsAppChannel(): boolean {
        const c = this.phaseGeneralForm.channels || [];
        return c.includes('whatsapp') || c.includes('sms');
    }
    get isVoiceChannel(): boolean { return (this.phaseGeneralForm.channels || []).includes('voice'); }
    get isEmailChannel(): boolean { return (this.phaseGeneralForm.channels || []).includes('email'); }

    /**
     * Channel switched on the General tab → if the currently-open config
     * tab no longer applies, fall back to General so we never show a blank
     * pane. Also warm the data the new channel's tab will need.
     */
    onPhaseChannelChange(): void {
        // Keep the single primary channel (engine + DB CHECK) in sync with the multi-select.
        this.phaseGeneralForm.channel = (this.phaseGeneralForm.channels?.[0] as any) || 'whatsapp';
        const t = this.phaseConfigActiveTab;
        const stale =
            (t === 'opening' && !this.isWhatsAppChannel) ||
            (t === 'voice'   && !this.isVoiceChannel) ||
            (t === 'email'   && !this.isEmailChannel);
        if (stale) this.phaseConfigActiveTab = 'general';
        if (this.isEmailChannel) this.ensureEmailTemplatesLoaded();
        if (this.isVoiceChannel || this.isWhatsAppChannel) this.ensureChannelConfigsLoaded();
    }

    // ── WhatsApp opening preview (mirrors the lead-wa-config phone mockup) ──
    get pcOpeningTemplate(): any {
        const id = this.phaseWaConfigForm.templateId;
        if (!id) return null;
        return (this.availableTemplates || []).find(t => t.id === id) ?? null;
    }
    private pcComponent(type: string): any {
        return (this.pcOpeningTemplate?.components as any[] || []).find(c => c?.type === type) ?? null;
    }
    get pcTemplateBody(): string { return this.pcComponent('BODY')?.text ?? ''; }
    get pcTemplateFooter(): string { return this.pcComponent('FOOTER')?.text ?? ''; }
    get pcTemplateHeaderText(): string {
        const h = this.pcComponent('HEADER');
        return h?.format === 'TEXT' ? (h.text ?? '') : '';
    }
    get pcHasImageHeader(): boolean {
        const h = this.pcComponent('HEADER');
        return !!h && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(h.format);
    }

    /**
     * Header-media source options drawn from the lead intake. Each entry is a
     * `{{lead.<code>}}` token; the engine's resolver expands it per-lead at send
     * time (reading the value from the lead's custom_fields), so a phase opening
     * can pull its image straight from an intake URL field instead of a static
     * link. Empty unless this is a lead-pushed entry phase with declared fields.
     */
    get pcImageSourceOptions(): { label: string; value: string }[] {
        const isEntry = this.phaseGeneralForm.isEntryPhase;
        const isLead = this.phaseGeneralForm.intakePattern === 'lead_pushed';
        if (!isEntry || !isLead) return [];
        return (this.phaseGeneralForm.entryMetadataFields || []).map(f => ({
            label: `Intake: ${this.fieldLabelFor(f.fieldCode)}`,
            value: `{{lead.${f.fieldCode}}}`,
        }));
    }

    /** The intake token currently used as the header image, or null when the
        field holds a static URL (so the picker shows a selection only when one
        of its options is in effect). */
    get pcSelectedImageIntake(): string | null {
        const v = this.phaseWaConfigForm.headerImageUrl || '';
        return this.pcImageSourceOptions.some(o => o.value === v) ? v : null;
    }
    get pcTemplateButtons(): any[] { return this.pcComponent('BUTTONS')?.buttons ?? []; }
    /** Body with {{N}} replaced by the bound data path, shown in brackets. */
    get pcPreviewBody(): string {
        let body = this.pcTemplateBody;
        const map = this.phaseWaConfigForm.variableMapping || {};
        for (const key of Object.keys(map)) {
            const val = (map as any)[key];
            if (!val) continue;
            body = body.split(`{{${key}}}`).join(`[${val}]`);
        }
        return body;
    }

    // ── Voice config — reuses phase-wa-config; channelConfig = voice line ──
    get voiceChannelConfigOptions(): any[] {
        return (this.channelConfigs || [])
            .filter((c: any) => (c?.channel || '').toUpperCase() === 'VOICE')
            .map((c: any) => ({ ...c, displayLabel: c.phoneNumber || c.senderIdentifier || c.displayName || c.id }));
    }

    // ── Email config — pick a real email template + send timing ──
    private ensureEmailTemplatesLoaded(): void {
        if (this.emailTemplates?.length || this.loadingEmailTemplates) return;
        this.loadingEmailTemplates = true;
        this.emailTemplateStore.getAll().subscribe({
            next: (rows: any) => {
                this.loadingEmailTemplates = false;
                const list = Array.isArray(rows) ? rows : (rows?.data ?? []);
                this.emailTemplates = (list || []).filter((t: any) => t?.status !== 'ARCHIVED');
            },
            error: () => { this.loadingEmailTemplates = false; }
        });
    }
    get emailTemplateOptions(): any[] {
        return (this.emailTemplates || []).map((t: any) => ({
            ...t,
            displayLabel: t.subject ? `${t.name} — ${t.subject}` : t.name
        }));
    }
    get selectedEmailTemplate(): any {
        const id = this.phaseEmailForm.emailTemplateId;
        if (!id) return null;
        return (this.emailTemplates || []).find((t: any) => t.id === id) ?? null;
    }
    readonly emailTriggerOptions = [
        { label: 'When phase activates', value: 'PHASE_ACTIVATED' },
        { label: 'When phase completes', value: 'PHASE_COMPLETED' },
    ];
    readonly emailDelayUnitOptions = [
        { label: 'minutes', value: 'MINUTES' },
        { label: 'hours',   value: 'HOURS' },
        { label: 'days',    value: 'DAYS' },
    ];

    /**
     * Persist the Email config via the shared phase config row:
     *   templateId       → chosen email template id
     *   reminderSlots[0] → { trigger, delayValue, delayUnit } send timing
     *   channelConfigId  → null (email has no WhatsApp/voice line)
     */
    savePhaseEmailConfig(): void {
        if (!this.phaseConfigStage?.id) return;
        if (!this.phaseEmailForm.emailTemplateId) {
            this.showError('Validation', 'Pick an email template first.');
            return;
        }
        this.savingPhaseWaConfig = true;
        this.whatsAppService.savePhaseWaConfig({
            phaseId: this.phaseConfigStage.id,
            templateId: this.phaseEmailForm.emailTemplateId,
            channelConfigId: null,
            variableMapping: {},
            reminderSlots: [{
                orderIndex: 1,
                trigger: this.phaseEmailForm.trigger,
                delayValue: this.phaseEmailForm.delayValue,
                delayUnit: this.phaseEmailForm.delayUnit,
            }],
            headerImageUrl: '',
            isActive: this.phaseEmailForm.active,
        }).subscribe({
            next: (res: any) => {
                this.savingPhaseWaConfig = false;
                if (res && res.id) this.phaseWaConfigForm.id = res.id;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Email config saved' });
            },
            error: err => { this.savingPhaseWaConfig = false; this.showError('Save Failed', err); }
        });
    }

    /** Load the email-sequence catalogue + resolve which one is currently bound to this phase. */
    private loadPhaseEmailSequences(phaseId: string): void {
        this.loadingEmailSequences = true;
        this.emailSequenceService.list().subscribe({
            next: (rows: any[]) => {
                const list = rows || [];
                this.emailSequenceOptions = list.map((s: any) => {
                    const n = s.steps?.length || 0;
                    return { label: n ? `${s.name} (${n} email${n === 1 ? '' : 's'})` : s.name, value: s.id };
                });
                const bound = list.find((s: any) => s.phaseId === phaseId);
                this.phaseEmailSequenceId = bound ? bound.id : null;
                this.loadingEmailSequences = false;
            },
            error: () => { this.loadingEmailSequences = false; },
        });
    }

    /** Persist the phase → sequence binding immediately when the dropdown changes. */
    onPhaseEmailSequenceChange(): void {
        if (!this.phaseConfigStage?.id) return;
        this.emailSequenceService.bind(this.phaseConfigStage.id, this.phaseEmailSequenceId).subscribe({
            next: () => this.messageService.add({
                severity: 'success', summary: 'Saved',
                detail: this.phaseEmailSequenceId ? 'Email sequence attached to this phase' : 'Email sequence detached',
            }),
            error: (err: any) => this.showError('Save Failed', err),
        });
    }

    /**
     * Resolve a channel config's phone number for display. Mirrors how
     * LeadWhatsAppConfig surfaces the sender — the channel config (phone)
     * is implied by the template, never entered separately. Uses the
     * already-loaded `channelConfigs` array from the WhatsApp tab.
     */
    channelPhoneFor(channelConfigId: string | null | undefined): string {
        if (!channelConfigId) return '';
        const c = (this.channelConfigs || []).find((x: any) => x.id === channelConfigId);
        return c?.phoneNumber ?? c?.senderIdentifier ?? '';
    }

    /** Builds "templateName — +91 98xxx" for a template option. */
    private templateOptionLabel(t: WaTemplate): string {
        const phone = this.channelPhoneFor(t.companyChannelConfigId);
        const base = t.metaTemplateName || t.name || '(unnamed)';
        return phone ? `${base} — ${phone}` : base;
    }

    /**
     * NOTICE templates as picker options carrying a displayLabel that
     * includes the sender number — same reference style as the
     * LeadWhatsAppConfig template picker.
     */
    get noticeTemplateOptions(): any[] {
        return (this.availableTemplates || [])
            .filter(t => t?.templateType === 'NOTICE' && (t?.status === 'APPROVED' || !t?.status))
            .map(t => ({ ...t, displayLabel: this.templateOptionLabel(t) }));
    }

    /** ENTRY templates as picker options with the number in the label. */
    get openingTemplateOptions(): any[] {
        return (this.availableTemplates || [])
            .filter(t => t?.templateType === 'ENTRY' && (t?.status === 'APPROVED' || !t?.status))
            .map(t => ({ ...t, displayLabel: this.templateOptionLabel(t) }));
    }

    /**
     * Look up a template's human label by id — used after reload to
     * render a selected value as a name instead of a bare UUID.
     */
    templateLabelFor(templateId: string | null | undefined): string {
        if (!templateId) return '';
        const t = (this.availableTemplates || []).find(x => x.id === templateId);
        return t ? this.templateOptionLabel(t) : (templateId as string);
    }

    /**
     * When the admin picks the Opening template, derive the channel
     * config (sender phone) from that template — the LeadWhatsAppConfig
     * convention. The admin never enters a channel config UUID; it's an
     * intrinsic property of the chosen template.
     */
    onOpeningTemplateChange(templateId: string | null): void {
        const t = (this.availableTemplates || []).find(x => x.id === templateId);
        this.phaseWaConfigForm.channelConfigId = t?.companyChannelConfigId ?? null;
    }

    /** Display phone for the currently-selected opening template. */
    get openingTemplatePhone(): string {
        return this.channelPhoneFor(this.phaseWaConfigForm.channelConfigId);
    }

    /* ============================================================
       V025 — Opening tab variable binding editor
       Maps WhatsApp template body parameters ({{1}}, {{2}}, …) to
       a data source path the runtime resolves at send time.
       For entry phases, the source dropdown is biased toward the
       fields the admin declared on the Entry phase metadata picker
       (so {{1}} → name, {{2}} → phone is one click each).
       For non-entry phases, the dropdown shows the wider menu —
       lead.*, case.*, workflow.*, system.*.
       ============================================================ */

    /** Sorted list of binding keys for the editor — '1','2','3' etc. */
    get variableBindingKeys(): string[] {
        const map = this.phaseWaConfigForm.variableMapping || {};
        return Object.keys(map)
            .filter(k => k !== null && k !== undefined)
            .sort((a, b) => {
                const na = parseInt(a, 10);
                const nb = parseInt(b, 10);
                if (!isNaN(na) && !isNaN(nb)) return na - nb;
                return a.localeCompare(b);
            });
    }

    /** Add a new {{N}} binding row, defaulting to the next integer key. */
    addVariableBinding(): void {
        const existing = this.variableBindingKeys
            .map(k => parseInt(k, 10))
            .filter(n => !isNaN(n));
        const next = (existing.length ? Math.max(...existing) : 0) + 1;
        this.phaseWaConfigForm.variableMapping = {
            ...(this.phaseWaConfigForm.variableMapping || {}),
            [String(next)]: ''
        };
    }

    removeVariableBinding(key: string): void {
        const copy = { ...(this.phaseWaConfigForm.variableMapping || {}) };
        delete copy[key];
        this.phaseWaConfigForm.variableMapping = copy;
    }

    /** Two-way binding helper — sets `map[key] = value` immutably. */
    setVariableBinding(key: string, value: string): void {
        this.phaseWaConfigForm.variableMapping = {
            ...(this.phaseWaConfigForm.variableMapping || {}),
            [key]: value
        };
    }

    /**
     * Produces the rendered {{N}} token string for the variable binding
     * editor. Building it in code avoids the HTML-entity vs Angular-
     * interpolation collision: literal `{{` in the template source is
     * always parsed as the start of an Angular expression, and HTML
     * entities like `&#123;&#123;` get decoded BEFORE the template
     * compiler runs — so neither approach renders a literal `{{N}}`
     * cleanly inside a binding context.
     */
    tokenFor(key: string): string {
        return '{{' + key + '}}';
    }

    /**
     * Source paths offered in the binding dropdown. Mode-aware:
     *
     * - When the phase is an entry phase configured as 'lead_pushed',
     *   the entry-metadata fields come first (so {{1}} → name is one
     *   click). System variables append at the bottom for things like
     *   the current date and company name.
     *
     * - When the phase is anything else (sub-phase opening, manual
     *   reminder, follow-up phase), the dropdown lists the wider menu
     *   of paths the runtime resolves: lead.*, case.*, workflow.*,
     *   system.*. Admins can still type a custom path the resolver
     *   knows about — the value field stays editable.
     */
    get variableSourceOptions(): { label: string; value: string; group: string }[] {
        const opts: { label: string; value: string; group: string }[] = [];
        const isEntry = this.phaseGeneralForm.isEntryPhase;
        const isLead  = this.phaseGeneralForm.intakePattern === 'lead_pushed';

        if (isEntry && isLead) {
            // Suggest the exact fields the admin declared as expected.
            //
            // The PATH is what gets inserted and what the resolver matches, so it has to stay
            // visible verbatim — an admin copying "Course Interest" into a template would get a
            // placeholder that never resolves. But a bare tenant code
            // (lead.do_you_want_to_take_free_demo_class) does not say which question it is, so
            // the readable name is appended after it. `value` is untouched: what lands in the
            // message is still the bare path.
            for (const f of (this.phaseGeneralForm.entryMetadataFields || [])) {
                const path = `lead.${f.fieldCode}`;
                const human = this.fieldLabels.labelFor(f.fieldCode);
                // Skip the suffix when it is character-identical to the code — no point
                // rendering "lead.city — city".
                const suffix = human && human !== f.fieldCode ? ` — ${human}` : '';
                opts.push({
                    label: `${path}${f.required ? ' *' : ''}${suffix}`,
                    value: path,
                    group: 'Lead intake fields'
                });
            }
        }

        // Common lead-level fields the runtime can always resolve when
        // the case is bound to a lead row. Useful for non-entry phases
        // too — e.g. a KYC phase template wants the customer's name.
        for (const code of ['name', 'phone', 'email', 'address', 'city']) {
            const exists = opts.some(o => o.value === `lead.${code}`);
            if (!exists) {
                opts.push({ label: `lead.${code}`, value: `lead.${code}`, group: 'Lead fields' });
            }
        }

        // Case-level fields — captured during the journey, surfaced to
        // any later phase via the case row + case_data JSONB.
        opts.push({ label: 'case.id',              value: 'case.id',              group: 'Case fields' });
        opts.push({ label: 'case.currentStatus',   value: 'case.currentStatus',   group: 'Case fields' });
        opts.push({ label: 'case.assignedOwner',   value: 'case.assignedOwner',   group: 'Case fields' });

        // Source — where the lead came from. Useful for entry-phase
        // greetings: "Hi {{name}}, you reached out via {{source.campaign}}."
        opts.push({ label: 'source.name',     value: 'source.name',     group: 'Source / attribution' });
        opts.push({ label: 'source.campaign', value: 'source.campaign', group: 'Source / attribution' });

        // System — resolved at send time from the company + clock.
        opts.push({ label: 'system.companyName', value: 'system.companyName', group: 'System' });
        opts.push({ label: 'system.today',       value: 'system.today',       group: 'System' });
        opts.push({ label: 'system.now',         value: 'system.now',         group: 'System' });

        return opts;
    }

    private ensureChannelConfigsLoaded(): void {
        // If the WhatsApp tab already populated channelConfigs, reuse it
        // for phone resolution — no second fetch.
        if (this.channelConfigs?.length) {
            this.availableChannelConfigs = this.channelConfigs;
            return;
        }
        if (this.availableChannelConfigs?.length || this.loadingChannelConfigsForPhase) return;
        this.loadingChannelConfigsForPhase = true;
        // The configs endpoint needs a real channelAccountId (UUID), so we
        // can't blind-call it. Fetch the accounts first, then fork-join
        // their configs — same path the WhatsApp tab uses. This lets the
        // sender phone resolve even when the dialog opens before that tab.
        this.whatsAppService.getChannelAccounts().subscribe({
            next: (accounts: any[]) => {
                const list = accounts || [];
                if (!list.length) {
                    this.loadingChannelConfigsForPhase = false;
                    return;
                }
                const calls = list.map((a: any) =>
                    this.whatsAppService.getChannelConfigs(a.id).pipe(catchError(() => of([])))
                );
                forkJoin(calls).subscribe({
                    next: (allConfigs: any[]) => {
                        this.loadingChannelConfigsForPhase = false;
                        const merged = (allConfigs || []).flat();
                        this.availableChannelConfigs = merged;
                        // Mirror into channelConfigs so channelPhoneFor()
                        // resolves regardless of which tab loaded first.
                        if (!this.channelConfigs?.length) this.channelConfigs = merged;
                    },
                    error: () => { this.loadingChannelConfigsForPhase = false; }
                });
            },
            error: () => { this.loadingChannelConfigsForPhase = false; }
        });
    }

    /** Quick name lookup for badges. */
    departmentName(id: string | null | undefined): string {
        if (!id) return '';
        const d = (this.availableDepartments || []).find((x: any) => x.id === id);
        return d?.name ?? '';
    }

    /** Quick label for the channel chip on each phase card. */
    channelLabel(channel: string | null | undefined): string {
        switch (channel) {
            case 'whatsapp': return 'WhatsApp';
            case 'sms':      return 'SMS';
            case 'voice':    return 'Voice';
            case 'email':    return 'Email';
            case 'web':      return 'Web';
            default:         return channel || 'WhatsApp';
        }
    }

    openMapWorkflowDialog(stage: any): void {
        if (!stage?.id) return;
        this.mappingStageId = stage.id;
        this.mappingStageName = stage.name ?? '';
        this.mappingSelectedWorkflowId = stage.aiWorkflowId ?? null;
        this.showMapWorkflowDialog = true;

        // Lazy-load the workflow list the first time the dialog is opened.
        if (!this.availableWorkflows?.length && !this.loadingMapWorkflows) {
            this.loadingMapWorkflows = true;
            this.workflowService.getCompanyWorkflows().subscribe({
                next: (res: any) => {
                    this.availableWorkflows = res?.data ?? res ?? [];
                    this.loadingMapWorkflows = false;
                },
                error: err => {
                    this.loadingMapWorkflows = false;
                    this.showError('Could not load workflows', err);
                }
            });
        }
    }

    saveMapWorkflow(): void {
        if (!this.mappingStageId) return;
        this.savingMapWorkflow = true;

        const newWorkflowId = this.mappingSelectedWorkflowId ?? null;

        this.whatsAppService.updateStatus(this.mappingStageId, {
            aiWorkflowId: newWorkflowId
        }).subscribe({
            next: (res: any) => {
                this.savingMapWorkflow = false;
                const updated = res?.data ?? res;
                // Patch the in-memory list so the chip updates without a reload.
                const idx = this.statuses.findIndex(s => s.id === this.mappingStageId);
                if (idx >= 0) {
                    this.statuses[idx] = { ...this.statuses[idx], aiWorkflowId: updated?.aiWorkflowId ?? newWorkflowId };
                    this.statuses = [...this.statuses];   // trigger change detection
                }
                this.showMapWorkflowDialog = false;
                this.messageService.add({
                    severity: 'success',
                    summary: newWorkflowId ? 'Workflow mapped' : 'Workflow unmapped',
                    detail: newWorkflowId
                        ? `Workflow attached to ${this.mappingStageName}`
                        : `Removed workflow mapping from ${this.mappingStageName}`
                });
            },
            error: err => {
                this.savingMapWorkflow = false;
                this.showError('Save Failed', err);
            }
        });
    }

    clearMappedWorkflow(): void {
        this.mappingSelectedWorkflowId = null;
    }

    // ────────────────────────────────────────────────────────────
    // Map-department dialog — owning team per stage
    // ────────────────────────────────────────────────────────────

    /** Label for the chip on the stage row when a department (or restricted teams) is set. */
    mappedDepartmentLabel(stage: any): string | null {
        if (stage?.restricted) {
            const n = Array.isArray(stage.departmentIds) ? stage.departmentIds.length : 0;
            return n ? `Restricted · ${n} team${n === 1 ? '' : 's'}` : 'Restricted';
        }
        const id = stage?.departmentId;
        if (!id) return null;
        const match = (this.availableDepartments || [])
            .find((d: any) => d.id === id);
        const name = match?.name ?? 'Team attached';
        // Multi-team stage — show how many other departments share it.
        const extra = Array.isArray(stage.departmentIds)
            ? stage.departmentIds.filter((t: string) => t && t !== id).length : 0;
        return extra ? `${name} +${extra}` : name;
    }

    /** Co-owner options for the CONFIGURE PHASE dialog. Separate from
     *  coOwnerDepartmentOptions() below, which filters against the Map-department dialog's own
     *  primary — using that one here would exclude the wrong team. */
    phaseCoOwnerOptions(): any[] {
        const primary = this.phaseGeneralForm?.departmentId;
        return (this.availableDepartments || []).filter((d: any) => d?.id && d.id !== primary);
    }

    /** Teams offered as CO-OWNERS of a normal stage — every team except the primary owner
     *  (which is picked separately above, and is included automatically server-side). */
    coOwnerDepartmentOptions(): any[] {
        const primary = this.mappingSelectedDepartmentId;
        return (this.availableDepartments || []).filter((d: any) => d?.id && d.id !== primary);
    }

    hasMappedDepartment(stage: any): boolean {
        return !!stage?.departmentId || !!stage?.restricted;
    }

    openMapDepartmentDialog(stage: any): void {
        if (!stage?.id) return;
        this.mappingDeptStageId = stage.id;
        this.mappingDeptStageName = stage.name ?? '';
        this.mappingSelectedDepartmentId = stage.departmentId ?? null;
        this.mappingRestricted = !!stage.restricted;
        this.mappingSelectedDepartmentIds = Array.isArray(stage.departmentIds) ? [...stage.departmentIds] : [];
        this.showMapDepartmentDialog = true;

        // Lazy-load the teams list the first time the dialog opens.
        if (!this.availableDepartments?.length && !this.loadingMapDepartments) {
            this.loadingMapDepartments = true;
            this.whatsAppService.getTeams().subscribe({
                next: (res: any) => {
                    this.availableDepartments = res?.data ?? res ?? [];
                    this.loadingMapDepartments = false;
                },
                error: err => {
                    this.loadingMapDepartments = false;
                    this.showError('Could not load teams', err);
                }
            });
        }
    }

    saveMapDepartment(): void {
        if (!this.mappingDeptStageId) return;
        this.savingMapDepartment = true;

        const restricted = this.mappingRestricted;
        const newDeptId = this.mappingSelectedDepartmentId ?? null;
        // Restricted stage → the restricted-team list. Normal stage → the primary owner PLUS any
        // co-owning teams (multi-team stage); clearing the primary clears the co-owners too, since
        // a stage can't have co-owners without an owner.
        const coOwners = (this.mappingSelectedDepartmentIds ?? []).filter(t => t && t !== newDeptId);
        const deptIds = restricted ? (this.mappingSelectedDepartmentIds ?? [])
                                   : (newDeptId ? coOwners : []);
        // Normal stages set their owning department in Configure phase → General; this dialog
        // now only flips the RESTRICTED flag and, for a restricted stage, its partner-team list.
        // Sending departmentId/departmentIds for a normal stage here would fight that screen and
        // silently clear the co-owners it just saved.
        const patch: any = restricted
            ? { restricted: true, departmentIds: deptIds }
            : { restricted: false };

        this.whatsAppService.updateStatus(this.mappingDeptStageId, patch).subscribe({
            next: (res: any) => {
                this.savingMapDepartment = false;
                const updated = res?.data ?? res;
                // Patch the in-memory row so the chip updates instantly.
                const idx = this.statuses.findIndex(s => s.id === this.mappingDeptStageId);
                if (idx >= 0) {
                    this.statuses[idx] = {
                        ...this.statuses[idx],
                        restricted: updated?.restricted ?? restricted,
                        departmentIds: updated?.departmentIds ?? deptIds,
                        departmentId: restricted ? this.statuses[idx].departmentId : (updated?.departmentId ?? newDeptId)
                    };
                    this.statuses = [...this.statuses];   // trigger change detection
                }
                this.showMapDepartmentDialog = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Stage teams saved',
                    detail: restricted
                        ? `${deptIds.length} restricted team${deptIds.length === 1 ? '' : 's'} attached to ${this.mappingDeptStageName} — no auto-assignment`
                        : (newDeptId ? `Team attached to ${this.mappingDeptStageName}` : `Removed team mapping from ${this.mappingDeptStageName}`)
                });
            },
            error: err => {
                this.savingMapDepartment = false;
                this.showError('Save Failed', err);
            }
        });
    }

    // ────────────────────────────────────────────────────────────
    // Delete journey stage / sub-stage
    // ────────────────────────────────────────────────────────────

    /**
     * Confirms with the operator and calls DELETE /api/statuses/{id}.
     * Backend enforces the safety rules — sub-stages, active cases,
     * pipeline mapping, or transitions referencing this stage all
     * block the delete and the server returns 409 with a clear
     * reason. We surface that reason verbatim in the error toast.
     */
    deleteJourneyStage(stage: any, kind: 'stage' | 'substage'): void {
        if (!stage?.id) return;

        const label = kind === 'stage' ? 'journey stage' : 'sub-stage';
        const warn = kind === 'stage'
            ? `Delete journey stage "${stage.name}"?\n\nAny SOPs attached will be soft-deleted. This cannot be undone.`
            : `Delete sub-stage "${stage.name}"?\n\nAny SOPs attached will be soft-deleted. This cannot be undone.`;

        if (!confirm(warn)) return;

        this.whatsAppService.deleteStatus(stage.id).subscribe({
            next: (res: any) => {
                // Strip the deleted row from the in-memory list so the
                // tree updates without a full reload.
                this.statuses = this.statuses.filter(s => s.id !== stage.id);
                // Drop its cached SOPs too (small, but tidy).
                delete this.sopsByOwner[stage.id];
                // Collapse if it was open.
                this.expandedStages.delete(stage.id);
                this.expandedSubstages.delete(stage.id);

                this.messageService.add({
                    severity: 'success',
                    summary: `Deleted ${label}`,
                    detail: res?.message ?? `"${stage.name}" removed`
                });
            },
            error: err => {
                // Backend returns 409 with a human-readable reason
                // when delete is blocked — surface it directly.
                const detail =
                    err?.error?.message       // backend ApiResponse.error message
                    ?? err?.error?.error      // alt shape some clients use
                    ?? err?.message
                    ?? 'Could not delete the stage. Try again or contact support.';
                this.showError('Cannot delete', { error: { message: detail } });
            }
        });
    }

    // ── Team ─────────────────────────────────────────────────

    loadTeams(): void {
        this.loadingTeams = true;
        this.whatsAppService.getTeams().subscribe({
            next: (res: any) => {
                this.loadingTeams = false;
                this.teams = res?.data ?? res ?? [];
            },
            error: err => {
                this.loadingTeams = false;
                this.showError('Load Failed', err);
            }
        });
    }

    createTeam(): void {
        const name = (this.teamForm.name || '').trim();
        if (!name) {
            this.showError('Validation', 'Team name is required.');
            return;
        }
        this.savingTeam = true;
        this.whatsAppService.createTeam({ name }).subscribe({
            next: (res: any) => {
                this.savingTeam = false;
                this.teamForm = { name: '' };
                this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message || 'Team created' });
                this.loadTeams();
            },
            error: err => {
                this.savingTeam = false;
                this.showError('Create Failed', err);
            }
        });
    }

    /** Persist a team's attendance check-in toggles (Enable check-in / Managers can check in). */
    // ── Edit team (name / description) ──────────────────────────────────────
    // The table already edits the three toggles inline; name and description had no editor at
    // all, so fixing a typo meant DELETING the team — which also takes its members, groups and
    // phase mappings with it. The PATCH endpoint already accepted both fields.
    showEditTeam = false;
    editTeamRow: any = null;
    editTeamName = '';
    editTeamDesc = '';
    savingEditTeam = false;

    openEditTeam(t: any): void {
        this.editTeamRow = t;
        this.editTeamName = (t?.name || '').toString();
        this.editTeamDesc = (t?.description || '').toString();
        this.showEditTeam = true;
    }

    saveEditTeam(): void {
        const t = this.editTeamRow;
        const name = (this.editTeamName || '').trim();
        if (!t?.id || !name || this.savingEditTeam) return;
        this.savingEditTeam = true;
        this.whatsAppService.updateTeam(t.id, { name, description: (this.editTeamDesc || '').trim() }).subscribe({
            next: () => {
                this.savingEditTeam = false;
                this.showEditTeam = false;
                // Patch the row in place so the table updates without a full reload.
                t.name = name;
                t.description = (this.editTeamDesc || '').trim();
                this.messageService.add({ severity: 'success', summary: 'Team updated', detail: name });
            },
            error: (err: any) => {
                this.savingEditTeam = false;
                this.loadTeams();   // reload discards the half-applied edit
                this.showError('Update failed', err);
            },
        });
    }

    // ── Rename a group (inline) ────────────────────────────────────────────
    editingGroupId: string | null = null;
    editingGroupName = '';
    savingGroupRename = false;

    startGroupRename(g: any): void {
        this.editingGroupId = g?.id ?? null;
        this.editingGroupName = (g?.name || '').toString();
    }

    cancelGroupRename(): void {
        this.editingGroupId = null;
        this.editingGroupName = '';
    }

    saveGroupRename(g: any): void {
        const name = (this.editingGroupName || '').trim();
        if (!g?.id || !name || this.savingGroupRename) return;
        if (name === g.name) { this.cancelGroupRename(); return; }   // nothing to save
        this.savingGroupRename = true;
        this.whatsAppService.renameTeamGroup(g.id, name).subscribe({
            next: () => {
                this.savingGroupRename = false;
                g.name = name;             // patch in place; membership is untouched
                this.cancelGroupRename();
                this.messageService.add({ severity: 'success', summary: 'Group renamed', detail: name });
            },
            error: (err: any) => {
                this.savingGroupRename = false;
                this.messageService.add({ severity: 'error', summary: 'Failed',
                    detail: err?.error?.message || err?.message || 'Could not rename group' });
            },
        });
    }

    saveTeamAttendance(t: any): void {
        this.whatsAppService.updateTeam(t.id, {
            attendanceEnabled: !!t.attendanceEnabled,
            managerCheckInEnabled: !!t.managerCheckInEnabled,
        }).subscribe({
            next: () => this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Attendance settings updated for ' + (t.name || 'team') + '.' }),
            error: (err: any) => { this.loadTeams(); this.showError('Update failed', err); },   // reload reverts the optimistic toggle
        });
    }

    /** Persist a team's Restricted (business-partner / distributor) toggle. When on, this
     *  team's members work cases fully but can't assign, and advancing leaves the next phase
     *  unassigned. */
    saveTeamRestricted(t: any): void {
        this.whatsAppService.updateTeam(t.id, { restrictedMode: !!t.restrictedMode }).subscribe({
            next: () => this.messageService.add({ severity: 'success', summary: 'Saved',
                detail: (t.restrictedMode ? 'Restricted mode ON — ' : 'Restricted mode OFF — ')
                    + (t.name || 'team') + (t.restrictedMode ? ' can no longer assign leads.' : ' can assign leads again.') }),
            error: (err: any) => { this.loadTeams(); this.showError('Update failed', err); },   // reload reverts the optimistic toggle
        });
    }

    /** What to call an employee in a picker. Accounts invited by email often have no name yet,
     *  so fall back through email → phone → employee code rather than rendering a blank row. */
    employeeLabel(emp: any): string {
        if (!emp) return '';
        return (emp.name || '').trim()
            || (emp.fullName || '').trim()
            || (emp.email || '').trim()
            || (emp.phone || '').trim()
            || (emp.employeeCode || '').trim()
            || 'Unnamed user';
    }

    /** Second line under the name — ONLY when it adds something. Someone with no name is already
     *  labelled by their email, and printing it again underneath just showed it twice. */
    employeeSubLabel(emp: any): string {
        if (!emp) return '';
        const label = this.employeeLabel(emp);
        const email = (emp.email || '').trim();
        const phone = (emp.phone || '').trim();
        if (email && email !== label) return email;
        if (phone && phone !== label) return phone;
        return '';
    }

    /** Employees not already in the team being added to — powers the Add-Member dropdown so the
     *  same person can't be picked twice (frontend guard; the backend also rejects duplicates). */
    get availableEmployees(): any[] {
        const taken = new Set((this.teamMembers || []).map((m: any) => m?.userId));
        return (this.employees || []).filter((e: any) => !taken.has(e?.id));
    }

    openAddMember(team: any): void {
        this.selectedTeamForMember = team;
        // V016 — reset isManager too so the toggle starts off for
        // every new dialog open, regardless of the previous state.
        this.memberForm = { teamId: team.id, selectedEmployees: [], isManager: false };
        this.showAddMemberDialog = true;
        this.loadEmployees();
        // Load current members so the dropdown can hide anyone already in the team (dedup guard).
        this.whatsAppService.getTeamMembers(team.id).subscribe({
            next: (r: any) => { this.teamMembers = r?.data ?? r ?? []; },
            error: () => {}
        });
    }

    loadEmployees(): void {
        if (this.employees.length) return; // already loaded
        this.loadingEmployees = true;
        this.whatsAppService.getCompanyEmployees().subscribe({
            next: (res: any) => {
                this.loadingEmployees = false;
                this.employees = res?.data ?? res ?? [];
            },
            error: err => {
                this.loadingEmployees = false;
                this.showError('Load Employees Failed', err);
            }
        });
    }

    addEmployee(): void {
        if (this.userInviteForm.invalid) { this.userInviteForm.markAllAsTouched(); return; }
        // Use getRawValue so the disabled email field is included on edit (form.value skips disabled controls).
        const value = this.userInviteForm.getRawValue();
        // Access template is saved via its own endpoint; admins never carry one.
        const desiredTemplate: string | null = this.editingUserIsAdmin ? null : (value.permissionTemplateId ?? null);

        // ── Edit existing user ──
        if (this.editingUserId) {
            const editingId = this.editingUserId;
            const existing = (this.employees || []).find(e => e.id === editingId);
            const currentTemplate: string | null = existing?.permissionTemplateId ?? null;
            const templateChanged = desiredTemplate !== currentTemplate;

            this.savingUser = true;
            this.teamService.updateEmployee(editingId, {
                name: value.name,
                phone: value.phone,
                roles: value.roles,
                employeeCode: value.employeeCode,
                departmentIds: value.departmentIds || [],
            }).subscribe({
                next: () => {
                    // Update succeeded — now chain the template assignment if it changed.
                    // Failure here should NOT roll back the user update; surface a distinct warning.
                    const done = () => {
                        this.savingUser = false;
                        this.showAddUserDialog = false;
                        this.editingUserId = null;
                        this.employees = [];
                        this.loadEmployees();
                        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Member updated successfully' });
                    };
                    if (!templateChanged) { done(); return; }
                    this.permissionTemplateService.assignUser(editingId, desiredTemplate).subscribe({
                        next: () => done(),
                        error: (err: any) => {
                            this.savingUser = false;
                            this.showAddUserDialog = false;
                            this.editingUserId = null;
                            this.employees = [];
                            this.loadEmployees();
                            this.messageService.add({ severity: 'warn', summary: 'Partly saved',
                                detail: 'Member details saved, but the access template could not be updated: ' + (err?.message || 'unknown error') });
                        }
                    });
                },
                error: (err: any) => {
                    this.savingUser = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to update member' });
                }
            });
            return;
        }

        // ── Add new user ──
        if (!this.subscriptionService.canAddUser()) {
            this.messageService.add({ severity: 'warn', summary: 'Limit Reached', detail: 'User limit reached for your current plan.' });
            return;
        }
        this.savingUser = true;
        const newEmail = String(value.email || '').trim().toLowerCase();
        this.teamService.addEmployee(value).subscribe({
            next: () => {
                this.subscriptionService.fetchSubscription().subscribe({ error: () => {} });
                // Add response doesn't return the new user's id — reload, match on email,
                // then apply the chosen access template (skipped for admins / "Role default").
                const finish = () => {
                    this.savingUser = false;
                    this.showAddUserDialog = false;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Member added successfully' });
                };
                if (!desiredTemplate) { this.employees = []; this.loadEmployees(); finish(); return; }
                this.teamService.getEmployees().subscribe({
                    next: (rows: any) => {
                        const list = (rows?.data ?? rows) as any[] | undefined;
                        this.employees = list || [];
                        const created = (list || []).find(u => String(u.email || '').toLowerCase() === newEmail);
                        if (!created || this.isAdminUser(created)) { finish(); return; }
                        this.permissionTemplateService.assignUser(created.id, desiredTemplate).subscribe({
                            next: () => { this.employees = []; this.loadEmployees(); finish(); },
                            error: () => {
                                finish();
                                this.messageService.add({ severity: 'warn', summary: 'Template not applied',
                                    detail: 'The user was added but the access template could not be attached — set it from Edit.' });
                            }
                        });
                    },
                    error: () => { this.employees = []; this.loadEmployees(); finish(); }
                });
            },
            error: (err: any) => {
                this.savingUser = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to add member' });
            }
        });
    }

    // ── Bulk import users from CSV ──────────────────────────────

    /** Download a ready-to-fill CSV template with the expected columns + sample rows. */
    downloadUserTemplate(): void {
        const rows = [
            'name,email,phone,roles,password,employeeCode,department',
            'John Doe,john.doe@example.com,9876543210,AGENT,,,Sales',
            'Jane Smith,jane.smith@example.com,9123456780,MANAGER|AGENT,,,Noida',
        ];
        const csv = rows.join('\r\n') + '\r\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'users-template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /** File-input change handler: upload the chosen CSV, then clear the input so the same file can be re-picked. */
    onUserCsvSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files[0];
        if (file) {
            this.uploadUserCsv(file);
        }
        input.value = '';
    }

    /** Send the CSV to the backend and surface the per-row result summary. */
    uploadUserCsv(file: File): void {
        this.uploadingUsers = true;
        this.teamService.bulkUploadEmployees(file).subscribe({
            next: (res: any) => {
                this.uploadingUsers = false;
                const data = res?.data ?? res ?? {};
                this.userImportResult = {
                    total: data.total ?? 0,
                    created: data.created ?? 0,
                    failed: data.failed ?? 0,
                    errors: data.errors ?? [],
                    warnings: data.warnings ?? [],
                };
                this.showUserImportResult = true;
                // Refresh the users list and the plan usage counter.
                this.employees = [];
                this.loadEmployees();
                this.subscriptionService.fetchSubscription().subscribe({ error: () => {} });

                if (this.userImportResult.created > 0) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Import complete',
                        detail: `${this.userImportResult.created} user(s) added${this.userImportResult.failed ? `, ${this.userImportResult.failed} failed` : ''}.`
                    });
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Nothing imported',
                        detail: this.userImportResult.failed
                            ? `${this.userImportResult.failed} row(s) failed — see details.`
                            : 'No data rows were found in the file.'
                    });
                }
            },
            error: (err: any) => {
                this.uploadingUsers = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Import failed',
                    detail: err?.message || 'Could not import users from the CSV.'
                });
            }
        });
    }

    /**
     * Add EVERY picked employee to the department in one go.
     *
     * <p>Each person is still a separate call — the backend takes one member at a time — but they
     * are fired together and reported once. A failure on one person doesn't abandon the rest:
     * whoever went in is kept and the toast names how many didn't, so a half-finished team is
     * never silently presented as complete.</p>
     */
    addTeamMember(): void {
        const picked = (this.memberForm.selectedEmployees || []).filter(e => e && e.id);
        if (!picked.length) {
            this.showError('Validation', 'Please select at least one employee.');
            return;
        }
        const teamId = this.memberForm.teamId;
        const asManager = !!this.memberForm.isManager;
        this.savingMember = true;
        // V016 — forward the manager flag from the form. Even when false we send it explicitly so
        // the backend's "key present → set value" branch is unambiguous.
        // catchError per request so one rejection can't cancel the others mid-flight.
        forkJoin(
            picked.map(emp =>
                this.whatsAppService.addTeamMember(teamId, emp.id, asManager).pipe(
                    map(() => ({ ok: true, name: this.employeeLabel(emp) })),
                    catchError(() => of({ ok: false, name: this.employeeLabel(emp) })),
                )
            )
        ).subscribe((results: any[]) => {
            this.savingMember = false;
            const added = results.filter(r => r.ok);
            const failed = results.filter(r => !r.ok);
            if (added.length) {
                this.showAddMemberDialog = false;
                this.memberForm = { teamId: '', selectedEmployees: [], isManager: false };
            }
            if (added.length && !failed.length) {
                this.messageService.add({
                    severity: 'success', summary: 'Success',
                    detail: added.length === 1
                        ? `${added[0].name} added to the department`
                        : `${added.length} members added to the department`,
                });
            } else if (added.length && failed.length) {
                // Partial success — say exactly who to retry rather than a vague failure.
                this.messageService.add({
                    severity: 'warn', summary: `${added.length} added, ${failed.length} failed`,
                    detail: `Couldn't add: ${failed.map(f => f.name).join(', ')}. Try those again.`,
                    life: 8000,
                });
            } else {
                this.showError('Add Members Failed', 'None of the selected people could be added.');
            }
            // Refresh the members list so the new people show immediately, WITH their names (the
            // /members endpoint returns them) — and so the picker hides them next time it opens.
            if (teamId && added.length) {
                this.whatsAppService.getTeamMembers(teamId).subscribe({
                    next: (r: any) => { this.teamMembers = r?.data ?? r ?? []; },
                    error: () => {}
                });
            }
        });
    }

    /**
     * V016 — flips the isManager flag on a single team_members row
     * without re-opening the Add dialog. Optimistically updates the
     * local row so the UI feels instant, rolls back on error. Toast
     * on success and failure per the team's CRUD convention.
     */
    toggleMemberManager(member: any): void {
        if (!member || !member.id || !this.viewingTeam?.id) return;
        if (this.togglingManagerId === member.id) return;

        const before = !!member.isManager;
        const after = !before;

        // Optimistic — flip the local row immediately.
        member.isManager = after;
        this.togglingManagerId = member.id;

        this.whatsAppService.updateTeamMember(
            this.viewingTeam.id,
            member.id,
            { isManager: after }
        ).subscribe({
            next: (res: any) => {
                this.togglingManagerId = null;
                // Trust the server's view of the row in case the
                // backend normalised the value (e.g. tri-state UI).
                if (res && typeof res.isManager === 'boolean') {
                    member.isManager = res.isManager;
                }
                this.messageService.add({
                    severity: 'success',
                    summary: 'Updated',
                    detail: after
                        ? `${this.getMemberName(member.userId)} is now a manager`
                        : `${this.getMemberName(member.userId)} is no longer a manager`
                });
            },
            error: err => {
                // Roll back the optimistic flip.
                member.isManager = before;
                this.togglingManagerId = null;
                this.showError('Update Failed', err);
            }
        });
    }

    openMapStatus(team: any): void {
        this.selectedTeamForStatus = team;
        this.selectedStatusId = null;
        this.originalStatusId = null;
        this.showMapStatusDialog = true;
        // load current mapping to pre-select
        this.whatsAppService.getTeamStatuses(team.id).subscribe({
            next: (res: any) => {
                const mapped: any[] = res?.data ?? res ?? [];
                const first = mapped[0]?.statusId ?? null;
                this.selectedStatusId = first;
                this.originalStatusId = first;
            },
            error: () => {}
        });
    }

    saveTeamStatuses(): void {
        if (!this.selectedTeamForStatus) return;
        this.savingTeamStatuses = true;
        const ids = this.selectedStatusId ? [this.selectedStatusId] : [];
        this.whatsAppService.mapTeamStatuses(this.selectedTeamForStatus.id, ids).subscribe({
            next: (res: any) => {
                this.savingTeamStatuses = false;
                this.showMapStatusDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message || 'Statuses mapped to team' });
            },
            error: err => {
                this.savingTeamStatuses = false;
                this.showError('Map Failed', err);
            }
        });
    }

    openViewTeamStatuses(team: any): void {
        this.viewingTeamForStatus = team;
        this.teamStatusMappings = [];
        this.showTeamStatusesDialog = true;
        this.loadingTeamStatuses = true;
        const statusLoad$ = this.statuses.length > 0
            ? of(this.statuses)
            : this.whatsAppService.getStatuses();
        statusLoad$.subscribe({
            next: (res: any) => {
                const allStatuses: any[] = Array.isArray(res) ? res : (res?.data ?? res ?? []);
                if (this.statuses.length === 0) this.statuses = allStatuses;
                this.whatsAppService.getTeamStatuses(team.id).subscribe({
                    next: (r: any) => {
                        this.loadingTeamStatuses = false;
                        const raw: any[] = r?.data ?? r ?? [];
                        // enrich each mapping with the resolved stage name from statuses
                        this.teamStatusMappings = raw.map((m: any) => {
                            const sid = m.statusId || m.id;
                            const stage = allStatuses.find((st: any) => st.id === sid || st.statusId === sid);
                            return { ...m, resolvedName: stage?.name ?? sid };
                        });
                    },
                    error: err => {
                        this.loadingTeamStatuses = false;
                        this.showError('Load Failed', err);
                    }
                });
            },
            error: () => {
                this.whatsAppService.getTeamStatuses(team.id).subscribe({
                    next: (r: any) => {
                        this.loadingTeamStatuses = false;
                        this.teamStatusMappings = r?.data ?? r ?? [];
                    },
                    error: err => {
                        this.loadingTeamStatuses = false;
                        this.showError('Load Failed', err);
                    }
                });
            }
        });
    }

    // ── SOP ──────────────────────────────────────────────

    openSopDialog(stage: any): void {
        this.selectedStageForSop = stage;
        this.sopTasks = [];
        this.sopForm = { label: '', orderNo: 1, isMandatory: false };
        this.showSopDialog = true;
        this.loadSopTasks();
    }

    loadSopTasks(): void {
        if (!this.selectedStageForSop) return;
        this.loadingSop = true;
        this.sopService.getTasksByStatus(this.selectedStageForSop.id).subscribe({
            next: (res: any) => {
                this.loadingSop = false;
                this.sopTasks = (res?.data ?? res ?? []).sort((a: any, b: any) => a.orderNo - b.orderNo);
                this.sopForm.orderNo = this.sopTasks.length + 1;
            },
            error: err => {
                this.loadingSop = false;
                this.showError('Load SOP Failed', err);
            }
        });
    }

    createSopTask(): void {
        const label = (this.sopForm.label || '').trim();
        if (!label) { this.showError('Validation', 'Task label is required.'); return; }
        this.savingSop = true;
        this.sopService.createTask({
            statusId: this.selectedStageForSop.id,
            label,
            orderNo: this.sopForm.orderNo,
            isMandatory: this.sopForm.isMandatory
        }).subscribe({
            next: (res: any) => {
                this.savingSop = false;
                this.sopForm = { label: '', orderNo: this.sopTasks.length + 2, isMandatory: false };
                this.messageService.add({ severity: 'success', summary: 'Created', detail: res?.message || 'SOP task added' });
                this.loadSopTasks();
            },
            error: err => {
                this.savingSop = false;
                this.showError('Create SOP Failed', err);
            }
        });
    }

    deleteSopTask(task: any): void {
        this.deletingSopId = task.id;
        this.sopService.deleteTask(task.id).subscribe({
            next: (res: any) => {
                this.deletingSopId = null;
                this.sopTasks = this.sopTasks.filter(t => t.id !== task.id);
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: res?.message || 'SOP task removed' });
            },
            error: err => {
                this.deletingSopId = null;
                this.showError('Delete SOP Failed', err);
            }
        });
    }

    onSopDrop(event: CdkDragDrop<any[]>): void {
        moveItemInArray(this.sopTasks, event.previousIndex, event.currentIndex);
        this.sopTasks = this.sopTasks.map((t, i) => ({ ...t, orderNo: i + 1 }));
    }

    getStatusName(statusId: string): string {
        const s = this.statuses.find((st: any) =>
            st.id === statusId || st.statusId === statusId || st._id === statusId
        );
        return s ? (s.name ?? s.statusName ?? statusId) : statusId;
    }

    /** Open the change-password dialog for a user. */
    openPasswordDialog(u: any): void {
        this.passwordUser = u;
        this.newPassword = '';
        this.resetPasswordVisible = false;
        this.showPasswordDialog = true;
    }

    /** Fill the Change-password dialog with a strong, readable password. Same recipe as
     *  the invite-dialog generator so admins get a consistent format. */
    generateResetPassword(): void {
        const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ', lower = 'abcdefghijkmnopqrstuvwxyz', digits = '23456789', symbols = '!@#$%&*?';
        const all = upper + lower + digits + symbols;
        const rand = (s: string) => s[Math.floor(Math.random() * s.length)];
        let pwd = rand(upper) + rand(lower) + rand(digits) + rand(symbols);
        while (pwd.length < 12) pwd += rand(all);
        this.newPassword = pwd.split('').sort(() => Math.random() - 0.5).join('');
        this.resetPasswordVisible = true; // reveal so the admin can see + share what was generated
    }

    /** Admin sets a new password for the selected user. */
    savePassword(): void {
        if (!this.passwordUser?.id || !this.newPassword || this.newPassword.length < 6) return;
        this.savingPassword = true;
        this.teamService.resetUserPassword(this.passwordUser.id, this.newPassword).subscribe({
            next: () => {
                this.savingPassword = false;
                this.showPasswordDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Password updated', detail: 'The new password is active.' });
            },
            error: (err) => { this.savingPassword = false; this.showError('Password Update Failed', err); }
        });
    }

    /** Remove (deactivate) a user from the company. */
    removeUser(u: any): void {
        if (!u?.id) return;
        if (!confirm(`Remove user "${u.name || u.email || 'this user'}"? They will be deactivated.`)) return;
        this.removingUserId = u.id;
        this.teamService.deleteEmployee(u.id).subscribe({
            next: () => {
                this.removingUserId = null;
                this.messageService.add({ severity: 'success', summary: 'Removed', detail: 'User removed.' });
                this.loadEmployees();
            },
            error: (err) => { this.removingUserId = null; this.showError('Remove Failed', err); }
        });
    }

    /** Delete a whole team (its members, groups and stage mappings are cleaned up). */
    deleteTeamConfirm(t: any): void {
        if (!t?.id) return;
        if (!confirm(`Delete team "${t.name}"? Its members and stage mappings will be removed. This cannot be undone.`)) return;
        this.deletingTeamId = t.id;
        this.teamService.deleteTeam(t.id).subscribe({
            next: () => {
                this.deletingTeamId = null;
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Team deleted.' });
                this.loadTeams();
            },
            error: (err) => { this.deletingTeamId = null; this.showError('Delete Failed', err); }
        });
    }

    /** Remove a single member from the currently-open team (does NOT delete the user). */
    removeMember(m: any): void {
        const teamId = this.viewingTeam?.id;
        if (!m?.id || !teamId) return;
        if (!confirm(`Remove "${m.name || this.getMemberName(m.userId) || 'this member'}" from the team?`)) return;
        this.removingMemberId = m.id;
        this.teamService.removeTeamMember(teamId, m.id).subscribe({
            next: () => {
                this.removingMemberId = null;
                this.teamMembers = this.teamMembers.filter((x: any) => x.id !== m.id);
                this.messageService.add({ severity: 'success', summary: 'Removed', detail: 'Member removed from team.' });
            },
            error: (err) => { this.removingMemberId = null; this.showError('Remove Failed', err); }
        });
    }

    openViewMembers(team: any): void {
        this.viewingTeam = team;
        this.showMembersDialog = true;
        this.teamMembers = [];
        this.teamGroups = [];
        this.newGroupName = '';
        // Reset dialog UI state so it opens fresh (last team's search / tab don't leak in).
        this.memberSearch = '';
        this.membersTab = 'people';
        this.loadTeamGroups(team.id);
        this.loadingTeamMembers = true;
        this.whatsAppService.getTeamMembers(team.id).subscribe({
            next: (res: any) => {
                this.loadingTeamMembers = false;
                this.teamMembers = res?.data ?? res ?? [];
            },
            error: err => {
                this.loadingTeamMembers = false;
                this.showError('Load Members Failed', err);
            }
        });
    }

    private loadTeamGroups(teamId: string): void {
        this.whatsAppService.getTeamGroups(teamId).subscribe({
            next: (res: any) => { this.teamGroups = res?.data ?? res ?? []; },
            error: () => { this.teamGroups = []; }
        });
    }

    /** Create a group within the team currently open in the Members dialog. */
    createGroup(): void {
        const name = (this.newGroupName || '').trim();
        if (!name || !this.viewingTeam?.id) return;
        this.savingGroup = true;
        this.whatsAppService.createTeamGroup(this.viewingTeam.id, name).subscribe({
            next: () => {
                this.savingGroup = false;
                this.newGroupName = '';
                this.loadTeamGroups(this.viewingTeam.id);
                this.messageService.add({ severity: 'success', summary: 'Group created', detail: name });
            },
            error: (err: any) => {
                this.savingGroup = false;
                this.messageService.add({ severity: 'error', summary: 'Failed', detail: err?.message || 'Could not create group' });
            }
        });
    }

    deleteGroup(g: any): void {
        if (!g?.id || !this.viewingTeam?.id) return;
        this.whatsAppService.deleteTeamGroup(g.id).subscribe({
            next: () => {
                this.loadTeamGroups(this.viewingTeam.id);
                this.teamMembers = this.teamMembers.map((m: any) => m.groupId === g.id ? { ...m, groupId: null } : m);
                this.messageService.add({ severity: 'success', summary: 'Group deleted', detail: g.name });
            },
            error: (err: any) => {
                this.messageService.add({ severity: 'error', summary: 'Failed', detail: err?.message || 'Could not delete group' });
            }
        });
    }

    /** Move a member into a group (or out, when groupId is null). Optimistic + toast. */
    setMemberGroup(m: any, groupId: string | null): void {
        if (!m?.id || !this.viewingTeam?.id) return;
        const prev = m.groupId ?? null;
        m.groupId = groupId;
        this.whatsAppService.setMemberGroup(this.viewingTeam.id, m.id, groupId).subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Member group updated' }); },
            error: (err: any) => {
                m.groupId = prev;
                this.messageService.add({ severity: 'error', summary: 'Failed', detail: err?.message || 'Could not update group' });
            }
        });
    }

    getMemberName(userId: string): string {
        const emp = this.employees.find((e: any) => e.id === userId);
        return emp ? (emp.name || emp.fullName || emp.email || userId) : userId;
    }

    confirmUpgrade(): void {
        const payload = {
            requestType: 'UPGRADE_REQUEST',
            companyId: this.companyProfile?.id || this.companyProfile?.companyId || '',
            name: this.companyProfile?.name || '',
            email: this.companyProfile?.email || '',
            message: 'Please upgrade our plan'
        };

        this.upgradingPlan = true;
        this.whatsAppService.submitPublicContact(payload).subscribe({
            next: (res: any) => {
                this.upgradingPlan = false;
                this.showUpgradeDialog = false;

                if (res?.success !== true) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Upgrade Request Failed',
                        detail: res?.message || 'Unable to submit upgrade request'
                    });
                    return;
                }

                this.messageService.add({
                    severity: 'success',
                    summary: 'Upgrade Requested',
                    detail: res?.message || 'Request submitted successfully',
                    life: 4000
                });
            },
            error: (err) => {
                this.upgradingPlan = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Upgrade Request Failed',
                    detail: err || 'Unable to submit upgrade request'
                });
            }
        });
    }

    // ── LEAD SOURCES METHODS ─────────────────────────────────────

    onSourceIdInput(value: string): void {
        this.sourceForm.source_id = value.toLowerCase().replace(/ /g, '_').replace(/[^a-z0-9_]/g, '');
    }

    get webhookUrlPreview(): string {
        const cid = localStorage.getItem('companyId') ?? '{companyId}';
        const src = this.pickedSourceType || '{source}';
        const sid = this.sourceForm.source_id || '{source_id}';
        return `${environment.eventApiBaseUrl}/lead/v1/intake/${cid}/${src}/${sid}`;
    }

    /** Meta connection fields still missing. On an EDIT the Page token is already stored (and
     *  never returned), so a blank box means "keep it" — only flag it when creating a new source
     *  or when nothing is stored yet. The Page id is always shown, so it's always required. */
    get metaMissingFields(): string[] {
        if (this.pickedSourceType !== 'meta_ads') return [];
        const stored = !!this.editingSource && this.sourceForm.meta_configured;
        const missing: string[] = [];
        if (!this.sourceForm.meta_page_id?.trim()) missing.push('Facebook Page ID');
        if (!stored && !this.sourceForm.meta_page_access_token?.trim()) missing.push('Page Access Token');
        return missing;
    }

    /** Dropdown options for form-field mapping: system fields plus an explicit "don't map". */
    get metaFormMappingOptions(): { label: string; value: string }[] {
        return [{ label: "Don't map — keep as captured data", value: '' }, ...this.systemFieldOptions];
    }

    /** Lead-source names from Master Data (category SOURCE_NAME) — the picklist for the Add-Source
     *  "Lead Source" field. Editable, so an admin can still type a brand-new name on the spot. */
    get leadSourceNameOptions(): { label: string; value: string }[] {
        return (this.masterDataRows?.['SOURCE_NAME'] ?? [])
            .filter(r => r.label && r.label.trim())
            .map(r => ({ label: r.label, value: r.label }));
    }

    /** The Meta source whose forms are on screen — the panel's tab, or the edit dialog's source. */
    private get metaCtx(): { id: number } | null {
        return this.metaPanelSource ?? this.editingSource;
    }

    loadMetaForms(): void {
        const ctx = this.metaCtx;
        if (!ctx) return;
        this.metaFormsLoading = true;
        this.metaFormsError = '';
        const call = this.formProvider === 'google'
            ? this.sourceConfigService.listGoogleForms(ctx.id)
            : this.sourceConfigService.listMetaForms(ctx.id);
        call.subscribe({
            next: (forms) => { this.metaForms = forms; this.metaFormsLoading = false; },
            error: (err) => {
                this.metaFormsLoading = false;
                this.metaFormsError = err?.message ?? 'Could not load lead forms.';
            }
        });
        if (this.formProvider !== 'google') this.loadMetaAdCoverage();
    }

    // ── Shopify events (fixed 3 topics; enable + journey + mapping per event) ──
    /** Activity-type options for the "punch which activity" picker (loaded once). */
    private loadShopifyActivityOptions(): void {
        if (this.shopifyActivityOptions.length) return;
        this.operationsService.listActivityTypes().subscribe({
            next: (list: any) => {
                this.shopifyActivityOptions = (Array.isArray(list) ? list : [])
                    .map((a: any) => ({ label: a?.name ?? a?.label ?? '(activity)', value: String(a?.id ?? a?.value ?? '') }))
                    .filter((o: any) => o.value);
            },
            error: () => { this.shopifyActivityOptions = []; }
        });
    }

    loadShopifyEvents(): void {
        const src = this.editingSource;
        if (!src) return;
        this.loadShopifyActivityOptions();
        this.shopifyEventsLoading = true;
        this.shopifyEventsError = '';
        this.sourceConfigService.listShopifyEvents(src.id).subscribe({
            next: (events) => { this.shopifyEvents = events || []; this.shopifyEventsLoading = false; },
            error: (err) => {
                this.shopifyEventsLoading = false;
                this.shopifyEventsError = err?.message ?? 'Could not load Shopify events.';
            }
        });
    }

    /** CONNECT — auto-register this store's webhooks via Shopify's Admin API. Uses the saved store
     *  domain + access token; no manual webhook setup in Shopify. Shows a per-topic result. */
    connectShopify(): void {
        const src = this.editingSource;
        if (!src || this.shopifyRegistering) return;
        this.shopifyRegistering = true;
        this.shopifyRegisterResult = null;
        this.sourceConfigService.registerShopifyWebhooks(src.id).subscribe({
            next: (res) => {
                this.shopifyRegistering = false;
                this.shopifyRegisterResult = res;
                const all = res.registered === res.total;
                this.messageService.add({
                    severity: all ? 'success' : 'warn',
                    summary: all ? 'Shopify connected' : 'Partly connected',
                    detail: `${res.registered}/${res.total} webhooks registered`
                });
                this.loadShopifyEvents();
            },
            error: (err) => {
                this.shopifyRegistering = false;
                this.messageService.add({ severity: 'error', summary: 'Connect failed',
                    detail: err?.message || 'Could not register the webhooks. Check the store domain + token.' });
            }
        });
    }

    toggleShopifyEvent(ev: ShopifyEvent): void {
        const src = this.editingSource;
        if (!src || this.shopifyEventTogglingSlug) return;
        this.shopifyEventTogglingSlug = ev.slug;
        this.sourceConfigService.toggleShopifyEvent(src.id, ev.slug).subscribe({
            next: (updated) => {
                this.shopifyEventTogglingSlug = null;
                this.shopifyEvents = this.shopifyEvents.map(e => e.slug === ev.slug ? updated : e);
                if (this.shopifyEventOpen?.slug === ev.slug) this.shopifyEventOpen = updated;
            },
            error: () => {
                this.shopifyEventTogglingSlug = null;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not toggle the event.' });
            }
        });
    }

    /** Open/close the mapping + journey editor for one event (accordion, one at a time). */
    openShopifyEvent(ev: ShopifyEvent): void {
        if (this.shopifyEventOpen?.slug === ev.slug) { this.shopifyEventOpen = null; return; }
        this.shopifyEventOpen = ev;
        const fm = ev.fieldMapping || {};
        this.shopifyEventDraftRows = Object.keys(fm).length
            ? Object.keys(fm).map(k => ({ incoming: k, system: fm[k] }))
            : [{ incoming: '', system: '' }];
        this.shopifyEventDraftOperationId = ev.operationId || '';
        this.shopifyEventDraftSourceName = ev.sourceName || '';
        this.shopifyEventDraftActivityTypeId = ev.activityTypeId || null;
    }

    addShopifyMappingRow(): void { this.shopifyEventDraftRows.push({ incoming: '', system: '' }); }
    removeShopifyMappingRow(i: number): void {
        if (this.shopifyEventDraftRows.length > 1) this.shopifyEventDraftRows.splice(i, 1);
    }

    /** True when an event has at least one saved field mapping — drives the "Not mapped" badge. */
    shopifyEventMapped(ev: ShopifyEvent): boolean {
        return !!ev.fieldMapping && Object.keys(ev.fieldMapping).length > 0;
    }

    saveShopifyEvent(): void {
        const src = this.editingSource;
        const ev = this.shopifyEventOpen;
        if (!src || !ev) return;
        const mapping: Record<string, string> = {};
        this.shopifyEventDraftRows.filter(r => r.incoming && r.system).forEach(r => { mapping[r.incoming] = r.system; });
        this.shopifyEventSaving = true;
        this.sourceConfigService.saveShopifyEventMapping(
            src.id, ev.slug, mapping,
            this.shopifyEventDraftOperationId || null,
            this.shopifyEventDraftSourceName?.trim() || null,
            this.shopifyEventDraftActivityTypeId || null
        ).subscribe({
            next: (updated) => {
                this.shopifyEventSaving = false;
                this.shopifyEvents = this.shopifyEvents.map(e => e.slug === ev.slug ? updated : e);
                this.shopifyEventOpen = null;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Event mapping saved.' });
            },
            error: (err) => {
                this.shopifyEventSaving = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Could not save.' });
            }
        });
    }

    // ── Ad-attribution health ────────────────────────────────────────────────────────────
    // Campaign / ad set only arrive when the Page's token can read ads AND we're inside Meta's
    // per-app quota. When either fails, Meta returns the lead and omits those fields WITHOUT an
    // error — so a source can look perfectly healthy while every lead lands with no campaign.
    // This strip turns that silence into a number.
    metaAdCoverage: any = null;
    metaAdCoverageLoading = false;

    loadMetaAdCoverage(): void {
        const ctx = this.metaCtx;
        this.metaAdCoverage = null;
        if (!ctx) return;
        this.metaAdCoverageLoading = true;
        this.sourceConfigService.metaAdCoverage(ctx.id, 7).subscribe({
            next: (c) => { this.metaAdCoverage = c; this.metaAdCoverageLoading = false; },
            // Silent: this is a diagnostic, never a reason to redden the panel.
            error: () => { this.metaAdCoverageLoading = false; },
        });
        // How many leads are repairable — shown on the Backfill button so the admin knows the
        // scale before pressing. Dry run: no Meta calls, no writes.
        this.sourceConfigService.metaBackfillAttribution(ctx.id, true).subscribe({
            next: (r) => { this.metaBackfillPreview = r; },
            error: () => { this.metaBackfillPreview = null; },
        });
    }

    metaBackfillPreview: any = null;
    metaBackfillRunning = false;

    /**
     * Repair the leads that arrived without a campaign. Each distinct AD is resolved once and
     * reused for every lead it produced, so this costs a handful of API calls rather than one
     * per lead — which is what exhausted the quota originally. Idempotent: pressing twice can't
     * duplicate anything, and the run is capped so it can't drain the quota in one go.
     */
    runMetaBackfill(): void {
        const ctx = this.metaCtx;
        if (!ctx || this.metaBackfillRunning) return;
        this.metaBackfillRunning = true;
        this.sourceConfigService.metaBackfillAttribution(ctx.id, false).subscribe({
            next: (r) => {
                this.metaBackfillRunning = false;

                // The run is fire-and-forget now: the server answers 202 with {status, message}
                // and works in the background. Without this branch a perfectly healthy start would
                // read leadsUpdated as undefined and report "Nothing could be filled in".
                if (r?.status === 'started' || r?.status === 'already_running') {
                    this.messageService.add({
                        severity: r.status === 'started' ? 'success' : 'info',
                        summary: r.status === 'started' ? 'Backfill started' : 'Already running',
                        detail: r.message
                            || 'It runs in the background. Reload this page in about 5 minutes to see the result.',
                        life: 12000,
                    });
                    return;   // nothing to refresh yet — the strip would still read the old numbers
                }

                const leads = r?.leadsUpdated ?? 0;
                const unresolved = r?.adsUnresolved ?? 0;
                if (leads > 0) {
                    this.messageService.add({
                        severity: 'success', summary: `${leads} lead(s) updated`,
                        detail: `${r?.adsResolved ?? 0} ad(s) resolved`
                            + (r?.moreRemaining ? ' — more remain, press again to continue.' : ''),
                        life: 6000,
                    });
                } else {
                    // Report META'S OWN error, never a guess. This used to assert "the Page's token
                    // likely can't read ads" for every failure, which sent admins off arranging
                    // permissions when the real cause was a transient 502 — and on Koshish the ad
                    // account turned out to be shared but assigned to no system user.
                    this.messageService.add({
                        severity: 'warn', summary: 'Nothing could be filled in',
                        detail: unresolved > 0
                            ? `Meta returned no campaign for ${unresolved} ad(s).`
                              + (r?.sampleError ? ` Meta said: "${String(r.sampleError).slice(0, 160)}"` : '')
                            : 'No leads were waiting on a campaign.',
                        life: 8000,
                    });
                }
                this.loadMetaAdCoverage();   // refresh the strip so the effect is visible at once
            },
            error: (err) => {
                this.metaBackfillRunning = false;
                this.messageService.add({ severity: 'error', summary: 'Backfill failed',
                    detail: err?.message ?? 'Could not run the backfill.' });
            },
        });
    }

    openMetaForm(form: MetaForm): void {
        const ctx = this.metaCtx;
        if (!ctx) return;
        if (this.metaFormOpen?.form_id === form.form_id) { this.metaFormOpen = null; return; }
        this.metaFormLoadingId = form.form_id;
        const call = this.formProvider === 'google'
            ? this.sourceConfigService.getGoogleFormFields(ctx.id, form.form_id)
            : this.sourceConfigService.getMetaFormFields(ctx.id, form.form_id);
        call.subscribe({
            next: (detail) => {
                this.metaFormLoadingId = null;
                this.metaFormOpen = detail;
                this.metaFormOperationDraft = detail.operation_id ?? '';
                this.metaFormSourceNameDraft = detail.source_name ?? '';
                this.metaSourceNameOptions = (detail.source_name_options ?? []).map(n => ({ label: n, value: n }));
                this.metaFieldSearch = '';
                // Draft starts from what's saved; unsaved fields take the type-based
                // suggestion so a standard form is usually save-and-done.
                this.metaFormDraft = {};
                for (const f of detail.fields)
                    this.metaFormDraft[f.key] = f.mapped_to ?? f.suggested ?? '';
            },
            error: (err) => {
                this.metaFormLoadingId = null;
                this.messageService.add({ severity: 'error', summary: 'Error',
                    detail: err?.message ?? 'Could not load the form\'s fields.' });
            }
        });
    }

    saveMetaFormMapping(): void {
        const ctx = this.metaCtx;
        if (!ctx || !this.metaFormOpen) return;
        const mapping: Record<string, string> = {};
        for (const [key, target] of Object.entries(this.metaFormDraft))
            if (target) mapping[key] = target;

        if (!Object.values(mapping).some(t => t === 'phone' || t === 'email')) {
            this.messageService.add({ severity: 'warn', summary: 'Mapping incomplete',
                detail: 'Map at least one field to Phone or Email — leads need a way to be contacted.' });
            return;
        }

        this.metaFormSaving = true;
        const save = this.formProvider === 'google'
            ? this.sourceConfigService.saveGoogleFormMapping(ctx.id, this.metaFormOpen.form_id, mapping,
                    this.metaFormOperationDraft || null, this.metaFormSourceNameDraft?.trim() || null)
            : this.sourceConfigService.saveMetaFormMapping(ctx.id, this.metaFormOpen.form_id, mapping,
                    this.metaFormOperationDraft || null, this.metaFormSourceNameDraft?.trim() || null);
        save.subscribe({
                next: () => {
                    this.metaFormSaving = false;
                    this.messageService.add({ severity: 'success', summary: 'Form mapped',
                        detail: `Leads from "${this.metaFormOpen?.name}" now flow into the CRM.` });
                    this.metaFormOpen = null;
                    this.loadMetaForms();
                },
                error: (err) => {
                    this.metaFormSaving = false;
                    this.messageService.add({ severity: 'error', summary: 'Error',
                        detail: err?.message ?? 'Could not save the mapping.' });
                }
            });
    }

    get configChecklist(): { label: string; valid: boolean }[] {
        const id = this.sourceForm.source_id.trim();
        const idValid = id.length > 0 && /^[a-z0-9_]+$/.test(id);
        const fieldsValid = this.isRequiredField('phone') || this.isRequiredField('email');
        const mapped = this.fieldMappingRows.filter(r => r.incoming && r.system).map(r => r.system);
        const mappingValid = this.sourceForm.required_fields.length > 0 &&
            this.sourceForm.required_fields.every(f => mapped.includes(f));
        const googleValid = this.pickedSourceType !== 'google_ads' || !!this.sourceForm.google_key.trim();
        const workflowValid = !!this.sourceForm.operationId || !!this.sourceForm.workflowName.trim();
        return [
            { label: 'Source ID valid',          valid: idValid        },
            { label: 'Required fields selected',  valid: fieldsValid    },
            { label: 'Field mapping complete',    valid: mappingValid   },
            { label: 'Operation selected',         valid: workflowValid  },
            ...(this.pickedSourceType === 'google_ads' ? [{ label: 'Google Key provided', valid: googleValid }] : []),
            ...(this.pickedSourceType === 'meta_ads'
                ? [{ label: 'Meta credentials provided', valid: this.metaMissingFields.length === 0 }] : []),
            ...(this.pickedSourceType === 'indiamart'
                ? [{ label: 'IndiaMART CRM key provided', valid: this.indiamartKeyValid }] : []),
            ...(this.pickedSourceType === 'shopify'
                ? [{ label: 'Shopify credentials provided', valid: this.shopifyMissingFields.length === 0 }] : [])
        ];
    }

    /** On an EDIT the stored key is never returned, so blank means "keep it" —
     *  only demand a key when creating or when nothing is stored yet. */
    get indiamartKeyValid(): boolean {
        if (this.pickedSourceType !== 'indiamart') return true;
        const stored = !!this.editingSource && this.sourceForm.indiamart_configured;
        return stored || !!this.sourceForm.indiamart_crm_key?.trim();
    }

    /** On CREATE all three Shopify credentials are required; on EDIT the token + secret are never
     *  returned, so blank means "keep stored" — only the domain is always demanded. */
    get shopifyMissingFields(): string[] {
        if (this.pickedSourceType !== 'shopify') return [];
        const stored = !!this.editingSource && this.sourceForm.shopify_configured;
        const missing: string[] = [];
        if (!this.sourceForm.shopify_store_domain?.trim()) missing.push('Store domain');
        if (!stored && !this.sourceForm.shopify_access_token?.trim()) missing.push('Admin API access token');
        if (!stored && !this.sourceForm.shopify_webhook_secret?.trim()) missing.push('Webhook signing secret');
        return missing;
    }

    get isSourceFormReady(): boolean {
        return this.configChecklist.every(c => c.valid);
    }

    getMappingStatus(field: string): 'mapped' | 'missing' {
        const mapped = this.fieldMappingRows.filter(r => r.incoming && r.system).map(r => r.system);
        return mapped.includes(field) ? 'mapped' : 'missing';
    }

    private getDefaultMappingRows(): { incoming: string; system: string }[] {
        const defaults: Record<string, { incoming: string; system: string }[]> = {
            meta_ads:       [{ incoming: 'full_name', system: 'name' }, { incoming: 'phone_number', system: 'phone' }, { incoming: 'email_address', system: 'email' }],
            google_ads:     [{ incoming: 'full_name', system: 'name' }, { incoming: 'phone_number', system: 'phone' }, { incoming: 'email_address', system: 'email' }],
            website:        [{ incoming: 'full_name', system: 'name' }, { incoming: 'phone', system: 'phone' }, { incoming: 'email', system: 'email' }],
            whatsapp:       [{ incoming: 'name', system: 'name' }, { incoming: 'phone', system: 'phone' }],
            manual:         [{ incoming: 'name', system: 'name' }, { incoming: 'phone', system: 'phone' }, { incoming: 'email', system: 'email' }],
            webhook: [{ incoming: 'name', system: 'name' }, { incoming: 'phone', system: 'phone' }],
            // Keys match the crmListing/v2 fields the pull-connector forwards verbatim.
            indiamart:      [{ incoming: 'SENDER_NAME', system: 'name' }, { incoming: 'SENDER_MOBILE', system: 'phone' }, { incoming: 'SENDER_EMAIL', system: 'email' }],
            // The Shopify webhook adapter flattens the store payload to canonical keys before intake.
            shopify:        [{ incoming: 'name', system: 'name' }, { incoming: 'phone', system: 'phone' }, { incoming: 'email', system: 'email' }]
        };
        return defaults[this.pickedSourceType] ?? [{ incoming: '', system: '' }];
    }

    loadSources(): void {
        if (this.loadingSources) return;
        this.loadingSources = true;
        this.sourceConfigService.list().subscribe({
            next: (list) => { this.sources = list; this.loadingSources = false; },
            error: ()     => { this.loadingSources = false; }
        });
    }

    openAddSource(): void {
        this.pickedSourceType = '';
        this.showChooseDialog = true;
    }

    proceedToConfigure(): void {
        this.editingSource = null;
        // Manual sources have a fixed sourceId convention (manual_create) so
        // the lead-microservice's resolveSourceId("manual") matches. Pre-fill
        // it and relax required-fields since UI dialog only enforces phone.
        const isManual = this.pickedSourceType === 'manual';
        this.sourceForm = {
            source_id: isManual ? 'manual_create' : '',
            required_fields: isManual ? ['phone'] : ['phone', 'name'],
            campaign_name: '',
            campaign_type: '',
            region: '',
            google_key: '',
            workflowName: '',
            operationId: '',
            meta_page_access_token: '', meta_page_id: '',
            // Default ON for a NEW Meta source — safer to start silent and let the admin flip it
            // live than to blast real customers on the very first test lead.
            meta_test_mode: this.pickedSourceType === 'meta_ads',
            meta_configured: false, meta_webhook_url: '',
            indiamart_crm_key: '', indiamart_configured: false,
            shopify_store_domain: '', shopify_access_token: '', shopify_webhook_secret: '', shopify_configured: false,
        };
        this.fieldMappingRows = this.getDefaultMappingRows();
        this.sourceFormError = '';
        this.showChooseDialog = false;
        // Pull the System Field options from Master Data (Settings → Master Data →
        // System Field) so any custom field the tenant added shows in the mapping
        // dropdown. Overrides the hardcoded fallback when active rows exist.
        this.loadMasterData('SYSTEM_FIELD');
        this.showConfigDialog = true;
    }

    openEditSource(src: SourceConfig): void {
        this.sourceConfigService.getById(src.id).subscribe({
            next: (detail: any) => {
                this.editingSource = src;
                this.pickedSourceType = src.source;
                this.sourceForm = {
                    source_id:       detail.sourceId      ?? detail.source_id      ?? src.sourceId,
                    required_fields: detail.requiredFields ?? detail.required_fields ?? ['phone', 'name'],
                    campaign_name:   detail.metadata?.campaignName ?? detail.metadata?.campaign_name ?? '',
                    campaign_type:   detail.metadata?.campaignType ?? detail.metadata?.campaign_type ?? '',
                    region:          detail.metadata?.region ?? '',
                    google_key:      detail.googleKey ?? detail.google_key ?? '',
                    workflowName:    detail.workflowName  ?? detail.workflow_name  ?? '',
                    operationId:     detail.operationId   ?? detail.operation_id   ?? '',
                    // The Page token is never returned — leave blank ("keep stored") and let
                    // meta_configured drive the "saved / unchanged" hint.
                    meta_page_access_token: '',
                    meta_page_id:          detail.metaPageId ?? detail.meta_page_id ?? '',
                    meta_test_mode:        !!(detail.metaTestMode ?? detail.meta_test_mode),
                    meta_configured:       !!(detail.metaConfigured ?? detail.meta_configured),
                    meta_webhook_url:      detail.metaWebhookUrl ?? detail.meta_webhook_url ?? '',
                    // Key never returned — blank means "keep stored".
                    indiamart_crm_key:     '',
                    indiamart_configured:  !!(detail.indiamartConfigured ?? detail.indiamart_configured),
                    // Domain round-trips; token + secret never returned (blank = keep stored).
                    shopify_store_domain:  detail.shopifyStoreDomain ?? detail.shopify_store_domain ?? '',
                    shopify_access_token:  '',
                    shopify_webhook_secret: '',
                    shopify_configured:    !!(detail.shopifyConfigured ?? detail.shopify_configured),
                };
                const fm = detail.fieldMapping ?? detail.field_mapping ?? {};
                this.fieldMappingRows = Object.keys(fm).length
                    ? Object.keys(fm).map((k: string) => ({ incoming: k, system: fm[k] }))
                    : [{ incoming: '', system: '' }];
                this.sourceFormError = '';
                // Meta sources: pull the Page's lead forms so the mapping badges are visible
                // the moment the dialog opens — an unmapped form is parking leads right now.
                this.metaForms = []; this.metaFormOpen = null; this.metaFormsError = '';
                if (src.source === 'meta_ads') this.loadMetaForms();
                // Shopify: pull the store's 3 events so the enable/mapping panel is ready on open.
                this.shopifyEvents = []; this.shopifyEventOpen = null; this.shopifyEventsError = '';
                if (src.source === 'shopify') this.loadShopifyEvents();
                // Refresh System Field options from Master Data so custom fields appear.
                this.loadMasterData('SYSTEM_FIELD');
                this.showConfigDialog = true;
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load source details.' })
        });
    }

    toggleRequiredField(field: string): void {
        const idx = this.sourceForm.required_fields.indexOf(field);
        if (idx >= 0) this.sourceForm.required_fields.splice(idx, 1);
        else this.sourceForm.required_fields.push(field);
    }

    isRequiredField(field: string): boolean { return this.sourceForm.required_fields.includes(field); }
    addMappingRow(): void { this.fieldMappingRows.push({ incoming: '', system: '' }); }
    removeMappingRow(i: number): void { if (this.fieldMappingRows.length > 1) this.fieldMappingRows.splice(i, 1); }

    private validateSourceForm(): string {
        // Campaign Name is the source's display identity everywhere — always required
        // (uniqueness is enforced server-side and surfaced via sourceFormError).
        if (!this.sourceForm.campaign_name.trim())
            return 'Campaign Name is required — it identifies this source everywhere in the app.';
        // Duplicate guard client-side too, for instant feedback (case-insensitive,
        // excludes the source being edited).
        const wanted = this.sourceForm.campaign_name.trim().toLowerCase();
        const clash = this.sources.find(s =>
            (!this.editingSource || s.id !== this.editingSource.id) &&
            (this.getCampaignName(s) || '').trim().toLowerCase() === wanted);
        if (clash) return `Campaign Name "${this.sourceForm.campaign_name.trim()}" is already used by source ${clash.sourceId}. Names must be unique.`;
        const id = this.sourceForm.source_id.trim();
        if (!id) return 'Campaign / Form Name is required.';
        if (!/^[a-z0-9_]+$/.test(id)) return 'Name must be lowercase with no spaces (letters, numbers, underscores only).';
        if (!this.isRequiredField('phone') && !this.isRequiredField('email')) return 'At least Phone or Email must be a required field.';
        const mapped = this.fieldMappingRows.filter(r => r.incoming && r.system).map(r => r.system);
        const missing = this.sourceForm.required_fields.filter(f => !mapped.includes(f));
        if (missing.length) return `Field mapping must cover required fields. Missing: ${missing.join(', ')}.`;
        if (!this.sourceForm.operationId && !this.sourceForm.workflowName.trim())
            return 'Select an Operation (or a legacy Workflow).';
        const metaMissing = this.metaMissingFields;
        if (metaMissing.length)
            return `Meta Ads needs: ${metaMissing.join(', ')}. Meta signs each lead and hides the lead data behind the Graph API, so all three are needed.`;
        if (!this.indiamartKeyValid)
            return 'IndiaMART CRM Key is required — generate it in the IndiaMART seller panel (Lead Manager → Settings → CRM Integration).';
        const shopifyMissing = this.shopifyMissingFields;
        if (shopifyMissing.length)
            return `Shopify needs: ${shopifyMissing.join(', ')}. Create a custom app in your Shopify admin (Settings → Apps → Develop apps) to get all three.`;
        return '';
    }

    submitSourceConfig(): void {
        this.sourceFormError = this.validateSourceForm();
        if (this.sourceFormError) return;

        const fieldMapping: Record<string, string> = {};
        this.fieldMappingRows.filter(r => r.incoming && r.system).forEach(r => { fieldMapping[r.incoming] = r.system; });

        this.savingSource = true;
        const metadata = { campaign_name: this.sourceForm.campaign_name, campaign_type: this.sourceForm.campaign_type, region: this.sourceForm.region };
        // Meta connection — only for meta_ads. A blank Page token is OMITTED so an edit keeps
        // what's stored (the API never sends it back, so blank means "unchanged", not "clear").
        const meta = this.pickedSourceType !== 'meta_ads' ? {} : {
            ...(this.sourceForm.meta_page_access_token?.trim() ? { meta_page_access_token: this.sourceForm.meta_page_access_token.trim() } : {}),
            ...(this.sourceForm.meta_page_id != null ? { meta_page_id: this.sourceForm.meta_page_id.trim() } : {}),
            // Always sent — it's a toggle, so "off" must be able to turn it off (unlike the
            // secrets, where blank means "keep what's stored").
            meta_test_mode: !!this.sourceForm.meta_test_mode,
        };

        // IndiaMART key — blank is OMITTED so an edit keeps the stored key (the API
        // never sends it back, so blank means "unchanged", not "clear").
        const indiamart = this.pickedSourceType !== 'indiamart' ? {} : {
            ...(this.sourceForm.indiamart_crm_key?.trim() ? { indiamart_crm_key: this.sourceForm.indiamart_crm_key.trim() } : {}),
        };

        // Shopify — domain round-trips; blank token/secret are OMITTED so an edit keeps the stored
        // value (the API never sends them back, so blank means "unchanged", not "clear").
        const shopify = this.pickedSourceType !== 'shopify' ? {} : {
            ...(this.sourceForm.shopify_store_domain?.trim() ? { shopify_store_domain: this.sourceForm.shopify_store_domain.trim() } : {}),
            ...(this.sourceForm.shopify_access_token?.trim() ? { shopify_access_token: this.sourceForm.shopify_access_token.trim() } : {}),
            ...(this.sourceForm.shopify_webhook_secret?.trim() ? { shopify_webhook_secret: this.sourceForm.shopify_webhook_secret.trim() } : {}),
        };

        const call = this.editingSource
            ? this.sourceConfigService.update(this.editingSource.id, {
                required_fields: this.sourceForm.required_fields,
                field_mapping:   fieldMapping,
                metadata,
                ...meta,
                ...indiamart,
                ...shopify,
                ...(this.sourceForm.workflowName ? { workflow_name: this.sourceForm.workflowName } : {}),
                ...(this.sourceForm.operationId ? { operation_id: this.sourceForm.operationId } : {})
              } as UpdateSourcePayload)
            : this.sourceConfigService.create({
                source:          this.pickedSourceType,
                source_id:       this.sourceForm.source_id.trim(),
                required_fields: this.sourceForm.required_fields,
                field_mapping:   fieldMapping,
                metadata,
                ...(this.pickedSourceType === 'google_ads' && this.sourceForm.google_key ? { google_key: this.sourceForm.google_key } : {}),
                ...meta,
                ...indiamart,
                ...shopify,
                ...(this.sourceForm.workflowName ? { workflow_name: this.sourceForm.workflowName } : {}),
                ...(this.sourceForm.operationId ? { operation_id: this.sourceForm.operationId } : {})
              } as CreateSourcePayload);

        call.subscribe({
            next: (res: any) => {
                this.savingSource = false;
                this.showConfigDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Source configuration saved.' });
                if (!this.editingSource && this.pickedSourceType === 'indiamart') {
                    // No webhook to paste anywhere — IndiaMART is pull-based. The poller
                    // picks the source up on its next tick and flips PENDING → CONNECTED
                    // (or INVALID_KEY), so the verdict shows on the list without a test call.
                    this.messageService.add({
                        severity: 'info', summary: 'IndiaMART connected',
                        detail: 'Polling starts within a minute — watch the Pull status on this source.', life: 8000
                    });
                } else if (!this.editingSource && this.pickedSourceType === 'meta_ads') {
                    // No credentials to hand out — Meta calls OUR central webhook and leads are
                    // routed here by Page id, so the generic intake URL/token dialog would only
                    // confuse. The real proof is a lead from Meta's own testing tool.
                    this.messageService.add({
                        severity: 'info', summary: 'Meta source connected',
                        detail: 'Leads from this Page now route here automatically. Fire a test lead from '
                            + 'Meta\'s Lead Ads Testing tool to see it land (test mode is on — no WhatsApp fires).',
                        life: 10000
                    });
                } else if (!this.editingSource && this.pickedSourceType === 'shopify') {
                    // Shopify uses its own HMAC-signed webhooks (not our Bearer intake), so the generic
                    // credentials dialog doesn't apply. Point the admin at the next steps instead.
                    this.messageService.add({
                        severity: 'info', summary: 'Shopify store connected',
                        detail: 'Now open this source to choose which events become leads (abandoned checkout / '
                            + 'order / customer) and copy the webhook URL to add in your Shopify admin.',
                        life: 10000
                    });
                } else if (!this.editingSource) {
                    this.currentCredentials = res as SourceCredentials;
                    this.credentialsMode = 'new';
                    this.testResult = null;
                    this.showCredentialsDialog = true;
                }
                this.loadSources();
            },
            error: (err) => {
                this.savingSource = false;
                const msg: string = err?.message ?? 'Failed to save configuration.';
                this.sourceFormError = (msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('already exist'))
                    ? 'A source with this name already exists. Use a unique Campaign Name.'
                    : msg;
            }
        });
    }

    doRegenerateToken(src: SourceConfig): void {
        this.regeneratingId = src.id;
        this.sourceConfigService.regenerateToken(src.id).subscribe({
            next: (creds) => {
                this.regeneratingId = null;
                this.currentCredentials = creds;
                this.credentialsMode = 'regenerate';
                this.testResult = null;
                this.showCredentialsDialog = true;
                this.messageService.add({ severity: 'warn', summary: 'Token Regenerated', detail: 'Copy the new token — it will not be shown again.', life: 6000 });
            },
            error: (err) => {
                this.regeneratingId = null;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to regenerate token.' });
            }
        });
    }

    openTestDialog(src: SourceConfig): void {
        this.testResult = null;
        this.testBody = { name: 'Test User', phone: '+919000000001', email: 'test@example.com' };
        this.currentCredentials = { id: src.id, source: src.source, sourceId: src.sourceId, authToken: '', webhookUrl: '', authHeader: '', curlExample: '', isActive: src.isActive };
        this.credentialsMode = 'test';
        this.showCredentialsDialog = true;
        // Load the source's live token + integration details so Run Test can
        // authenticate against the intake API (the list view doesn't carry the token).
        this.sourceConfigService.getCredentials(src.id).subscribe({
            next: (creds) => { this.currentCredentials = creds; },
            error: () => this.messageService.add({ severity: 'warn', summary: 'Could not load token',
                detail: 'Regenerate the source token, then run the test.' })
        });
    }

    toggleSource(src: SourceConfig): void {
        this.togglingId = src.id;
        this.sourceConfigService.toggle(src.id).subscribe({
            next: () => {
                this.togglingId = null;
                src.isActive = !src.isActive;
                this.messageService.add({ severity: 'success', summary: src.isActive ? 'Enabled' : 'Disabled', detail: `"${src.sourceId}" updated.` });
            },
            error: (err) => {
                this.togglingId = null;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to toggle source.' });
            }
        });
    }

    deleteSource(src: SourceConfig): void {
        this.sourceToDelete = src;
        this.showDeleteConfirm = true;
    }

    confirmDeleteSource(): void {
        if (!this.sourceToDelete) return;
        const src = this.sourceToDelete;
        this.showDeleteConfirm = false;
        this.sourceToDelete = null;
        this.deletingId = src.id;
        this.sourceConfigService.toggle(src.id).subscribe({
            next: () => {
                this.deletingId = null;
                const target = this.sources.find(s => s.id === src.id);
                if (target) target.isActive = false;
                this.messageService.add({ severity: 'info', summary: 'Disabled', detail: `"${src.sourceId}" has been disabled.` });
            },
            error: (err) => {
                this.deletingId = null;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to disable source.' });
            }
        });
    }

    runTest(): void {
        if (!this.currentCredentials) return;
        this.testLoading = true;
        this.testResult = null;
        this.sourceConfigService.runTest(this.currentCredentials.id, this.testBody).subscribe({
            next: (res) => { this.testLoading = false; this.testResult = { ...res, _success: true }; },
            error: (err) => { this.testLoading = false; this.testResult = { _success: false, _status: err?.status, _message: err?.message }; }
        });
    }

    copyCredField(text: string, field: string): void {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            this.copiedField = field;
            setTimeout(() => { if (this.copiedField === field) this.copiedField = ''; }, 2000);
        });
    }

    // ── EVENTS SETUP ─────────────────────────────────────────────

    eventTypeGroups = [
        {
            label: 'Lead Events',
            items: [
                { label: 'Lead Created', value: 'lead_created' },
                { label: 'Source Added', value: 'source_added' }
            ]
        },
        {
            label: 'Behavior Events',
            items: [
                { label: 'Page Visited',   value: 'page_visited' },
                { label: 'Form Submitted', value: 'form_submitted' },
                { label: 'Link Clicked',   value: 'link_clicked' }
            ]
        },
        {
            label: 'Commerce Events',
            items: [
                { label: 'Cart Abandoned',     value: 'cart_abandoned' },
                { label: 'Purchase Completed', value: 'purchase_completed' },
                { label: 'Refund Requested',   value: 'refund_requested' }
            ]
        },
        {
            label: 'Communication Events',
            items: [
                { label: 'Conversation Started', value: 'conversation_started' },
                { label: 'WhatsApp Replied',     value: 'whatsapp_replied' },
                { label: 'Email Opened',         value: 'email_opened' },
                { label: 'Call Connected',       value: 'call_connected' },
                { label: 'Call Missed',          value: 'call_missed' },
                { label: 'Opt Out',              value: 'opt_out' }
            ]
        },
        {
            label: 'Appointment Events',
            items: [
                { label: 'Appointment Booked',    value: 'appointment_booked' },
                { label: 'Appointment Cancelled', value: 'appointment_cancelled' }
            ]
        }
    ];

    private eventFieldDefs: Record<string, { key: string; label: string; placeholder: string }[]> = {
        lead_created:           [{ key: 'source',           label: 'Source',        placeholder: 'meta_ads' }],
        source_added:           [{ key: 'source',           label: 'Source',        placeholder: 'google_ads' },
                                 { key: 'source_id',        label: 'Source ID',     placeholder: 'summer_2025' }],
        page_visited:           [{ key: 'page_url',         label: 'Page URL',      placeholder: 'https://yoursite.com/pricing' },
                                 { key: 'page_title',       label: 'Page Title',    placeholder: 'Pricing' }],
        form_submitted:         [{ key: 'form_name',        label: 'Form Name',     placeholder: 'Contact Form' },
                                 { key: 'form_url',         label: 'Form URL',      placeholder: 'https://yoursite.com/contact' }],
        link_clicked:           [{ key: 'url',              label: 'Link URL',      placeholder: 'https://yoursite.com/docs' },
                                 { key: 'label',            label: 'Link Label',    placeholder: 'Read Docs' }],
        cart_abandoned:         [{ key: 'cart_value',       label: 'Cart Value',    placeholder: '2499' },
                                 { key: 'product_name',     label: 'Product Name',  placeholder: 'Pro Plan' },
                                 { key: 'currency',         label: 'Currency',      placeholder: 'INR' }],
        purchase_completed:     [{ key: 'order_id',         label: 'Order ID',      placeholder: 'ORD-001' },
                                 { key: 'amount',           label: 'Amount',        placeholder: '4999' },
                                 { key: 'currency',         label: 'Currency',      placeholder: 'INR' }],
        refund_requested:       [{ key: 'order_id',         label: 'Order ID',      placeholder: 'ORD-001' },
                                 { key: 'amount',           label: 'Refund Amount', placeholder: '999' },
                                 { key: 'reason',           label: 'Reason',        placeholder: 'Not satisfied' }],
        conversation_started:   [{ key: 'channel',          label: 'Channel',       placeholder: 'whatsapp' }],
        whatsapp_replied:       [{ key: 'message',          label: 'Message',       placeholder: 'Yes, interested!' }],
        email_opened:           [{ key: 'subject',          label: 'Subject',       placeholder: 'Your Quote is Ready' }],
        call_connected:         [{ key: 'duration_seconds', label: 'Duration (sec)',placeholder: '120' },
                                 { key: 'agent_name',       label: 'Agent Name',    placeholder: 'Rahul S.' }],
        call_missed:            [{ key: 'attempted_at',     label: 'Attempted At',  placeholder: '2025-06-15T10:30:00Z' }],
        opt_out:                [{ key: 'reason',           label: 'Reason',        placeholder: 'Not interested' }],
        appointment_booked:     [{ key: 'appointment_date', label: 'Date',          placeholder: '2025-06-15' },
                                 { key: 'appointment_time', label: 'Time',          placeholder: '10:00 AM' },
                                 { key: 'service_name',     label: 'Service',       placeholder: 'Demo Call' }],
        appointment_cancelled:  [{ key: 'appointment_date', label: 'Date',          placeholder: '2025-06-15' },
                                 { key: 'reason',           label: 'Reason',        placeholder: 'Rescheduled' }]
    };

    selectedEventType = '';
    eventDynamicValues: Record<string, string> = {};
    eventPhone = '+919000000001';
    eventMode: 'test' | 'live' = 'test';
    idempotencyKey = '';
    sendingEvent = false;
    eventSendResult: { success: boolean; message: string; data?: any } | null = null;
    copiedEventField = '';

    get currentEventFields(): { key: string; label: string; placeholder: string }[] {
        return this.eventFieldDefs[this.selectedEventType] ?? [];
    }

    onEventTypeChange(): void {
        this.eventDynamicValues = {};
        this.eventSendResult = null;
        for (const f of this.currentEventFields) {
            this.eventDynamicValues[f.key] = '';
        }
    }

    private buildEventPayload(): EventPayload {
        const event_data: Record<string, any> = {};
        for (const f of this.currentEventFields) {
            const v = this.eventDynamicValues[f.key];
            if (v) event_data[f.key] = v;
        }
        return {
            phone: this.eventPhone,
            event_type: this.selectedEventType,
            event_data,
            idempotency_key: this.idempotencyKey || undefined
        };
    }

    sendEvent(): void {
        if (!this.selectedEventType || !this.eventPhone) return;
        this.sendingEvent = true;
        this.eventSendResult = null;
        const payload = this.buildEventPayload();
        this.eventSetupService.send(payload, this.eventMode === 'test').subscribe({
            next: (res) => {
                this.sendingEvent = false;
                this.eventSendResult = { success: true, message: 'Event sent successfully!', data: res };
            },
            error: (err) => {
                this.sendingEvent = false;
                this.eventSendResult = { success: false, message: err?.message ?? 'Failed to send event.' };
            }
        });
    }

    quickTestEvent(eventType: string, eventData: Record<string, any>): void {
        this.selectedEventType = eventType;
        this.onEventTypeChange();
        for (const [k, v] of Object.entries(eventData)) {
            this.eventDynamicValues[k] = String(v);
        }
        this.sendEvent();
    }

    get eventApiEndpoint(): string {
        const base = environment.eventApiBaseUrl;
        const cid = localStorage.getItem('companyId') ?? '{companyId}';
        return this.eventMode === 'test'
            ? `${base}/event/v1/test/${cid}`
            : `${base}/event/v1/${cid}`;
    }

    get eventCurlSnippet(): string {
        const endpoint = this.eventApiEndpoint;
        const body = this.selectedEventType
            ? JSON.stringify(this.buildEventPayload(), null, 2)
            : JSON.stringify({ phone: '+91XXXXXXXXXX', event_type: 'cart_abandoned', event_data: { cart_value: 2499 } }, null, 2);
        return `curl -X POST ${endpoint} \\\n  -H "Content-Type: application/json" \\\n  -H "Idempotency-Key: {unique_request_id}" \\\n  -d '${body}'`;
    }

    copyEventField(text: string, field: string): void {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            this.copiedEventField = field;
            setTimeout(() => { if (this.copiedEventField === field) this.copiedEventField = ''; }, 2000);
        });
    }

    // ── MASTER DATA METHODS ─────────────────────────────────────

    loadMasterData(category: MasterDataCategory): void {
        this.loadingMasterData[category] = true;
        this.masterDataService.list(category, false).subscribe({
            next: (rows) => {
                this.masterDataRows[category] = rows ?? [];
                this.loadingMasterData[category] = false;

                if (category === 'SYSTEM_FIELD') {
                    const activeRows = (rows ?? []).filter(r => r.isActive);
                    if (activeRows.length) {
                        this.systemFieldOptions = activeRows
                            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                            .map(r => ({ label: r.label, value: r.code }));
                    }
                }
            },
            error: (err) => {
                this.loadingMasterData[category] = false;
                this.showError('Load Failed', err);
            }
        });
    }

    importingSourceNames = false;
    reprocessingCases = false;

    /** Promote already-ingested leads of the current provider into cases (test-mode leftovers). */
    reprocessToCases(): void {
        const source = this.formProvider === 'google' ? 'google_ads' : 'meta_ads';
        this.reprocessingCases = true;
        this.sourceConfigService.reprocessToCases(source).subscribe({
            next: (r) => {
                this.reprocessingCases = false;
                this.messageService.add({ severity: 'success', summary: 'Reprocess finished',
                    detail: `${r.reprocessed} lead(s) sent to cases`
                        + (r.skipped_has_case ? `, ${r.skipped_has_case} already had a case` : '')
                        + (r.skipped_no_phone ? `, ${r.skipped_no_phone} had no phone` : '') + '.', life: 9000 });
            },
            error: (err) => {
                this.reprocessingCases = false;
                this.messageService.add({ severity: 'error', summary: 'Reprocess failed',
                    detail: err?.message ?? 'Could not reprocess leads.' });
            }
        });
    }

    /** Create Source Names entries from the distinct sources already on the company's leads. */
    importSourceNamesFromLeads(): void {
        this.importingSourceNames = true;
        this.sourceConfigService.importSourceNamesFromLeads().subscribe({
            next: (res) => {
                this.importingSourceNames = false;
                this.loadMasterData('SOURCE_NAME');
                this.messageService.add({ severity: 'success', summary: 'Source names imported',
                    detail: `${res.created} added` + (res.skipped ? `, ${res.skipped} skipped (already present or internal)` : '') + '.' });
            },
            error: (err) => {
                this.importingSourceNames = false;
                this.messageService.add({ severity: 'error', summary: 'Import failed',
                    detail: err?.message ?? 'Could not import source names.' });
            }
        });
    }

    selectMasterDataTab(category: MasterDataCategory): void {
        this.masterDataTab = category;
    }

    get currentMasterDataRows(): MasterDataDto[] {
        return this.masterDataRows[this.masterDataTab] ?? [];
    }

    get currentMasterDataLoading(): boolean {
        return !!this.loadingMasterData[this.masterDataTab];
    }

    get currentMasterDataCategoryLabel(): string {
        return this.masterDataCategories.find(c => c.key === this.masterDataTab)?.label ?? this.masterDataTab;
    }

    openAddMasterData(): void {
        this.editingMasterData = null;
        const nextOrder = (this.currentMasterDataRows?.length ?? 0) + 1;
        this.masterDataForm = {
            code: '', label: '', description: '', displayOrder: nextOrder, isActive: true,
            fieldType: 'text', boolValue: false, kvPairs: [], allowOther: false, trackPerformance: false, showInCapture: false
        };
        this.masterDataFormError = '';
        this.showMasterDataDialog = true;
    }

    openEditMasterData(row: MasterDataDto): void {
        this.editingMasterData = row;
        const meta = this.parseSystemFieldMeta(row.metadata);
        this.masterDataForm = {
            code: row.code,
            label: row.label,
            description: row.description ?? '',
            displayOrder: row.displayOrder ?? 0,
            isActive: row.isActive ?? true,
            fieldType: meta.fieldType,
            boolValue: meta.boolValue,
            kvPairs: meta.kvPairs,
            allowOther: meta.allowOther,
            trackPerformance: meta.trackPerformance,
            showInCapture: meta.showInCapture
        };
        this.masterDataFormError = '';
        this.showMasterDataDialog = true;
    }

    // ── System Field value-type helpers ──────────────────────────
    /** Append an empty key/value row to the Key–Value editor. */
    addKvPair(): void {
        this.masterDataForm.kvPairs = [...this.masterDataForm.kvPairs, { key: '', value: '' }];
    }

    /** Drop one key/value row from the Key–Value editor. */
    removeKvPair(index: number): void {
        this.masterDataForm.kvPairs = this.masterDataForm.kvPairs.filter((_, i) => i !== index);
    }

    /** Read the value-type back out of a SYSTEM_FIELD row's metadata JSON. */
    private parseSystemFieldMeta(raw?: string): {
        fieldType: SystemFieldValueType; boolValue: boolean; kvPairs: { key: string; value: string }[]; allowOther: boolean; trackPerformance: boolean; showInCapture: boolean;
    } {
        const fallback = { fieldType: 'text' as SystemFieldValueType, boolValue: false, kvPairs: [] as { key: string; value: string }[], allowOther: false, trackPerformance: false, showInCapture: false };
        if (!raw) return fallback;
        try {
            const m = JSON.parse(raw) || {};
            const known: SystemFieldValueType[] = ['textarea', 'number', 'boolean', 'date', 'keyvalue', 'file', 'multifile'];
            const fieldType: SystemFieldValueType = known.includes(m.valueType) ? m.valueType : 'text';
            const kvPairs = Array.isArray(m.options)
                ? m.options.map((o: any) => ({ key: String(o?.key ?? ''), value: String(o?.value ?? '') }))
                : [];
            return { fieldType, boolValue: m.value === true, kvPairs, allowOther: m.allowOther === true, trackPerformance: m.trackPerformance === true, showInCapture: m.showInCapture === true };
        } catch {
            return fallback;
        }
    }

    /** Build the metadata JSON to persist for a SYSTEM_FIELD entry (undefined for other categories). */
    private buildSystemFieldMetadata(): string | undefined {
        if (this.masterDataTab !== 'SYSTEM_FIELD') return undefined;
        const track = !!this.masterDataForm.trackPerformance;
        const cap = !!this.masterDataForm.showInCapture;   // curate the customer-flow Captured-data panel
        if (this.masterDataForm.fieldType === 'boolean') {
            return JSON.stringify({ valueType: 'boolean', value: !!this.masterDataForm.boolValue, trackPerformance: track, showInCapture: cap });
        }
        if (this.masterDataForm.fieldType === 'keyvalue') {
            const options = (this.masterDataForm.kvPairs || [])
                .map(p => ({ key: (p.key || '').trim(), value: (p.value || '').trim() }))
                .filter(p => p.key);
            return JSON.stringify({ valueType: 'keyvalue', options, allowOther: !!this.masterDataForm.allowOther, trackPerformance: track, showInCapture: cap });
        }
        if (this.masterDataForm.fieldType === 'textarea'
            || this.masterDataForm.fieldType === 'number'
            || this.masterDataForm.fieldType === 'file'
            || this.masterDataForm.fieldType === 'multifile') {
            return JSON.stringify({ valueType: this.masterDataForm.fieldType, trackPerformance: track, showInCapture: cap });
        }
        return JSON.stringify({ valueType: 'text', trackPerformance: track, showInCapture: cap });
    }

    /** Short label describing a SYSTEM_FIELD row's value-type, for the table chip. */
    systemFieldTypeBadge(row: MasterDataDto): string {
        const m = this.parseSystemFieldMeta(row?.metadata);
        if (m.fieldType === 'textarea') return 'Text area';
        if (m.fieldType === 'number') return 'Number';
        if (m.fieldType === 'boolean') return 'Boolean';
        if (m.fieldType === 'date') return 'Date';
        if (m.fieldType === 'keyvalue') return `Key–Value · ${m.kvPairs.length}`;
        if (m.fieldType === 'file') return 'File';
        if (m.fieldType === 'multifile') return 'Multi-file';
        return 'Text';
    }

    onMasterDataCodeInput(value: string): void {
        if (this.editingMasterData) return; // code is immutable after creation
        const normalized = this.masterDataTab === 'LEAD_STATUS'
            ? value.toUpperCase().replace(/[^A-Z0-9_]/g, '_')
            : value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
        this.masterDataForm.code = normalized;
    }

    submitMasterData(): void {
        const code  = (this.masterDataForm.code  || '').trim();
        const label = (this.masterDataForm.label || '').trim();
        if (!code)  { this.masterDataFormError = 'Code is required.'; return; }
        if (!label) { this.masterDataFormError = 'Label is required.'; return; }
        if (this.masterDataTab === 'SYSTEM_FIELD' && this.masterDataForm.fieldType === 'keyvalue'
            && !(this.masterDataForm.kvPairs || []).some(p => (p.key || '').trim())) {
            this.masterDataFormError = 'Add at least one key for a Key–Value field.';
            return;
        }
        this.masterDataFormError = '';

        this.savingMasterData = true;

        const metadata = this.buildSystemFieldMetadata();
        const op$ = this.editingMasterData
            ? this.masterDataService.update(this.editingMasterData.id, {
                category: this.masterDataTab,
                code,
                label,
                description: this.masterDataForm.description,
                displayOrder: this.masterDataForm.displayOrder,
                isActive: this.masterDataForm.isActive,
                metadata
              })
            : this.masterDataService.create({
                category: this.masterDataTab,
                code,
                label,
                description: this.masterDataForm.description,
                displayOrder: this.masterDataForm.displayOrder,
                isActive: this.masterDataForm.isActive,
                metadata
              });

        op$.subscribe({
            next: () => {
                this.savingMasterData = false;
                this.showMasterDataDialog = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.editingMasterData ? 'Updated' : 'Created',
                    detail: `${this.currentMasterDataCategoryLabel} entry saved.`
                });
                this.editingMasterData = null;
                this.loadMasterData(this.masterDataTab);
            },
            error: (err) => {
                this.savingMasterData = false;
                this.masterDataFormError = err?.message || 'Failed to save entry.';
                this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: this.masterDataFormError });
            }
        });
    }

    deleteMasterData(row: MasterDataDto): void {
        if (row.isSystem) {
            this.messageService.add({
                severity: 'warn',
                summary: 'System Entry',
                detail: 'System entries cannot be deleted. Toggle Active instead.'
            });
            return;
        }
        this.deletingMasterDataId = row.id;
        this.masterDataService.delete(row.id).subscribe({
            next: () => {
                this.deletingMasterDataId = null;
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `"${row.code}" removed.` });
                this.loadMasterData(this.masterDataTab);
            },
            error: (err) => {
                this.deletingMasterDataId = null;
                this.messageService.add({ severity: 'danger', summary: 'Delete Failed', detail: err?.message || 'Unable to delete entry.' });
            }
        });
    }

    toggleMasterDataActive(row: MasterDataDto): void {
        this.masterDataService.update(row.id, {
            category: this.masterDataTab,
            code: row.code,
            label: row.label,
            isActive: !row.isActive,
            displayOrder: row.displayOrder
        }).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: row.isActive ? 'Disabled' : 'Enabled',
                    detail: `"${row.code}" updated.`
                });
                this.loadMasterData(this.masterDataTab);
            },
            error: (err) => {
                this.messageService.add({ severity: 'danger', summary: 'Update Failed', detail: err?.message || 'Unable to update entry.' });
            }
        });
    }
}

